import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { renderAsync } from 'docx-preview';

import { BulkWhatsAppComposerModal } from './BulkWhatsAppComposerModal';
import { companyNameFromVacancyRecord, vacancyTitleFromVacancyRecord } from '../utils/whatsappShortcodes';
import { CallSidebar } from './CallSidebar';
import CandidateMoveModal from './CandidateMoveModal';
import { VOORGESTELD_RESUME_BUNDLE_KEY, RESUME_VOORGESTELD_MOVE_MODAL_EVENT, resumeBundleMatchesVacancyEvent } from '../utils/voorgesteldResumeNavigation';
import { portalPrivacyFromUser } from '../utils/clientPortalGuards';
import { DEFAULT_KANBAN_STAGES } from '../constants';
import { triggerAutomation } from '../services/automationService';
import {
    supabase_get_vacancies, supabase_upload_file,
    supabase_save_candidate, supabase_update_candidate_full,
    supabase, supabase_delete_candidate_profile,
    supabase_get_cv_versions, supabase_get_candidate_communications, supabase_save_candidate_communication,
    supabase_get_documents, supabase_get_document_files, supabase_get_cv_templates, supabase_upload_image, supabase_delete_application, supabase_get_talent_pool,
    supabase_delete_document, supabase_get_pro_cv_templates, supabase_get_flows, supabase_save_document,
    supabase_get_authorized_client_ids,
    supabase_search_talent_pool,
    supabase_get_paginated_talent_pool,
    supabase_get_portal_scoped_candidate_ids,
    supabase_get_candidate_applications,
    supabase_get_candidate_application_stage_history,
    supabase_get_candidate_interviews,
    supabase_get_candidate_placements_light,
    supabase_search_global_candidate_match,
    generateCandidateSlug,
    supabase_get_candidate_profile_by_slug_id,
    openStorageDocument,
    supabase_update_application_status,
    supabase_recruiter_publish_portal_proposal,
    supabase_recruiter_apply_voorgesteld_match_score,
    supabase_get_templates,
    supabase_log_interaction,
    supabase_delete_placement,
} from '../services/supabaseService';
import { uploadToOneDrive, getOneDriveFolderUrl } from '../services/microsoftGraphService';
import { generateWordDocument, extractTemplateFields } from '../services/wordTemplateService';
import { searchTalentPool, analyzeUploadedCV, ai, performRealMatch, performBatchMatch, improveWhatsAppMessage, AI_PROXY_CHAT_MODEL } from '../services/geminiService';
import { triggerCandidateStatusChanged } from '../services/automationService';
import { isCandidateSourceFilled } from '../utils/candidateSource';
import { coerceCvList, coerceCvTags, normalizeCvAnalysisResult } from '../utils/cvParseNormalize';

// --- Nieuws Component voor tijdens CV upload ---
const RECRUITMENT_NEWS_ITEMS = [
    "Wist je dat 73% van de kandidaten afhaakt bij een te lang sollicitatieproces?",
    "Nieuwe trend: AI neemt steeds meer administratieve HR-taken over in 2026.",
    "Werkzoekenden hechten in 2026 meer waarde aan flexibiliteit dan aan salaris.",
    "De IT-sector kampt nog steeds met het grootste tekort aan gekwalificeerd personeel.",
    "Tip: Een persoonlijke onboarding video verhoogt retentie met 40% in de eerste maand.",
    "Recruitment marketing via TikTok is de snelst groeiende strategie voor Gen Z.",
    "Wist je dat Werfplaats AI vacatureteksten in seconden 100% inclusief kan herschrijven?"
];

function RecruitmentNewsLoader() {
    const [newsIndex, setNewsIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setNewsIndex(prev => (prev + 1) % RECRUITMENT_NEWS_ITEMS.length);
        }, 5000); // Wissel elke 5 seconden
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="flex flex-col items-center text-center max-w-[280px] animate-fadeIn">
            <h4 className="text-[10px] font-black text-red- uppercase tracking-widest mb-1">Actueel Recruitment Nieuws</h4>
            <p className="text-xs text-slate-700 font-medium leading-relaxed italic h-[40px] flex items-center justify-center transition-opacity duration-500" key={newsIndex}>
                "{RECRUITMENT_NEWS_ITEMS[newsIndex]}"
            </p>
        </div>
    );
}
import { CVData, User as AppUser, CVTemplate, Vacancy, AutomationFlow, UserRole } from '../types';
import {
    Users, Activity, Loader2, Search, Filter, MapPin, Briefcase, ChevronRight, Clock, Download,
    MoreVertical, Mail, Phone, Linkedin, FileText, CheckCircle2, Check, AlertTriangle, User,
    ArrowUpRight, MessageSquare, Calendar, X, Plus, Edit3, Trash2, Save,
    Camera, AlertCircle, Upload, GraduationCap, Wrench, Send, Info,
    History, FileEdit, LayoutTemplate, Palette, FileCheck, BrainCircuit, StickyNote, UserPlus, PieChart, TrendingUp, Quote,
    Wand2, CalendarClock, Gamepad2, Zap, ChevronLeft, ChevronDown, Cloud, Code, Sparkles, ArrowLeft, Database, RefreshCw, Building2
} from 'lucide-react';
import TalentPoolStatistics from './TalentPoolStatistics';
import TalentPoolDatabaseGrid from './TalentPoolDatabaseGrid';
import DataQualityDashboard from './DataQualityDashboard';
import { applicationStatusDisplay } from '../utils/candidateJourneyBuckets';
import { getCandidateEmail } from '../utils/candidateContact';
import { MiniCVPreview } from './CVTemplateManager';
import CVContentSidebar from './CVContentSidebar';
import CandidateDetailModal from './CandidateDetailModal';
import { CandidatePipelineStrip, CandidateShortlistPhasesPanel } from './talentPool/CandidateShortlistJourney';
import {
    buildTalentPoolListStageCounts,
    buildTalentPoolListStageVacancies,
    talentPoolListStageVacanciesFor,
    type TalentPoolListStageId,
    pipelineStepToShortlistPhase,
    type PipelineStepId,
    type ShortlistPhaseId,
} from '../utils/candidateJourneyBuckets';
import { CandidateNotes } from './CandidateNotes';
import { fetchBullhornCandidateFiles, fetchBullhornCandidateNotes, downloadBullhornCandidateFile } from '../services/bullhornService';
import { supabase_sync_bullhorn_documents, supabase_sync_bullhorn_notes } from '../services/supabaseService';

function FlowClientLogo({ logoUrl }: { logoUrl?: string | null }) {
    const [broken, setBroken] = useState(false);
    return (
        <div className="w-8 h-8 rounded-full border border-slate-200 bg-slate-50 shrink-0 overflow-hidden flex items-center justify-center">
            {logoUrl && !broken ? (
                <img src={logoUrl} alt="" className="w-full h-full object-cover" onError={() => setBroken(true)} />
            ) : (
                <Building2 size={15} className="text-slate-400" />
            )}
        </div>
    );
}

/** Opdrachtgever / eindklant in vacature-flow: rond logo + alleen naam (geen UUID) */
function FlowLinkedClientCell({
    resolved,
    hasLink,
}: {
    resolved?: { id?: string; name?: string | null; logo_url?: string | null } | null;
    /** True als er een FK op de vacature staat maar de join kan alsnog leeg zijn */
    hasLink?: boolean;
}) {
    if (!resolved && !hasLink) {
        return <span className="text-slate-400 text-[11px] font-medium">—</span>;
    }
    const name = resolved?.name?.trim();
    return (
        <div className="flex items-center gap-2 min-w-0 max-w-[200px]">
            <FlowClientLogo logoUrl={resolved?.logo_url} />
            <span className="text-[10px] font-bold text-slate-700 truncate min-w-0" title={name || undefined}>
                {name || '—'}
            </span>
        </div>
    );
}

interface TalentPoolProps {
    user: AppUser | null;
    onNavigate: (tab: string, data?: CVData | null | string, template?: CVTemplate, docContent?: { title: string, content: string, candidateId?: string }) => void;
    onStartCall?: (profile: any) => void;
    onEditCallNote?: (profile: any, commId: string, content: string) => void;
    candidates: any[];
    vacancies?: Vacancy[];
    isLoading?: boolean | number;
    initialCandidateId?: string | null;
}

const DEFAULT_STATUSES = [
    { id: 'lead', label: 'Lead', color: 'bg-red-50 text-red-600 border-red-100' },
    { id: 'shortlist', label: 'Shortlist', color: 'bg-yellow-50 text-yellow-700 border-yellow-100' },
    { id: 'active', label: 'Actief', color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
    { id: 'beschikbaar', label: 'Beschikbaar (Open to work)', color: 'bg-sky-50 text-sky-700 border-sky-100' },
    { id: 'onder contract', label: 'Onder contract', color: 'bg-amber-50 text-amber-700 border-amber-100' },
    { id: 'placed', label: 'Geplaatst', color: 'bg-purple-50 text-purple-700 border-purple-100' },
    { id: 'wp-interim', label: 'wp-interim', color: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
    { id: 'werkenbijvideoworks', label: 'werkenbijvideoworks.nl', color: 'bg-blue-50 text-blue-700 border-blue-100' },
    { id: 'website sollicitatie', label: 'Website Sollicitatie', color: 'bg-cyan-50 text-cyan-700 border-cyan-100' },
    { id: 'archived', label: 'Gearchiveerd', color: 'bg-gray-50 text-gray-600 border-gray-100' },
    { id: 'blacklisted', label: 'Blacklist', color: 'bg-red-50 text-red-700 border-red-100' }
];

type TalentPoolStatusDef = { id: string; label: string; color?: string };

/** Values to match in DB `candidates.status` (label, id, or Bullhorn-style "Label …"). */
function talentPoolStatusFilterVariants(filterLabel: string | null, statuses: TalentPoolStatusDef[]): string[] {
    if (!filterLabel) return [];
    const t = filterLabel.trim();
    const out = new Set<string>();
    out.add(t);
    const stat = statuses.find((s) => s.label === t || s.id === t || s.label?.trim() === t);
    if (stat?.label?.trim()) out.add(stat.label.trim());
    if (stat?.id?.trim()) out.add(stat.id.trim());
    return Array.from(out).filter(Boolean);
}

function profileMatchesTalentPoolStatusFilter(
    profile: any,
    filterLabel: string | null,
    statuses: TalentPoolStatusDef[]
): boolean {
    if (!filterLabel) return true;
    const raw = String(profile?.status ?? profile?.candidate?.status ?? '').trim();
    if (!raw) return false;
    const rl = raw.toLowerCase();
    for (const v of talentPoolStatusFilterVariants(filterLabel, statuses)) {
        const vl = v.trim().toLowerCase();
        if (!vl) continue;
        if (rl === vl) return true;
        if (rl.startsWith(vl + ' ') || rl.startsWith(vl + '-')) return true;
    }
    return false;
}

function talentPoolProfileYearsExperience(profile: any): number {
    const raw = profile?.candidate?.experience ?? profile?.experience;
    if (raw == null || raw === '') return NaN;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    const n = parseFloat(String(raw).replace(/,/g, '.').replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : NaN;
}

/** Pool rows use `candidates.added_at` (not always `created_at`). */
function talentPoolProfileAddedDate(profile: any): Date | null {
    const c = profile?.candidate || {};
    const raw = c.added_at ?? c.created_at ?? profile?.added_at ?? profile?.created_at ?? null;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
}

const StatusBadge: React.FC<{ status?: string, customStatuses?: any[] }> = ({ status, customStatuses }) => {
    const statusesList = customStatuses && customStatuses.length > 0 ? customStatuses : DEFAULT_STATUSES;
    const found = statusesList.find(s => s.label === status) || statusesList.find(s => s.id === status?.toLowerCase());
    const colorClass = found ? found.color : 'bg-gray-50 text-gray-500 border-gray-100';
    return (
        <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${colorClass}`}>
            {status || 'Nieuw'}
        </span>
    );
};

// Lift cache to module scope so it survives component unmounts
const globalPageCache: Record<string, { data: any[], count: number }> = {};
import AiRichTextEditor from './AiRichTextEditor';
import { DebugTag } from './DebugTag';

const TALENT_POOL_VIEW_MODE_KEY = 'talentPoolViewMode';

/** RPC `match_candidates_with_data` returns `similarity` (0–1), not `similarity_score`. */
function vectorSimilarityToPercent(hit: any): number | undefined {
    const raw = hit?.similarity ?? hit?.similarity_score;
    if (raw == null || raw === '') return undefined;
    const n = Number(raw);
    if (!Number.isFinite(n)) return undefined;
    if (n >= 0 && n <= 1) return Math.round(n * 100);
    if (n > 1 && n <= 100) return Math.round(n);
    return Math.round(Math.max(0, Math.min(100, n)));
}

/** Geboortedatum uit CV-parse / dossier (Engels + Nederlands, nested personalInfo). */
function extractDateOfBirthFromProfile(profile: any): string {
    if (!profile) return '';
    const ai = profile.ai_parsed_data && typeof profile.ai_parsed_data === 'object' ? profile.ai_parsed_data : {};
    const pc = profile.parsed_cv && typeof profile.parsed_cv === 'object' ? profile.parsed_cv : {};
    const cand = profile.candidate || {};
    const candData = cand.data && typeof cand.data === 'object' ? cand.data : {};
    const piAi = ai.personalInfo && typeof ai.personalInfo === 'object' ? ai.personalInfo : {};
    const piPc = pc.personalInfo && typeof pc.personalInfo === 'object' ? pc.personalInfo : {};
    const raw =
        ai.dateOfBirth ||
        ai.geboortedatum ||
        pc.dateOfBirth ||
        pc.geboortedatum ||
        piAi.dateOfBirth ||
        piAi.geboortedatum ||
        piPc.dateOfBirth ||
        piPc.geboortedatum ||
        candData.date_of_birth ||
        candData.geboortedatum ||
        '';
    return String(raw).trim();
}

/** YYYY-MM-DD voor HTML date-input, of leeg. */
function normalizeDateOfBirthToInputValue(raw: string | null | undefined): string {
    if (!raw || typeof raw !== 'string') return '';
    const t = raw.trim();
    if (!t) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
    const mNl = t.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
    if (mNl) {
        let a = parseInt(mNl[1], 10);
        let b = parseInt(mNl[2], 10);
        const y = parseInt(mNl[3], 10);
        let day: number;
        let month: number;
        if (a > 12) {
            day = a;
            month = b;
        } else if (b > 12) {
            day = b;
            month = a;
        } else {
            day = a;
            month = b;
        }
        if (y < 1900 || y > 2100 || month < 1 || month > 12 || day < 1 || day > 31) return '';
        return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    const d = new Date(t);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return '';
}

function birthdayHintFromIsoYmd(isoYmd: string): string | null {
    if (!isoYmd || !/^\d{4}-\d{2}-\d{2}$/.test(isoYmd)) return null;
    const [y, m, d] = isoYmd.split('-').map(Number);
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    let next = new Date(today.getFullYear(), m - 1, d).getTime();
    if (next < start) next = new Date(today.getFullYear() + 1, m - 1, d).getTime();
    const diff = Math.round((next - start) / 86400000);
    if (diff === 0) return 'Vandaag jarig';
    if (diff === 1) return 'Morgen jarig';
    return `Volgende verjaardag over ${diff} dagen`;
}

/**
 * Uitleg waarom dit profiel bij je zoekopdracht hoort: score in gewone taal,
 * letterlijke woord-treffers in titel/tags/CV, anders inhoudelijke duiding + signalen uit het dossier.
 */
function buildAiVectorMatchReason(searchQuery: string, hit: any, pct?: number): string {
    const q = (searchQuery || '').trim();
    const ai = hit?.ai_parsed_data && typeof hit.ai_parsed_data === 'object' ? hit.ai_parsed_data : {};
    const role = String(hit?.role || ai.currentRole || ai.role || '').trim();
    const fromHitTags = Array.isArray(hit?.tags) ? hit.tags.filter(Boolean).map(String) : [];
    const fromAiTags = Array.isArray(ai.tags)
        ? ai.tags.map((t: any) => (typeof t === 'string' ? t : t?.name)).filter(Boolean).map(String)
        : [];
    const keywords = [...new Set([...fromHitTags, ...fromAiTags])];
    const kwShort = keywords.slice(0, 6).join(', ');

    const summary = String(ai.summary || ai.Summary || hit?.cv_summary || '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 220);
    const timeline = Array.isArray(ai.timeline) ? ai.timeline : [];
    const recent = timeline[0];
    const jobLine = recent
        ? [recent.title || recent.role, recent.company].filter(Boolean).join(' bij ')
        : '';

    const haystack = `${role} ${keywords.join(' ')} ${summary} ${jobLine}`.toLowerCase();
    const tokens = q
        .toLowerCase()
        .split(/\s+/)
        .map((t) => t.replace(/^[("'`]+|[)"'.]+$/g, ''))
        .filter((t) => t.length >= 2);
    const literalHits = [...new Set(tokens.filter((t) => haystack.includes(t)))].slice(0, 5);

    const parts: string[] = [];

    if (pct != null && Number.isFinite(pct)) {
        if (pct >= 72) {
            parts.push(`Dit profiel hoort bij de beste resultaten voor “${q || 'je zoekopdracht'}” (${pct}% relevantie).`);
        } else if (pct >= 55) {
            parts.push(`Duidelijke aansluiting op “${q || 'je zoekopdracht'}” (${pct}% relevantie) — de inhoud van het dossier sluit goed aan.`);
        } else if (pct >= 40) {
            parts.push(`Redelijke match (${pct}%): nuttig om te bekijken; niet alles hoeft letterlijk in het CV te staan.`);
        } else {
            parts.push(`Staat lager in de ranglijst (${pct}%), maar er is nog verband met je zoekopdracht.`);
        }
    } else if (q) {
        parts.push(`Resultaat voor “${q}” op basis van de inhoud van dit kandidaatdossier.`);
    }

    if (literalHits.length > 0) {
        const joined = literalHits.map((x) => `“${x}”`).join(', ');
        parts.push(
            literalHits.length === 1
                ? `Het woord ${joined} komt voor in functie, skills/tags of in de CV-tekst.`
                : `De woorden ${joined} komen (deels) voor in functie, skills/tags of CV-tekst.`
        );
    } else if (q) {
        if (role) {
            parts.push(
                `Je zocht “${q}”; er is geen exacte woord-voor-woord treffer, maar de huidige rol (“${role}”) en de beschreven ervaring sluiten vaak inhoudelijk aan op dit soort zoekopdrachten.`
            );
        } else {
            parts.push(
                `Geen identieke woorden uit “${q}” zichtbaar in de kernvelden; de match komt vooral door overeenkomst in betekenis tussen je zoekzin en de CV-inhoud.`
            );
        }
    }

    if (kwShort) {
        parts.push(`Signalen uit het dossier: ${kwShort}.`);
    }
    if (jobLine) {
        parts.push(`Recente werklijn: ${jobLine}.`);
    } else if (summary.length > 40) {
        parts.push(`Kort uit het profiel: ${summary}${summary.length >= 220 ? '…' : ''}`);
    }

    let out = parts.join(' ').replace(/\s+/g, ' ').trim();
    if (out.length > 420) out = `${out.slice(0, 417).trim()}…`;
    return out || `Match op basis van je zoekopdracht en de gegevens in dit profiel.`;
}

function talentPoolProfileCity(profile: any): string {
    const c = profile?.candidate || {};
    const parsed = profile?.ai_parsed_data || profile?.parsed_cv || {};
    const raw =
        c.address_city ||
        profile?.address_city ||
        parsed.city ||
        parsed.residence ||
        parsed.address_city ||
        parsed.woonplaats ||
        c.city ||
        c.location ||
        parsed.location ||
        '';
    return String(raw).trim();
}

function talentPoolProfileSummary(profile: any): string {
    const c = profile?.candidate || {};
    const parsed = profile?.ai_parsed_data || profile?.parsed_cv || {};
    const raw =
        c.cv_summary ||
        profile?.cv_summary ||
        parsed.motivation ||
        parsed.summary ||
        parsed.Summary ||
        parsed.description ||
        parsed.profiel ||
        '';
    return String(raw).replace(/\s+/g, ' ').trim();
}

const TalentPool: React.FC<TalentPoolProps> = ({ user, onNavigate, onStartCall, onEditCallNote, candidates = [], vacancies = [], isLoading = false, initialCandidateId }) => {
    const isClientPortal = window.location.hostname.toLowerCase().includes('clientportal') || new URLSearchParams(window.location.search).get('clientportal') === 'true';
    const [docxLoading, setDocxLoading] = useState(false);
    const docxRef = useRef<HTMLDivElement>(null);
    const [docTemplates, setDocTemplates] = useState<any[]>([]);
    const [cvTemplates, setCvTemplates] = useState<CVTemplate[]>([]);
    const [proTemplates, setProTemplates] = useState<any[]>([]);

    // UI State
    const [activeTab, setActiveTab] = useState<'profile' | 'raw_cv' | 'cv' | 'match' | 'notes' | 'comm' | 'shortlist' | 'flows' | 'automations'>('profile');
    const [clientStatuses, setClientStatuses] = useState(DEFAULT_STATUSES);
    const [selectedProfile, setSelectedProfile] = useState<any>(null);
    const [quickViewCandidateId, setQuickViewCandidateId] = useState<string | null>(null);
    const [hasRestoredInitial, setHasRestoredInitial] = useState(false);

    const handleSetSelectedProfile = (profile: any | null) => {
        if (profile) {
            let p = { ...profile };
            
            // Fix double stringified JSON if it exists:
            if (typeof p.ai_parsed_data === 'string') { try { p.ai_parsed_data = JSON.parse(p.ai_parsed_data); } catch(e){} }
            if (typeof p.parsed_cv === 'string') { try { p.parsed_cv = JSON.parse(p.parsed_cv); } catch(e){} }
            
            // Deep clone nested objects so we don't mutate reference state accidentally
            if (p.ai_parsed_data) p.ai_parsed_data = { ...p.ai_parsed_data };
            if (p.parsed_cv) p.parsed_cv = { ...p.parsed_cv };

            // Unpack Bullhorn nested envelope:
            if (p.ai_parsed_data?.bullhornData) {
                p.ai_parsed_data = { ...p.ai_parsed_data.bullhornData, ...p.ai_parsed_data };
            }
            if (p.parsed_cv?.bullhornData) {
                p.parsed_cv = { ...p.parsed_cv.bullhornData, ...p.parsed_cv };
            }
            
            // Fix flat profiles from pgvector (if missing nested candidate, wrap it!)
            if (!p.candidate) {
                p.candidate = { ...profile };
            }
            
            setSelectedProfile(p);
            
            // Dispatch Taskbar Addition explicitly here to ensure it catches candidates not in global liveCandidates list
            if (p.candidate && p.candidate.id) {
               const title = p.candidate.name || 'Kandidaat';
               window.dispatchEvent(new CustomEvent('wt:taskbar-add', { detail: { id: p.candidate.id, type: 'candidate', title } }));
            }
            
            const slug = generateCandidateSlug(p.candidate?.name || '', p.candidate?.candidate_number || p.candidate?.id);
            const url = new URL(window.location.href);
            if (url.searchParams.get('candidate') !== slug) {
                url.searchParams.set('candidate', slug);
                window.history.pushState({}, '', url.pathname + url.search);
            }
        } else {
            setSelectedProfile(null);
            const url = new URL(window.location.href);
            if (url.searchParams.has('candidate')) {
                url.searchParams.delete('candidate');
                window.history.pushState({}, '', url.pathname + url.search);
            }
        }
    };

    // Resolve candidate selection from prop or URL slug
    useEffect(() => {
        const resolveInitialLoad = async () => {
            if (candidates.length === 0) return;

            // 1. Check URL Slug manually if prop isn't present
            const url = new URL(window.location.href);
            const slug = url.searchParams.get('candidate');
            
            // If prop changed to a specific candidate, OR we have a slug and no profile is open
            const targetId = initialCandidateId || (slug ? slug.split('-').pop() : null);
            
            if (targetId && targetId !== String(selectedProfile?.candidate?.id) && targetId !== String(selectedProfile?.candidate?.candidate_number)) {
                
                // Try from local loaded list first
                let found = candidates.find(c => 
                    String(c.candidate?.candidate_number) === targetId || 
                    String(c.candidate?.id) === targetId
                );

                if (found) {
                    handleSetSelectedProfile(found);
                    setHasRestoredInitial(true);
                    return;
                }

                // If not in local pagination cache, we MUST fetch directly from DB to open it!
                const fetched = await supabase_get_candidate_profile_by_slug_id(targetId, user?.clientId);
                if (fetched) {
                    handleSetSelectedProfile(fetched);
                    setHasRestoredInitial(true);
                }
            }
        };

        resolveInitialLoad();
    }, [initialCandidateId, candidates.length, user?.clientId]);
    const [showAddModal, setShowAddModal] = useState<boolean | 'manual'>(false);
    const [showPasteText, setShowPasteText] = useState(false);
    const [pastedCVText, setPastedCVText] = useState('');

    useEffect(() => {
        const handleNew = () => { setAddMode('new'); setShowAddModal(true); };
        window.addEventListener('open-new-candidate', handleNew);
        return () => window.removeEventListener('open-new-candidate', handleNew);
    }, []);

    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [templateType, setTemplateType] = useState<'cv' | 'doc' | 'pro'>('cv');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [subTab, setSubTab] = useState<'list' | 'ai' | 'stats' | 'data-quality' | 'db-editor'>('list');
    
    // Sort Config
    const [sortConfig, setSortConfig] = useState<{column: string, ascending: boolean}>({ column: 'candidate_number', ascending: false });
    
    // Candidate Sources Config
    const [candidateSources, setCandidateSources] = useState<string[]>(['Website', 'LinkedIn', 'Telefoon', 'Anders']);

    // Editing State
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState<any>(null);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [replacementCvFile, setReplacementCvFile] = useState<File | null>(null);

    // Raw CV State
    const [editRawCv, setEditRawCv] = useState('');
    const [isEditingRawCv, setIsEditingRawCv] = useState(false);
    const [isSavingRawCv, setIsSavingRawCv] = useState(false);

    // Vacancy Link State
    const [showVacancySelector, setShowVacancySelector] = useState(false);
    const [searchVacancy, setSearchVacancy] = useState('');
    const [isLinkingVacancy, setIsLinkingVacancy] = useState(false);

    const handleLinkVacancy = async (vacancyId: string) => {
        if (!selectedProfile?.candidate?.id) return;
        setIsLinkingVacancy(true);
        try {
            const { error } = await supabase.from('applications').insert({
                vacancy_id: vacancyId,
                candidate_id: selectedProfile.candidate.id,
                status: 'Shortlist'
            });
            if (error) {
                if (error.code === '23505') {
                    alert('Deze kandidaat is al gekoppeld aan deze vacature.');
                    return;
                }
                throw error;
            }
            alert("Kandidaat succesvol gekoppeld aan vacature!");
            setShowVacancySelector(false);
            if (selectedProfile?.candidate?.id) {
                fetchCandidateApplications(selectedProfile.candidate.id);
            }
        } catch (e: any) {
            console.error("Link error", e);
            alert("Koppelen mislukt: " + e.message);
        } finally {
            setIsLinkingVacancy(false);
        }
    };

    useEffect(() => {
        if (selectedProfile) {
            const d = selectedProfile.ai_parsed_data || {};
            const c = selectedProfile.candidate || {};
            let t = d.raw_cv_text;
            if (!t && c.original_cv_text) {
                t = c.original_cv_text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '').replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '').replace(/<[^>]*>?/gm, '').trim();
            }
            if (!t) {
                t = selectedProfile.parsed_cv?.motivation || d.motivation || d.description || d.summary || d.profiel;
            }
            setEditRawCv(t || '');
        }
    }, [selectedProfile]);

    const handleSaveRawCv = async () => {
        setIsSavingRawCv(true);
        try {
            const updatedAiData = {
                ...(selectedProfile.ai_parsed_data || {}),
                raw_cv_text: editRawCv
            };
            const { error } = await supabase.from('candidates')
                .update({ ai_parsed_data: updatedAiData })
                .eq('id', selectedProfile.id);
            if (error) throw error;
            setIsEditingRawCv(false);
            setSelectedProfile({ ...selectedProfile, ai_parsed_data: updatedAiData });
            alert("Ruwe CV data opgeslagen!");
        } catch (e: any) {
            console.error("Save error", e);
            alert("Opslaan mislukt: " + e.message);
        } finally {
            setIsSavingRawCv(false);
        }
    };

    // Call Feature State
    const [showCallModal, setShowCallModal] = useState(false);
    const [recruiterPhone, setRecruiterPhone] = useState((user as any)?.phone || '');
    const [isCalling, setIsCalling] = useState(false);
    const [callNotes, setCallNotes] = useState('');

    // Contact Edit State
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [editPhone, setEditPhone] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [isSavingContact, setIsSavingContact] = useState(false);

    // Sync selected profile contact info to edit state
    useEffect(() => {
        if (selectedProfile?.candidate) {
            setEditPhone(selectedProfile.candidate.phone || '');
            setEditEmail(selectedProfile.candidate.email || '');
        }
    }, [selectedProfile]);

    // Deletion State
    const [candidateToDelete, setCandidateToDelete] = useState<string | null>(null);
    const [deleteConfirmationName, setDeleteConfirmationName] = useState('');
    const [deletedIds, setDeletedIds] = useState<string[]>([]);

    // Filters
    const [nlQuery, setNlQuery] = useState('');
    const [searchedCandidates, setSearchedCandidates] = useState<any[]>([]);
    const [isSearchingBackend, setIsSearchingBackend] = useState(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [isAiSearching, setIsAiSearching] = useState(false);

    // Add Candidate Flow
    const [addStep, setAddStep] = useState(1);
    const [addMode, setAddMode] = useState('new');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadedPhoto, setUploadedPhoto] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isSavingNew, setIsSavingNew] = useState(false);
    const [parsedCandidate, setParsedCandidate] = useState<any>(null);
    const [manualEntry, setManualEntry] = useState(false);
    const [manualTagsInput, setManualTagsInput] = useState('');
    const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
    const [duplicateCandidateMatch, setDuplicateCandidateMatch] = useState<any>(null);

    // OneDrive URL state
    const [oneDriveUrl, setOneDriveUrl] = useState<string | null>(null);

    // Pro Template Pre-Generation Config
    const [proTemplateConfig, setProTemplateConfig] = useState<{
        isOpen: boolean;
        template: CVTemplate | null;
        candidateData: CVData;
        vacancyId: string;
        isTailoring: boolean;
        tailorResult?: any;
    }>({ isOpen: false, template: null, candidateData: {} as CVData, vacancyId: '', isTailoring: false });

    // AI Email Generator state
    const [showAiEmailModal, setShowAiEmailModal] = useState(false);
    const [showEmailChoiceModal, setShowEmailChoiceModal] = useState(false);
    const [showEmailConfirmModal, setShowEmailConfirmModal] = useState(false);
    
    // WhatsApp Feature State
    const [showWhatsAppOptionsModal, setShowWhatsAppOptionsModal] = useState(false);
    const [showWhatsAppEditor, setShowWhatsAppEditor] = useState(false);
    const [showWhatsAppConfirmModal, setShowWhatsAppConfirmModal] = useState(false);
    const [pendingWhatsAppText, setPendingWhatsAppText] = useState('');
    const [whatsAppText, setWhatsAppText] = useState('');
    const [isGeneratingWhatsApp, setIsGeneratingWhatsApp] = useState(false);
    const [selectedWhatsAppVacancy, setSelectedWhatsAppVacancy] = useState<string>('');
    const [emailContext, setEmailContext] = useState('Stuur kandidaat mail over vacature');
    const [emailVacancyId, setEmailVacancyId] = useState('');
    const [generatedEmailSubject, setGeneratedEmailSubject] = useState('');
    const [generatedEmailBody, setGeneratedEmailBody] = useState('');
    const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
    const [savedEmailTemplates, setSavedEmailTemplates] = useState<{ id: string, name: string, subject: string, body: string }[]>([]);

    // Pagination State
    const [localCandidates, setLocalCandidates] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize] = useState<number>(50);
    const [isFetchingPage, setIsFetchingPage] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Client-portal scoping: when in client portal, the talent pool is restricted
    // to candidates that have at least one application against a vacancy of the
    // logged-in opdrachtgever (matched on vacancies.opdrachtgever_id or client_id).
    // null  = not yet loaded (we should wait before fetching to avoid a flash of "all candidates")
    // []    = no scoped candidates (show empty)
    // [...] = explicit allowlist
    const [portalCandidateIds, setPortalCandidateIds] = useState<string[] | null>(null);


    // Filter Logic
    const [filterStatus, setFilterStatus] = useState<string | null>(null);
    const [filterDateRange, setFilterDateRange] = useState<string>('all');
    /** When `filterDateRange === 'custom'`: filter on candidate `created_at` (inclusive, local day bounds). */
    const [filterDateFrom, setFilterDateFrom] = useState<string>('');
    const [filterDateTo, setFilterDateTo] = useState<string>('');
    const [filterHasCV, setFilterHasCV] = useState<boolean>(false);
    const [filterAvailabilityDate, setFilterAvailabilityDate] = useState<string>('');

    // Pipeline Stage Counts for List View (global bar)
    const [pipelineStageCounts, setPipelineStageCounts] = useState<Record<string, number>>({});
    const [pipelineStageFilter, setPipelineStageFilter] = useState<string | null>(null);
    const [pipelineFilterCandidateIds, setPipelineFilterCandidateIds] = useState<string[] | null>(null);
    // Per-candidate application stage counts for row display
    const [candidateAppCounts, setCandidateAppCounts] = useState<Record<string, Record<string, number>>>({});
    const [isCountsLoading, setIsCountsLoading] = useState(true);

    const [candidateStageVacancies, setCandidateStageVacancies] = useState<
        Record<string, Partial<Record<TalentPoolListStageId, import('../utils/candidateJourneyBuckets').TalentPoolStageVacancyRow[]>>>
    >({});
    const [activePopover, setActivePopover] = useState<{ cId: string, stageId: string } | null>(null);

    const fetchCandidateAppCounts = async (isInitial = false) => {
        if (isInitial) setIsCountsLoading(true);
        let allData: any[] = [];
        let allHistory: any[] = [];
        let from = 0;
        const step = 1000;

        while (true) {
            const { data, error } = await supabase
                .from('applications')
                .select('id,candidate_id,status,vacancy_id(id,title,vacancy_number)')
                .range(from, from + step - 1);
            if (error || !data || data.length === 0) break;
            allData = [...allData, ...data];
            if (data.length < step) break;
            from += step;
        }

        from = 0;
        while (true) {
            const { data, error } = await supabase
                .from('application_stage_history')
                .select('candidate_id,application_id,stage')
                .range(from, from + step - 1);
            if (error) {
                if (error.code !== '42P01' && !String(error.message || '').includes('application_stage_history')) {
                    console.warn('[TalentPool] application_stage_history', error);
                }
                break;
            }
            if (!data || data.length === 0) break;
            allHistory = [...allHistory, ...data];
            if (data.length < step) break;
            from += step;
        }

        if (allData.length > 0 || allHistory.length > 0) {
            const counts = buildTalentPoolListStageCounts(allData, allHistory);
            const stageVacs = buildTalentPoolListStageVacancies(allData, allHistory);
            setCandidateAppCounts(counts);
            setCandidateStageVacancies(stageVacs);
        }
        if (isInitial) setIsCountsLoading(false);
    };

    useEffect(() => {
        fetchCandidateAppCounts(true);

        const channel = supabase
            .channel('applications-counts')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'applications' }, () => {
                fetchCandidateAppCounts();
            })
            .on('postgres_changes', { event: '*', schema: 'public', table: 'application_stage_history' }, () => {
                fetchCandidateAppCounts();
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, []);

    // Inline status editing in table rows
    const [statusDropdownOpen, setStatusDropdownOpen] = useState<string | null>(null); // profile.id
    const [localStatusOverrides, setLocalStatusOverrides] = useState<Record<string, string>>({});

    const handleInlineStatusChange = async (profileId: string, newStatus: string) => {
        const previousStatus = localStatusOverrides[profileId]
            ?? localCandidates.find((c: any) => c.id === profileId)?.status
            ?? localCandidates.find((c: any) => c.id === profileId)?.candidate?.status;
        console.log('[TalentPool] Status changed (list)', { profileId, previousStatus, newStatus });
        setLocalStatusOverrides(prev => ({ ...prev, [profileId]: newStatus }));
        setStatusDropdownOpen(null);
        const { error } = await supabase.from('candidates').update({ status: newStatus }).eq('id', profileId);
        if (error) {
            console.error('[TalentPool] Status update failed (list)', error);
            return;
        }
        const profile = localCandidates.find((c: any) => c.id === profileId);
        void triggerCandidateStatusChanged({
            candidateId: profile?.candidate?.id ?? profileId,
            profileId,
            clientId: user?.clientId,
            previousStatus,
            newStatus,
            source: 'talentpool_list',
            user_id: user?.id,
            candidateName: profile?.candidate?.name ?? profile?.name,
        });
    };

    useEffect(() => {
        const fetchPipelineCounts = async () => {
            try {
                const { data: openVacs } = await supabase.from('vacancies').select('id').eq('data->>lifecycleStatus' as any, 'Open');
                const openIds = (openVacs || []).map((v: any) => v.id);
                if (openIds.length === 0) return;
                const counts: Record<string, number> = { 'Shortlist': 0, 'Voorgesteld': 0, 'Gesprek': 0, 'Aanbieding': 0, 'Plaatsing': 0 };
                const CHUNK = 20;
                const stages = Object.keys(counts);
                for (let i = 0; i < openIds.length; i += CHUNK) {
                    const chunkIds = openIds.slice(i, i + CHUNK);
                    await Promise.all(stages.map(async stage => {
                        const { count } = await (supabase as any)
                            .from('applications')
                            .select('*', { count: 'exact', head: true })
                            .in('vacancy_id', chunkIds)
                            .eq('status', stage);
                        counts[stage] += count || 0;
                    }));
                }
                setPipelineStageCounts(counts);
            } catch (e) { console.error(e); }
        };
        fetchPipelineCounts();
    }, []);

    useEffect(() => {
        if (!pipelineStageFilter) { setPipelineFilterCandidateIds(null); return; }
        const fetch = async () => {
            try {
                const { data: openVacs } = await supabase.from('vacancies').select('id').eq('data->>lifecycleStatus' as any, 'Open');
                const openIds = (openVacs || []).map((v: any) => v.id);
                if (!openIds.length) { setPipelineFilterCandidateIds([]); return; }
                
                const CHUNK = 20;
                let allCandidateIds: string[] = [];
                for (let i = 0; i < openIds.length; i += CHUNK) {
                    const chunkIds = openIds.slice(i, i + CHUNK);
                    const { data } = await supabase.from('applications').select('candidate_id').in('vacancy_id', chunkIds).eq('status', pipelineStageFilter);
                    if (data) {
                        allCandidateIds = [...allCandidateIds, ...data.map((a: any) => a.candidate_id)];
                    }
                }
                setPipelineFilterCandidateIds(allCandidateIds);
            } catch (e) { setPipelineFilterCandidateIds(null); }
        };
        fetch();
    }, [pipelineStageFilter]);

    // Fetch candidate sources
    useEffect(() => {
        if (user?.clientId) {
            import('../services/supabaseService').then(({ supabase_get_client_settings }) => {
                supabase_get_client_settings(user.clientId as string).then(data => {
                    if (data?.candidate_sources && Array.isArray(data.candidate_sources)) {
                        setCandidateSources(data.candidate_sources);
                    }
                    if (data?.talentpool_statuses && Array.isArray(data.talentpool_statuses) && data.talentpool_statuses.length > 0) {
                        setClientStatuses(data.talentpool_statuses);
                    }
                }).catch(console.error);
            });
        }
    }, [user?.clientId]);
    const [filterExperience, setFilterExperience] = useState<string | null>(null);
    const [aiSearchInput, setAiSearchInput] = useState('');

    // Persist list-view filters/sort/search across refresh/login
    const listStateStorageKey = useMemo(() => {
        const u = (user as any)?.id || 'anon';
        const c = (user as any)?.clientId || 'all';
        return `talentpool:liststate:v1:${u}:${c}`;
    }, [user]);
    const listStateLoadedRef = useRef<string | null>(null);

    useEffect(() => {
        if (!listStateStorageKey) return;
        if (listStateLoadedRef.current === listStateStorageKey) return;
        listStateLoadedRef.current = listStateStorageKey;
        try {
            const raw = window.localStorage.getItem(listStateStorageKey);
            if (!raw) return;
            const s = JSON.parse(raw || '{}');

            if (typeof s.nlQuery === 'string') setNlQuery(s.nlQuery);
            if (s.sortConfig && typeof s.sortConfig === 'object') {
                const col = typeof s.sortConfig.column === 'string' ? s.sortConfig.column : undefined;
                const asc = typeof s.sortConfig.ascending === 'boolean' ? s.sortConfig.ascending : undefined;
                if (col && asc != null) setSortConfig({ column: col, ascending: asc });
            }
            if (typeof s.filterStatus === 'string' || s.filterStatus === null) setFilterStatus(s.filterStatus ?? null);
            if (typeof s.filterDateRange === 'string') setFilterDateRange(s.filterDateRange);
            if (typeof s.filterDateFrom === 'string') setFilterDateFrom(s.filterDateFrom);
            if (typeof s.filterDateTo === 'string') setFilterDateTo(s.filterDateTo);
            if (typeof s.filterHasCV === 'boolean') setFilterHasCV(s.filterHasCV);
            if (typeof s.filterAvailabilityDate === 'string') setFilterAvailabilityDate(s.filterAvailabilityDate);
            if (typeof s.filterExperience === 'string' || s.filterExperience === null) setFilterExperience(s.filterExperience ?? null);
            if (Array.isArray(s.selectedTags)) setSelectedTags(s.selectedTags.filter((t: any) => typeof t === 'string'));
            if (typeof s.pipelineStageFilter === 'string' || s.pipelineStageFilter === null) setPipelineStageFilter(s.pipelineStageFilter ?? null);
        } catch (e) {
            console.warn('[TalentPool] Failed to restore liststate', e);
        }
        // When restoring filters, start at page 1
        setCurrentPage(1);
    }, [listStateStorageKey]);

    const listStateSaveTimer = useRef<number | null>(null);
    useEffect(() => {
        // Avoid saving before we've attempted to load for this key
        if (listStateLoadedRef.current !== listStateStorageKey) return;
        if (!listStateStorageKey) return;
        if (listStateSaveTimer.current) window.clearTimeout(listStateSaveTimer.current);
        listStateSaveTimer.current = window.setTimeout(() => {
            try {
                const payload = {
                    nlQuery,
                    sortConfig,
                    filterStatus,
                    filterDateRange,
                    filterDateFrom,
                    filterDateTo,
                    filterHasCV,
                    filterAvailabilityDate,
                    filterExperience,
                    selectedTags,
                    pipelineStageFilter,
                };
                window.localStorage.setItem(listStateStorageKey, JSON.stringify(payload));
            } catch (e) {
                // ignore quota / private mode
            }
        }, 250);
        return () => {
            if (listStateSaveTimer.current) window.clearTimeout(listStateSaveTimer.current);
        };
    }, [
        listStateStorageKey,
        nlQuery,
        sortConfig,
        filterStatus,
        filterDateRange,
        filterDateFrom,
        filterDateTo,
        filterHasCV,
        filterAvailabilityDate,
        filterExperience,
        selectedTags,
        pipelineStageFilter,
    ]);

    // Remove fullDatabaseCandidates background sync
    const [precomputedStats, setPrecomputedStats] = useState<any>(null);

    useEffect(() => {
        let isMounted = true;
        const fetchStatsAsync = async () => {
             const clientIdOpt = user?.role === UserRole.CLIENT || user?.role === UserRole.CLIENT_RECRUITER ? user.clientId : undefined;
             try {
                 const { supabase_get_talentpool_stats } = await import('../services/performanceService');
                 const data = await supabase_get_talentpool_stats(clientIdOpt);
                 if (isMounted && data) {
                     setPrecomputedStats(data);
                 }
             } catch (e) {
                 console.error("Stats fetch failed:", e);
             }
        };
        fetchStatsAsync();
        return () => { isMounted = false; };
    }, [user?.clientId, user?.role]);

    // AI Wand state for Email Generator
    const [aiMenuPos, setAiMenuPos] = useState<{ top: number, left: number } | null>(null);
    const [aiMenuSelection, setAiMenuSelection] = useState<{ start: number, end: number, text: string } | null>(null);
    const [showAiTone, setShowAiTone] = useState(false);
    const [isAiWandLoading, setIsAiWandLoading] = useState(false);
    const emailBodyRef = useRef<HTMLTextAreaElement>(null);

    const handleEmailTextSelection = () => {
        if (!emailBodyRef.current) return;
        const start = emailBodyRef.current.selectionStart;
        const end = emailBodyRef.current.selectionEnd;
        if (start !== end) {
            const text = generatedEmailBody.substring(start, end);
            const rect = emailBodyRef.current.getBoundingClientRect();
            setAiMenuPos({ top: rect.top - 50, left: rect.left + rect.width / 2 });
            setAiMenuSelection({ start, end, text });
            setShowAiTone(false);
        } else {
            setAiMenuPos(null);
            setAiMenuSelection(null);
        }
    };

    const handleEmailAiAction = async (action: string, param?: string) => {
        if (!aiMenuSelection) return;
        setIsAiWandLoading(true);
        try {
            let instruction = "";
            switch (action) {
                case 'edit': instruction = "Corrigeer de spelling, grammatica en zinsbouw van deze tekst. Behoud de inhoud."; break;
                case 'rewrite': instruction = "Herschrijf deze tekst om het vloeiender en wervender te maken."; break;
                case 'shorter': instruction = "Maak deze tekst korter en krachtiger zonder essentiële informatie te verliezen."; break;
                case 'longer': instruction = "Breid deze tekst uit met relevante details en heldere uitleg."; break;
                case 'tone': instruction = `Herschrijf deze tekst in een ${param} toon.`; break;
                case 'custom': instruction = param || "Verbeter deze tekst op natuurlijke wijze."; break;
            }
            const { refineWebsiteText } = await import('../services/geminiService');
            const refined = await refineWebsiteText(aiMenuSelection.text, instruction);
            if (refined) {
                const newText = generatedEmailBody.substring(0, aiMenuSelection.start) + refined + generatedEmailBody.substring(aiMenuSelection.end);
                setGeneratedEmailBody(newText);
                setAiMenuPos(null);
                setAiMenuSelection(null);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsAiWandLoading(false);
        }
    };

    const handleEmailCustomRewrite = () => {
        const prompt = window.prompt("Waar wil je dat Froukje AI op let of verandert in de geselecteerde tekst?");
        if (prompt) {
            handleEmailAiAction('custom', prompt);
        }
    };

    useEffect(() => {
        getOneDriveFolderUrl('Kandidaten CVs').then(url => {
            if (url) setOneDriveUrl(url);
        });
    }, [user?.id]);

    // Pool Selection State
    const [poolCandidates, setPoolCandidates] = useState<any[]>([]);
    const [selectedPoolIds, setSelectedPoolIds] = useState<string[]>([]);
    const [poolSearch, setPoolSearch] = useState('');

    // CV Mapper State
    const [showCvMapper, setShowCvMapper] = useState(false);
    
    // Docx preview reactivity
    useEffect(() => {
        if (showCvMapper && uploadedFile && (uploadedFile.name.toLowerCase().endsWith('.docx') || uploadedFile.name.toLowerCase().endsWith('.doc')) && docxRef.current) {
            setDocxLoading(true);
            renderAsync(uploadedFile, docxRef.current, undefined, { className: 'docx-viewer', inWrapper: false })
                .catch(e => console.error('docx-preview err:', e))
                .finally(() => setDocxLoading(false));
        }
    }, [showCvMapper, uploadedFile]);
    const [fileBase64, setFileBase64] = useState<string | null>(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
    const [isPoolLoading, setIsPoolLoading] = useState(false);
    const [showShortcodes, setShowShortcodes] = useState(false);

    // Match Flow
    const [matchVacancyId, setMatchVacancyId] = useState('');
    const [matchResult, setMatchResult] = useState<any>(null);
    const [visibleMatchCount, setVisibleMatchCount] = useState(10);
    const [isMatching, setIsMatching] = useState(false);
    const [matchStatusText, setMatchStatusText] = useState('');

    // CV Data
    const [cvVersions, setCvVersions] = useState<any[]>([]);

    // Automation Flows
    const [automationFlows, setAutomationFlows] = useState<AutomationFlow[]>([]);

    useEffect(() => {
        const loadFlows = async () => {
            try {
                const flows = await supabase_get_flows();
                if (flows) setAutomationFlows(flows.filter((f: any) => f.status === 'active'));
            } catch (e) {
                console.error("Failed to load flows", e);
            }
        };
        loadFlows();
    }, []);
    const [candidateDocs, setCandidateDocs] = useState<any[]>([]);
    const [candidateApplications, setCandidateApplications] = useState<any[]>([]);

    const talentPoolWhatsAppBulk = useMemo(() => {
        if (!selectedProfile) return { candidates: [] as const, vacancy: undefined as any };
        const activeApps = (candidateApplications || []).filter((a: any) => a.status !== 'Draft_CV');
        const app =
            (selectedWhatsAppVacancy &&
                activeApps.find((a: any) => String(a.vacancy_id) === String(selectedWhatsAppVacancy))) ||
            activeApps[0];
        const vac =
            (selectedWhatsAppVacancy
                ? vacancies.find((v: any) => String(v.id) === String(selectedWhatsAppVacancy))
                : undefined) || app?.vacancies;
        return {
            candidates: [
                {
                    id: selectedProfile.candidate.id,
                    name: selectedProfile.candidate.name,
                    email: selectedProfile.candidate.email,
                    phone: selectedProfile.candidate.phone,
                    role: selectedProfile.candidate.role,
                    vacancyTitle: vacancyTitleFromVacancyRecord(vac),
                    companyName: companyNameFromVacancyRecord(vac),
                },
            ],
            vacancy: vac,
        };
    }, [selectedProfile, candidateApplications, selectedWhatsAppVacancy, vacancies]);
    const [candidateInterviews, setCandidateInterviews] = useState<any[]>([]);
    const [candidatePlacements, setCandidatePlacements] = useState<any[]>([]);
    const [candidateStageHistory, setCandidateStageHistory] = useState<any[]>([]);
    const [shortlistScope, setShortlistScope] = useState<'current' | 'all'>('all');
    const [shortlistFocusPhase, setShortlistFocusPhase] = useState<ShortlistPhaseId | null>(null);
    const [shortlistFocusKey, setShortlistFocusKey] = useState(0);
    const [shortlistMoveModalOpen, setShortlistMoveModalOpen] = useState(false);
    const [shortlistMoveDetails, setShortlistMoveDetails] = useState<any>(null);
    const [shortlistEmailTemplates, setShortlistEmailTemplates] = useState<any[]>([]);
    const [shortlistRejectionReasons, setShortlistRejectionReasons] = useState<string[]>([
        'Kandidaat teruggetrokken',
        'Afgewezen door opdrachtgever',
        'Opdracht ingetrokken',
        'Overig',
    ]);
    const [shortlistDeleteTarget, setShortlistDeleteTarget] = useState<{
        kind: 'application' | 'placement';
        id: string;
        subtitle: string;
    } | null>(null);
    const [shortlistDeleteInput, setShortlistDeleteInput] = useState('');
    const [shortlistDeleteBusy, setShortlistDeleteBusy] = useState(false);
    const [isLoadingVersions, setIsLoadingVersions] = useState(false);

    const [showRemapDocSelector, setShowRemapDocSelector] = useState(false);
    const [remapDocsList, setRemapDocsList] = useState<any[]>([]);
    
    // Comm Data
    const [communications, setCommunications] = useState<any[]>([]);
    const [newCommText, setNewCommText] = useState('');
    const [isSendingComm, setIsSendingComm] = useState(false);
    const [commType, setCommType] = useState<'note' | 'email' | 'phone' | 'whatsapp'>('note');
    const commScrollRef = useRef<HTMLDivElement>(null);

    // Interview Modal State
    const [showInterviewModal, setShowInterviewModal] = useState(false);
    const [interviewCommText, setInterviewCommText] = useState("Beste {{name}},\n\nWe hebben je profiel met veel interesse bekeken. Graag nodigen we je uit voor een kort kennismakingsgesprek om de mogelijkheden te bespreken.\n\nLaat ons weten wanneer het jou zou uitkomen.\n\nMet vriendelijke groet,");

    // AI & Statistics State

    const [aiAnalysisResult, setAiAnalysisResult] = useState<any>(null);
    const [showStats, setShowStats] = useState(false);
    const [isAiFiltering, setIsAiFiltering] = useState(false);
    const [activeAiFilter, setActiveAiFilter] = useState<string[] | null>(null);
    const [lastAiSearchQuery, setLastAiSearchQuery] = useState<string | null>(null);

    const aiMatchById = useMemo(() => {
        const map: Record<string, { reason: string; score?: number }> = {};
        if (!aiAnalysisResult) return map;
        const matches = aiAnalysisResult.matches;
        if (Array.isArray(matches)) {
            for (const m of matches) {
                const id = m?.id;
                if (id == null) continue;
                const sc = typeof m.score === 'number' && Number.isFinite(m.score) ? m.score : undefined;
                map[String(id)] = {
                    reason:
                        typeof m.reason === 'string' && m.reason.trim()
                            ? m.reason.trim()
                            : 'Semantische match op je zoekopdracht.',
                    score: sc,
                };
            }
        }
        const best = aiAnalysisResult.bestMatches;
        const qLabel = lastAiSearchQuery || '';
        if (Array.isArray(best)) {
            for (const p of best) {
                const id = p?.id ?? p?.candidate_id;
                if (id == null) continue;
                const sid = String(id);
                if (map[sid]) continue;
                const pct = vectorSimilarityToPercent(p);
                map[sid] = {
                    reason: buildAiVectorMatchReason(qLabel, p, pct ?? undefined),
                    score: pct,
                };
            }
        }
        return map;
    }, [aiAnalysisResult, lastAiSearchQuery]);

    const showAiMatchColumn = useMemo(() => Object.keys(aiMatchById).length > 0, [aiMatchById]);

    const clearAiSearch = useCallback(() => {
        setAiAnalysisResult(null);
        setActiveAiFilter(null);
        setLastAiSearchQuery(null);
        setRefreshTrigger((t) => t + 1);
    }, []);

    useEffect(() => {
        const fn = (ev: Event) => {
            const d = (ev as CustomEvent<{ vacancyId?: string; applicationId?: string }>).detail;
            if (!d?.applicationId) return;
            try {
                const raw = sessionStorage.getItem(VOORGESTELD_RESUME_BUNDLE_KEY);
                if (!raw) return;
                const b = JSON.parse(raw);
                if (!d.vacancyId || !resumeBundleMatchesVacancyEvent(b, d)) return;
                setShortlistMoveDetails(b.move);
                setShortlistMoveModalOpen(true);
            } catch {
                /* ignore */
            }
        };
        window.addEventListener(RESUME_VOORGESTELD_MOVE_MODAL_EVENT, fn as EventListener);
        return () => window.removeEventListener(RESUME_VOORGESTELD_MOVE_MODAL_EVENT, fn as EventListener);
    }, []);

    // AI Wand state for Email Generator
    useEffect(() => {
        const initTemplates = async () => {
            try {
                const docData = await supabase_get_documents(user?.clientId || '', true);

                // Also fetch DOCX files from document_files to use as templates
                let fileTemplates: any[] = [];
                if (user?.id) {
                    const files = await supabase_get_document_files(user.id);
                    if (files) {
                        fileTemplates = files
                            .filter((f: any) => f.file_type === 'docx' || f.file_type === 'doc')
                            .map((f: any) => ({
                                id: f.id,
                                title: f.title,
                                file_type: 'docx', // Normalize
                                is_template: true, // Treat as template for this purpose
                                storage_path: f.storage_path
                            }));
                    }
                }

                setDocTemplates([...(docData || []), ...fileTemplates]);
                const cvData = await supabase_get_cv_templates(user?.clientId);
                setCvTemplates(cvData || []);
                if (user?.id) {
                    const proData = await supabase_get_pro_cv_templates(user.id);
                    setProTemplates(proData || []);
                }
            } catch (e) { console.error(e); }
        }
        if (user?.clientId) {
            initTemplates();
        }
    }, [user]);

    useEffect(() => {
        if (selectedProfile) {
            // Fix: Use ai_parsed_data from Supabase if local parsed_cv is missing
            const rawData = selectedProfile.parsed_cv || selectedProfile.ai_parsed_data || {};

            const aiBase =
                typeof selectedProfile.ai_parsed_data === 'object' && selectedProfile.ai_parsed_data
                    ? { ...selectedProfile.ai_parsed_data }
                    : {};
            const dobRaw =
                extractDateOfBirthFromProfile(selectedProfile) ||
                String(aiBase.dateOfBirth || aiBase.geboortedatum || '').trim();
            const dobNorm = normalizeDateOfBirthToInputValue(dobRaw) || dobRaw;

            const formData: any = {
                candidate: { ...selectedProfile.candidate },
                parsed_cv: normalizeCvAnalysisResult(JSON.parse(JSON.stringify(rawData))),
                ai_parsed_data: {
                    ...normalizeCvAnalysisResult(aiBase),
                    ...(dobNorm ? { dateOfBirth: dobNorm, geboortedatum: dobNorm } : {}),
                },
            };

            if (formData.parsed_cv.timeline?.length) {
                formData.parsed_cv.timeline = formData.parsed_cv.timeline.map((item: any) => ({
                    ...item,
                    description: item.description ? item.description.replace(/([^\n])\s*[•-]/g, '$1\n•') : ''
                }));
            }

            if (!formData.parsed_cv.education) formData.parsed_cv.education = [];
            if (!formData.parsed_cv.courses) formData.parsed_cv.courses = [];
            if (!formData.parsed_cv.hobbies) formData.parsed_cv.hobbies = [];
            if (!formData.parsed_cv.education) formData.parsed_cv.education = [];
            if (!formData.parsed_cv.courses) formData.parsed_cv.courses = [];
            if (!formData.parsed_cv.hobbies) formData.parsed_cv.hobbies = [];
            
            // Fallback for tags/skills from bullhorn
            if (!formData.parsed_cv.tags || formData.parsed_cv.tags.length === 0) {
                if (selectedProfile.candidate?.skill_set) {
                   const rawSkills = selectedProfile.candidate.skill_set;
                   if (Array.isArray(rawSkills)) {
                       formData.parsed_cv.tags = rawSkills.filter(Boolean);
                   } else if (typeof rawSkills === 'string') {
                       formData.parsed_cv.tags = rawSkills.split(',').map((s: string) => s.trim()).filter(Boolean);
                   } else {
                       formData.parsed_cv.tags = [];
                   }
                } else {
                   formData.parsed_cv.tags = [];
                }
            }


            // Fallback for summary/motivation from bullhorn description
            if (!formData.parsed_cv.motivation) {
                 // Removed fallback to raw CV HTML intentionally. We only want explicitly parsed summaries text.
            }

            setEditForm(formData);
            console.log("Selected Profile Data:", selectedProfile); // DEBUG: Check if ai_parsed_data is present
            setIsEditing(false);
            setMatchResult(null); // Reset match result when switching profiles
            setVisibleMatchCount(10);

            fetchCVVersions(selectedProfile.candidate.id);
            fetchCandidateDocs(selectedProfile.candidate.id);
            fetchCandidateApplications(selectedProfile.candidate.id);
            fetchCommunications(selectedProfile.id);
        } else {
            setEditForm(null);
        }
    }, [selectedProfile]);

    useEffect(() => {
        if (activeTab === 'comm' && commScrollRef.current) {
            commScrollRef.current.scrollTop = commScrollRef.current.scrollHeight;
        }
        // Refresh documents when switching to CV/Docs tab to ensure new saves appear
        if (activeTab === 'cv' && selectedProfile?.candidate?.id) {
            fetchCandidateDocs(selectedProfile.candidate.id);
            fetchCVVersions(selectedProfile.candidate.id);
        }
    }, [communications, activeTab, selectedProfile]);

    const fetchCVVersions = async (candidateId: string) => {
        if (!candidateId) return;
        setIsLoadingVersions(true);
        try {
            const versions = await supabase_get_cv_versions(candidateId);
            setCvVersions(versions || []);
        } catch (e) {
            console.error("Error fetching versions", e);
        } finally {
            setIsLoadingVersions(false);
        }
    };

    const fetchCandidateDocs = async (candidateId: string) => {
        if (!candidateId || !user?.clientId) return;
        try {
            console.log(`Fetching docs for candidate: ${candidateId} (Client: ${user.clientId})`);
            const docs = await supabase_get_documents(user.clientId, false, candidateId);

            // Also fetch OnlyOffice files for the user (as candidate specific filtering isn't fully supported yet in DB)
            // But we can filter by matching strings if we want, or just show all for now.
            // Given the requirement "Show upload successful", we definitely want the user's uploaded files.
            const files = await supabase_get_document_files(user.id || user.clientId);

            const fileDocs = files.map((f: any) => ({
                id: f.id,
                title: f.title,
                updated_at: f.updated_at,
                is_file: true,
                storage_path: f.storage_path,
                version: f.version,
                file_type: f.file_type,
                // Add dummy content/status to satisfy type if needed
                status: 'Draft',
                content: ''
            }));

            // Combine and sort
            const combined = [...(docs || []), ...fileDocs].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

            console.log("Docs fetched:", combined);
            setCandidateDocs(combined);
        } catch (e) {
            console.error("Error fetching candidate docs", e);
        }
    };

    const fetchCandidateApplications = async (candidateId: string) => {
        if (!candidateId) return;
        try {
            const apps = await supabase_get_candidate_applications(candidateId);
            setCandidateApplications(apps);
        } catch (e) {
            console.error("Error fetching candidate applications", e);
        }
    };

    const refreshCandidateShortlistData = useCallback(async (candidateId: string) => {
        if (!candidateId) return;
        try {
            const [apps, ints, pls, hist] = await Promise.all([
                supabase_get_candidate_applications(candidateId),
                supabase_get_candidate_interviews(candidateId),
                supabase_get_candidate_placements_light(candidateId),
                supabase_get_candidate_application_stage_history(candidateId),
            ]);
            setCandidateApplications(apps || []);
            setCandidateInterviews(ints || []);
            setCandidatePlacements(pls || []);
            setCandidateStageHistory(hist || []);
        } catch (e) {
            console.error('refreshCandidateShortlistData', e);
        }
    }, []);

    useEffect(() => {
        if (isClientPortal || !user?.clientId) return;
        supabase_get_templates().then((t) => {
            setShortlistEmailTemplates((prev) => (prev.length > 0 ? prev : t || []));
        });
        supabase
            .from('clients')
            .select('data')
            .eq('id', user.clientId)
            .single()
            .then(({ data: clientData }: any) => {
                if (clientData?.data?.ai_matching_settings?.rejectionReasons) {
                    setShortlistRejectionReasons(clientData.data.ai_matching_settings.rejectionReasons);
                }
                if (clientData?.data?.email_templates?.length) {
                    setShortlistEmailTemplates(clientData.data.email_templates);
                }
            });
    }, [user?.clientId, isClientPortal]);

    const openShortlistMoveForApplication = useCallback(
        (app: any) => {
            if (isClientPortal) {
                window.dispatchEvent(
                    new CustomEvent('wt:toast', {
                        detail: { message: 'Status wijzigen is niet beschikbaar in het klantportaal.', type: 'error' },
                    })
                );
                return;
            }
            const cand = selectedProfile?.candidate;
            const vac = app.vacancies;
            const clientName =
                vac?.opdrachtgever?.name ||
                vac?.clients?.name ||
                vac?.data?.clientName ||
                vac?.data?.werkplek ||
                '';
            const clientId = vac?.client_id || user?.clientId;
            const vd = vac?.data || {};
            const defaultClientEmail = String(
                vd.contactEmail ||
                    vd.clientContactEmail ||
                    vd.contactPersonEmail ||
                    vd.email ||
                    vd.hiringManagerEmail ||
                    ''
            ).trim();
            setShortlistMoveDetails({
                applicationId: app.id,
                candidateId: app.candidate_id || cand?.id,
                candidateRealId: app.candidate_id || cand?.id,
                candidateName: cand?.name || app.candidates?.name || 'Kandidaat',
                candidateEmail: getCandidateEmail(cand || app.candidates),
                fromStage: app.status || 'Shortlist',
                toStage: app.status || 'Shortlist',
                clientId,
                vacancyId: app.vacancy_id,
                vacancyTitle: vac?.title || '',
                clientName,
                defaultClientEmail: defaultClientEmail || undefined,
            });
            setShortlistMoveModalOpen(true);
        },
        [isClientPortal, selectedProfile, user?.clientId]
    );

    const requestShortlistApplicationDelete = useCallback(
        (app: any) => {
            if (isClientPortal) {
                window.dispatchEvent(
                    new CustomEvent('wt:toast', {
                        detail: { message: 'Verwijderen is niet beschikbaar in het klantportaal.', type: 'error' },
                    })
                );
                return;
            }
            const vac = app.vacancies;
            const title = vac?.title || 'Onbekende vacature';
            setShortlistDeleteInput('');
            setShortlistDeleteTarget({
                kind: 'application',
                id: app.id,
                subtitle: `${title} — deze sollicitatie wordt permanent uit de pipeline gehaald.`,
            });
        },
        [isClientPortal]
    );

    const requestShortlistPlacementDelete = useCallback(
        (p: any) => {
            if (isClientPortal) {
                window.dispatchEvent(
                    new CustomEvent('wt:toast', {
                        detail: { message: 'Verwijderen is niet beschikbaar in het klantportaal.', type: 'error' },
                    })
                );
                return;
            }
            const vac = p.vacancies;
            const d = p?.data && typeof p.data === 'object' ? p.data : null;
            const title =
                vac?.title?.trim() ||
                (typeof d?.title === 'string' ? d.title.trim() : '') ||
                (p.number != null ? `Plaatsing #${p.number}` : 'Plaatsing');
            setShortlistDeleteInput('');
            setShortlistDeleteTarget({
                kind: 'placement',
                id: p.id,
                subtitle: `${title} — dit plaatsingsrecord wordt permanent verwijderd.`,
            });
        },
        [isClientPortal]
    );

    const handleShortlistConfirmMove = useCallback(
        async (
            channel: 'email' | 'whatsapp' | 'log',
            templateId: string,
            subject: string,
            messageContent: string,
            internalNoteContent: string,
            targetStage: string,
            asDraft: boolean = false,
            rejectionReason?: string,
            cc?: string,
            bcc?: string,
            fromEmail?: string,
            voorgesteldClient?: import('../utils/portalProposalThread').VoorgesteldClientPayload | null,
            resolvedCandidateEmail?: string,
        ) => {
            if (isClientPortal) return;
            const moveDetails = shortlistMoveDetails;
            if (!moveDetails?.applicationId) return;
            try {
                const finalInternalNote = rejectionReason
                    ? `${internalNoteContent}\n\nAfwijzingsreden: ${rejectionReason}`.trim()
                    : internalNoteContent;
                const targetStageObj =
                    DEFAULT_KANBAN_STAGES.find((s) => s.name === targetStage || s.id === targetStage) ||
                    { id: targetStage, name: targetStage };
                await supabase_update_application_status(moveDetails.applicationId, targetStageObj.id);
                await triggerAutomation('stage_changed', {
                    vacancyId: moveDetails.vacancyId,
                    candidateId: moveDetails.candidateRealId,
                    stageId: targetStageObj.id,
                    stageName: targetStageObj.name,
                    user_id: (user as any)?.id,
                });
                const isVoorgesteld = targetStageObj.name === 'Voorgesteld' || targetStageObj.id === 'Voorgesteld';
                if (isVoorgesteld && voorgesteldClient && !asDraft) {
                    if (!voorgesteldClient.publishToPortal) {
                        const { error: msErr } = await supabase_recruiter_apply_voorgesteld_match_score(
                            moveDetails.applicationId,
                            voorgesteldClient.portalMatchScore,
                        );
                        if (msErr) console.error('[TalentPool] match_score sync', msErr);
                    }
                    if (voorgesteldClient.publishToPortal) {
                        const { error: pubErr } = await supabase_recruiter_publish_portal_proposal(
                            moveDetails.applicationId,
                            String(moveDetails.vacancyId || ''),
                            voorgesteldClient,
                            {
                                recruiterName: (user as any)?.profile?.name || (user as any)?.email || 'Recruiter',
                                clientId: moveDetails.clientId,
                                candidateRealId: moveDetails.candidateRealId || moveDetails.candidateId,
                            }
                        );
                        if (pubErr) console.error('[TalentPool] portal publish', pubErr);
                    }
                    if (voorgesteldClient.emailToClient && voorgesteldClient.clientEmailTo?.trim()) {
                        try {
                            const { sendDirectMessage } = await import('../services/microsoftGraphService');
                            const sig = (user as any)?.profile?.email_signature || '';
                            let body = voorgesteldClient.clientEmailBodyHtml.replace(/\n/g, '<br>');
                            const isSigIn = sig && body.replace(/\s/g, '').includes(sig.replace(/\s/g, '').substring(0, 20));
                            if (body.match(/\[HANDTEKENING\]/i)) body = body.replace(/\[HANDTEKENING\]/gi, sig || '');
                            else if (sig && !isSigIn) body += `<br><br>${sig}`;
                            const rawTo = voorgesteldClient.clientEmailTo.split(',').map((s) => s.trim()).filter(Boolean);
                            const primary = rawTo[0];
                            const ccFromPrimaryField = rawTo.slice(1);
                            const ccFromPayload = (voorgesteldClient.clientEmailCc || '')
                                .split(',')
                                .map((s) => s.trim())
                                .filter(Boolean);
                            const ccArr = [
                                ...ccFromPrimaryField,
                                ...ccFromPayload,
                                ...(cc ? cc.split(',').map((s) => s.trim()).filter(Boolean) : []),
                            ];
                            const bccFromPayload = (voorgesteldClient.clientEmailBcc || '')
                                .split(',')
                                .map((s) => s.trim())
                                .filter(Boolean);
                            const bccArr = [...bccFromPayload, ...(bcc ? bcc.split(',').map((s) => s.trim()).filter(Boolean) : [])];
                            if (primary) {
                                await sendDirectMessage(
                                    voorgesteldClient.clientEmailSubject,
                                    body,
                                    primary,
                                    ccArr,
                                    bccArr,
                                    fromEmail || undefined
                                );
                            }
                        } catch (e) {
                            console.error('[TalentPool] client email', e);
                        }
                    }
                }
                if (channel === 'email' && messageContent) {
                    const clientEmailSignature = (user as any)?.profile?.email_signature || '';
                    try {
                        let personalizedBody = messageContent.replace(/\n/g, '<br>');
                        const isSignatureInBody =
                            clientEmailSignature &&
                            personalizedBody.replace(/\s/g, '').includes(clientEmailSignature.replace(/\s/g, '').substring(0, 20));

                        if (personalizedBody.match(/\[HANDTEKENING\]/i)) {
                            personalizedBody = personalizedBody.replace(/\[HANDTEKENING\]/gi, clientEmailSignature || '');
                        } else if (clientEmailSignature && !isSignatureInBody) {
                            personalizedBody += `<br><br>${clientEmailSignature}`;
                        }

                        const { createDraftMessage, sendDirectMessage } = await import('../services/microsoftGraphService');
                        const candEmail = (resolvedCandidateEmail || moveDetails.candidateEmail || '').trim();

                        if (!candEmail) {
                            alert('Geen e-mailadres gevonden voor deze kandidaat.');
                            return;
                        }

                        const ccArr = cc ? cc.split(',').map((s) => s.trim()).filter(Boolean) : [];
                        const bccArr = bcc ? bcc.split(',').map((s) => s.trim()).filter(Boolean) : [];

                        if (asDraft) {
                            const { success } = await createDraftMessage(
                                subject,
                                personalizedBody,
                                candEmail,
                                ccArr,
                                bccArr,
                                fromEmail || undefined
                            );
                            if (success) alert('Concept e-mail succesvol aangemaakt in Outlook!');
                            else alert('Het aanmaken van een concept in Outlook is mislukt.');
                        } else {
                            const { success, error } = await sendDirectMessage(
                                subject,
                                personalizedBody,
                                candEmail,
                                ccArr,
                                bccArr,
                                fromEmail || undefined
                            );
                            if (success) alert('E-mail succesvol verzonden via Outlook!');
                            else {
                                if (error?.message?.includes('does not have the right to send mail')) {
                                    alert(
                                        `Je hebt in Microsoft 365 geen "Verzenden Als" (Send As) rechten gekregen om te mailen vanuit de gedeelde mailbox: ${fromEmail}. Vraag je IT-beheerder om dit in te stellen of kies je eigen mailbox.`
                                    );
                                } else {
                                    alert('Het verzenden via Outlook is mislukt.');
                                }
                            }
                        }
                    } catch (e) {
                        console.error('M365 Send Error', e);
                        alert('Fout bij E-mail / M365 connectie');
                    }
                } else if (channel === 'whatsapp' && messageContent) {
                    if (asDraft) {
                        await supabase.from('todos').insert({
                            title: `[OUTBOX WhatsApp] Bericht aan ${moveDetails.candidateName}`,
                            description: messageContent,
                            status: 'Pending',
                            due_date: new Date().toISOString(),
                            user_id: (user as any)?.id,
                            assignee_id: (user as any)?.id,
                        });
                        alert('WhatsApp bericht is opgeslagen in je Postvak UIT.');
                    } else {
                        const textUrl = encodeURIComponent(messageContent);
                        window.open(`https://web.whatsapp.com/send?text=${textUrl}`, '_blank');
                    }
                }

                if (moveDetails.clientId && user) {
                    const name = (user as any)?.profile?.name || 'Recruiter';
                    await supabase_log_interaction(
                        moveDetails.candidateRealId,
                        moveDetails.clientId,
                        'system',
                        `(Status Update) ${name} heeft kandidaat verplaatst naar '${targetStage}'.${rejectionReason ? ` Reden: ${rejectionReason}` : ''}`
                    );
                    if (finalInternalNote && finalInternalNote.trim()) {
                        await supabase_log_interaction(
                            moveDetails.candidateRealId,
                            moveDetails.clientId,
                            'note',
                            `[Verplaatst naar ${targetStage}] ${finalInternalNote}`
                        );
                    }

                    if (rejectionReason) {
                        const { data: profile } = await supabase
                            .from('candidates')
                            .select('ai_parsed_data')
                            .eq('id', moveDetails.candidateRealId)
                            .eq('client_id', moveDetails.clientId)
                            .single();
                        if (profile) {
                            const existingData = (profile as any).ai_parsed_data || {};
                            await supabase
                                .from('candidates')
                                .update({
                                    ai_parsed_data: { ...existingData, rejectionReason },
                                })
                                .eq('id', moveDetails.candidateRealId)
                                .eq('client_id', moveDetails.clientId);
                        }
                    }
                }
                const cid = selectedProfile?.candidate?.id;
                if (cid) await refreshCandidateShortlistData(cid);
                setShortlistMoveModalOpen(false);
                setShortlistMoveDetails(null);
            } catch (err) {
                console.error(err);
                alert('Er is iets misgegaan.');
            }
        },
        [isClientPortal, shortlistMoveDetails, user, selectedProfile?.candidate?.id, refreshCandidateShortlistData]
    );

    const executeShortlistDelete = async () => {
        if (shortlistDeleteInput.trim() !== 'VERWIJDEREN' || !shortlistDeleteTarget) return;
        const cid = selectedProfile?.candidate?.id;
        if (!cid) return;
        setShortlistDeleteBusy(true);
        try {
            if (shortlistDeleteTarget.kind === 'application') {
                const ok = await supabase_delete_application(shortlistDeleteTarget.id);
                if (!ok) throw new Error('delete application failed');
            } else {
                const del = await supabase_delete_placement(shortlistDeleteTarget.id);
                if (!del.success) throw new Error(del.error || 'delete placement failed');
            }
            await refreshCandidateShortlistData(cid);
            setShortlistDeleteTarget(null);
            setShortlistDeleteInput('');
            window.dispatchEvent(new CustomEvent('wt:toast', { detail: { message: 'Verwijderd.', type: 'success' } }));
        } catch (e) {
            console.error(e);
            window.dispatchEvent(
                new CustomEvent('wt:toast', {
                    detail: { message: 'Verwijderen is mislukt. Probeer opnieuw.', type: 'error' },
                })
            );
        } finally {
            setShortlistDeleteBusy(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        const cid = selectedProfile?.candidate?.id;
        if (!cid) {
            setCandidateInterviews([]);
            setCandidatePlacements([]);
            setCandidateStageHistory([]);
            return;
        }
        (async () => {
            const [ints, pls, hist] = await Promise.all([
                supabase_get_candidate_interviews(cid),
                supabase_get_candidate_placements_light(cid),
                supabase_get_candidate_application_stage_history(cid),
            ]);
            if (!cancelled) {
                setCandidateInterviews(ints || []);
                setCandidatePlacements(pls || []);
                setCandidateStageHistory(hist || []);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedProfile?.candidate?.id]);

    const fetchCommunications = async (profileId: string) => {
        try {
            const comms = await supabase_get_candidate_communications(profileId);
            setCommunications(comms || []);
        } catch (e) { console.error(e); }
    };

    const handleSendComm = async () => {
        if (!newCommText.trim() || !selectedProfile || !user) return;
        setIsSendingComm(true);
        try {
            const saved = await supabase_save_candidate_communication(selectedProfile.id, user.id, newCommText, commType);
            if (saved) {
                setCommunications(prev => [...prev, saved]);
                setNewCommText('');
            }
        } catch (e) { console.error(e); } finally { setIsSendingComm(false); }
    };

    const fetchPool = async () => {
        if (!user?.clientId) return;
        setIsPoolLoading(true);
        try {
            // Since we are IN the pool view, maybe this is to add from OTHER clients (if agency) 
            // or just a placeholder from the copied code.
            // We'll clear it for now to avoid confusion or fetch empty.
            setPoolCandidates([]);
        } catch (e) { console.error(e); } finally { setIsPoolLoading(false); }
    };

    const handleAddFromPool = async () => {
        // Placeholder for pool adding logic
        setShowAddModal(false);
        setSelectedPoolIds([]);
    };

    // Re-implementing helper functions to ensure full file content integrity
    const updateArrayItem = (path: 'timeline' | 'education' | 'courses' | 'references' | 'languages', index: number, field: string | null, value: string) => {
        const newParsed = { ...editForm.parsed_cv };
        if (path === 'timeline') newParsed.timeline[index] = { ...newParsed.timeline[index], [field!]: value };
        else if (path === 'education') newParsed.education[index] = { ...newParsed.education[index], [field!]: value };
        else if (path === 'courses') newParsed.courses[index] = { ...newParsed.courses[index], [field!]: value };
        else if (path === 'references') newParsed.references[index] = { ...newParsed.references[index], [field!]: value };
        else if (path === 'languages') newParsed.languages[index] = { ...newParsed.languages[index], [field!]: value };
        setEditForm({ ...editForm, parsed_cv: newParsed });
    };
    const addItemToArray = (path: 'timeline' | 'education' | 'courses' | 'references' | 'languages') => {
        const newParsed = { ...editForm.parsed_cv };
        if (path === 'timeline') newParsed.timeline = [...(newParsed.timeline || []), { role: 'Rol', company: 'Bedrijf', year: 'Jaar', description: '...' }];
        else if (path === 'education') newParsed.education = [...(newParsed.education || []), { degree: 'Opleiding', school: 'School', year: 'Jaar' }];
        else if (path === 'courses') newParsed.courses = [...(newParsed.courses || []), { name: 'Cursus', institute: 'Instituut', year: 'Jaar' }];
        else if (path === 'references') newParsed.references = [...(newParsed.references || []), { name: 'Naam', role: 'Functie', company: 'Bedrijf', email: '', phone: '' }];
        else if (path === 'languages') newParsed.languages = [...(newParsed.languages || []), { language: 'Taal', level: 'Niveau' }];
        setEditForm({ ...editForm, parsed_cv: newParsed });
    };
    const removeItemFromArray = (path: 'timeline' | 'education' | 'courses' | 'references' | 'languages', index: number) => {
        const newParsed = { ...editForm.parsed_cv };
        if (path === 'timeline') newParsed.timeline = newParsed.timeline.filter((_: any, i: number) => i !== index);
        else if (path === 'education') newParsed.education = newParsed.education.filter((_: any, i: number) => i !== index);
        else if (path === 'courses') newParsed.courses = newParsed.courses.filter((_: any, i: number) => i !== index);
        else if (path === 'references') newParsed.references = newParsed.references.filter((_: any, i: number) => i !== index);
        else if (path === 'languages') newParsed.languages = newParsed.languages.filter((_: any, i: number) => i !== index);
        setEditForm({ ...editForm, parsed_cv: newParsed });
    };
    const updateTagList = (path: 'tags' | 'hobbies', index: number, value: string) => {
        const newParsed = { ...editForm.parsed_cv };
        newParsed[path][index] = value;
        setEditForm({ ...editForm, parsed_cv: newParsed });
    };
    const addTag = (path: 'tags' | 'hobbies') => {
        const newParsed = { ...editForm.parsed_cv };
        newParsed[path] = [...(newParsed[path] || []), 'Nieuw Item'];
        setEditForm({ ...editForm, parsed_cv: newParsed });
    };
    const removeTag = (path: 'tags' | 'hobbies', index: number) => {
        const newParsed = { ...editForm.parsed_cv };
        newParsed[path] = newParsed[path].filter((_: any, i: number) => i !== index);
        setEditForm({ ...editForm, parsed_cv: newParsed });
    };

    const renderEditableList = (title: string, items: any[], path: string, icon: any) => {
        const list = coerceCvList(items);
        let fields = { title: 'role', subtitle: 'company', tertiary: 'period', desc: 'description' };
        let ph = { title: 'Functie', subtitle: 'Bedrijf', tertiary: 'Periode' };

        if (path === 'education') {
            fields = { title: 'degree', subtitle: 'school', tertiary: 'year', desc: 'description' };
            ph = { title: 'Opleiding', subtitle: 'School', tertiary: 'Jaar' };
        } else if (path === 'courses') {
            fields = { title: 'name', subtitle: 'institute', tertiary: 'year', desc: '' };
            ph = { title: 'Cursus', subtitle: 'Instituut', tertiary: 'Jaar' };
        } else if (path === 'references') {
            fields = { title: 'name', subtitle: 'company', tertiary: 'role', desc: 'email' };
            ph = { title: 'Naam', subtitle: 'Bedrijf', tertiary: 'Functie' };
        } else if (path === 'languages') {
            fields = { title: 'language', subtitle: 'level', tertiary: '', desc: '' };
            ph = { title: 'Taal', subtitle: 'Niveau', tertiary: '' };
        }

        return (
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:border-red- transition-all group">
                <div className="flex justify-between items-center mb-4">
                    <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">{icon} {title}</h4>
                    {isEditing && (
                        <button onClick={() => addItemToArray(path as any)} className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-red-0 transition-colors">
                            <Plus size={14} />
                        </button>
                    )}
                </div>
                <div className="space-y-3">
                    {list.length > 0 ? (
                        list.map((item: any, idx: number) => (
                            <div key={idx} className="relative pl-4 border-l-2 border-slate-100 py-1 hover:border-slate-300 transition-all">
                                {isEditing ? (
                                    <div className="space-y-2">
                                        <div className="relative">
                                            <input
                                                value={item[fields.title] || item.title || ''}
                                                onChange={(e) => updateArrayItem(path as any, idx, item.title !== undefined && fields.title === 'role' ? 'title' : fields.title, e.target.value)}
                                                className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-slate-300 focus:border-red-0 outline-none py-1 bg-transparent"
                                                placeholder={ph.title}
                                            />
                                            <button onClick={() => removeItemFromArray(path as any, idx)} className="absolute right-0 top-1 text-red-300 hover:text-red-500"><X size={12} /></button>
                                        </div>
                                        <input
                                            value={item[fields.subtitle] || ''}
                                            onChange={(e) => updateArrayItem(path as any, idx, fields.subtitle, e.target.value)}
                                            className="w-full text-[10px] font-medium text-slate-500 border-b border-dashed border-slate-200 focus:border-red-0 outline-none py-0.5 bg-transparent"
                                            placeholder={ph.subtitle}
                                        />
                                        {fields.tertiary && (
                                            <input
                                                value={item[fields.tertiary] || ''}
                                                onChange={(e) => updateArrayItem(path as any, idx, fields.tertiary, e.target.value)}
                                                className="w-full text-[10px] text-slate-400 border-b border-dashed border-slate-200 focus:border-red-0 outline-none py-0.5 bg-transparent"
                                                placeholder={ph.tertiary}
                                            />
                                        )}
                                        {fields.desc && path === 'references' && (
                                            <input
                                                value={item.email || ''}
                                                onChange={(e) => updateArrayItem(path as any, idx, 'email', e.target.value)}
                                                className="w-full text-[10px] text-slate-400 border-b border-dashed border-slate-200 focus:border-red-0 outline-none py-0.5 bg-transparent"
                                                placeholder="Email/Telefoon"
                                            />
                                        )}
                                        {fields.desc && path !== 'references' && (
                                            <textarea
                                                value={item[fields.desc] || ''}
                                                onChange={(e) => updateArrayItem(path as any, idx, fields.desc, e.target.value)}
                                                className="w-full text-xs text-slate-600 bg-slate-50 p-2 rounded border border-dashed border-slate-200 focus:border-red-0 outline-none min-h-[120px] resize-y mt-2"
                                                placeholder="Beschrijving..."
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex justify-between items-start">
                                            <h5 className="text-xs font-bold text-slate-800">{item[fields.title] || item.title || ''}</h5>
                                            {fields.tertiary && <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">{item[fields.tertiary]}</span>}
                                        </div>
                                        <p className="text-[10px] text-slate-500 font-medium">{item[fields.subtitle]}</p>
                                        {fields.desc && item[fields.desc] && <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">{item[fields.desc]}</p>}
                                        {path === 'references' && (
                                            <p className="text-[10px] text-slate-400 mt-1">{item.email} {item.phone ? ` • ${item.phone}` : ''}</p>
                                        )}
                                    </>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-4 text-slate-400 text-[10px] italic">Geen informatie beschikbaar</div>
                    )}
                </div>
            </div>
        );
    };

    const renderTagSection = (title: string, tags: string[], path: 'tags' | 'hobbies', icon: any) => {
        const tagList = coerceCvTags(tags);
        return (
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:border-red- transition-all group">
            <div className="flex justify-between items-center mb-4">
                <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">{icon} {title}</h4>
                {isEditing && (
                    <button onClick={() => addTag(path)} className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-red-0 transition-colors">
                        <Plus size={14} />
                    </button>
                )}
            </div>
            <div className="flex flex-wrap gap-2">
                {tagList.length > 0 ? (
                    tagList.map((tag: string, idx: number) => (
                        isEditing ? (
                            <div key={idx} className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md border border-slate-200">
                                <input
                                    value={tag}
                                    onChange={(e) => updateTagList(path, idx, e.target.value)}
                                    className="bg-transparent text-[10px] font-bold text-slate-700 outline-none w-20"
                                />
                                <button onClick={() => removeTag(path, idx)} className="text-red-300 hover:text-red-500"><X size={10} /></button>
                            </div>
                        ) : (
                            <span key={idx} className="px-2.5 py-1 bg-slate-50 border border-slate-100 rounded-lg text-[10px] font-bold text-slate-600 hover:bg-slate-100 transition-colors cursor-default">
                                {tag}
                            </span>
                        )
                    ))
                ) : (
                    <span className="text-slate-400 text-[10px] italic">Geen tags</span>
                )}
            </div>
        </div>
    );
    };

    const handleInitiateCall = async () => {
        const c = selectedProfile?.candidate;
        if (!recruiterPhone || !c?.phone) return;
        setIsCalling(true);
        try {
            const formatE164 = (p: string) => {
                let cleaned = String(p).replace(/\(0\)/g, '').replace(/[^0-9+]/g, '');
                if (cleaned.startsWith('00')) cleaned = '+' + cleaned.substring(2);
                else if (cleaned.startsWith('06')) cleaned = '+31' + cleaned.substring(1);
                else if (cleaned.startsWith('0') && !cleaned.startsWith('00')) cleaned = '+31' + cleaned.substring(1);
                else if (!cleaned.startsWith('+')) cleaned = '+' + cleaned;
                cleaned = cleaned.replace(/^\+310/, '+31'); // Fix commonly mistyped +3106 -> +316
                return cleaned;
            };
            const rPhone = formatE164(recruiterPhone);
            const cPhone = formatE164(c.phone);

            const apiUrl = window.location.hostname === 'localhost' ? '/api/bird/call' : '/api/bird/call';
            const res = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    recruiterPhone: rPhone,
                    candidatePhone: cPhone,
                    profileId: selectedProfile?.id,
                    userId: user?.id
                })
            });

            if (res.ok) {
                // Log communication
                if (user?.clientId) {
                    const timelineMessage = callNotes.trim()
                        ? `📞 Uitgaand gesprek gestart via Bird Bridge naar ${c.phone}\n\nNotities:\n${callNotes.trim()}`
                        : `📞 Uitgaand gesprek gestart via Bird Bridge naar ${c.phone}`;

                    await supabase_save_candidate_communication(
                        selectedProfile.id,
                        user.id,
                        timelineMessage,
                        'call'
                    );
                    const comms = await supabase_get_candidate_communications(selectedProfile.id);
                    setCommunications(comms || []);
                }
                setShowCallModal(false);
                setCallNotes(''); // clear notes on success
            } else {
                const err = await res.json();
                alert(`Bird API Fout: ${err.error || 'Onbekende fout'}`);
            }
        } catch (error) {
            console.error('Call Error:', error);
            alert('Kan de call niet starten.');
        } finally {
            setIsCalling(false);
        }
    };

    const handleAiAnalysis = async (query?: string) => {
        const searchQuery = query || aiSearchInput;
        if (!searchQuery.trim()) return;
        setIsAiFiltering(true);
        setIsAiSearching(true);
        try {
            const { generateEmbedding } = await import('../services/geminiService');
            const { supabase_match_candidates_v2 } = await import('../services/performanceService');

            const queryEmbedding = await generateEmbedding(searchQuery);
            if (queryEmbedding && queryEmbedding.length > 0) {
                const clientIdOpt = user?.role === UserRole.CLIENT || user?.role === UserRole.CLIENT_RECRUITER ? user.clientId : undefined;
                
                const data = await supabase_match_candidates_v2(queryEmbedding, 0.1, 50, clientIdOpt);

                if (data && data.length > 0) {
                    const matches = data.map((hit: any) => {
                        const pct = vectorSimilarityToPercent(hit);
                        return {
                            id: hit.id,
                            reason: buildAiVectorMatchReason(searchQuery, hit, pct),
                            score: pct,
                            breakdown: { title: 100, seniority: 100, skills: 100, experience: 100 },
                        };
                    });

                    setAiAnalysisResult({ matches });
                    setActiveAiFilter(matches.map((m: any) => m.id));
                    setLastAiSearchQuery(searchQuery);
                    return;
                }
            }
            setAiAnalysisResult({ matches: [] });
        } catch (e) {
            console.error("AI Filtering error:", e);
            alert("Er is een fout opgetreden bij het zoeken.");
        } finally {
            setIsAiFiltering(false);
            setIsAiSearching(false);
        }
    };

    useEffect(() => {
        const handleDeepSync = async (e: any) => {
            const profile = e.detail;
            const bhAuth = {
                restUrl: (user as any)?.bullhorn_settings?.restUrl,
                BhRestToken: (user as any)?.bullhorn_settings?.BhRestToken
            };

            if (!profile || !bhAuth.restUrl || !user) {
                alert("Geen Froukje integratie actief of geen kandidaat gegevens.");
                return;
            }
            if (!profile.ai_parsed_data?.id) return;
            window.dispatchEvent(new CustomEvent('showGlobalLoader', { detail: 'Bullhorn data ophalen (Notities & Bestanden)...' }));
            try {
                // Fetch Notes
                const fetchedNotes = await fetchBullhornCandidateNotes(bhAuth, profile.ai_parsed_data.id);
                if (fetchedNotes && fetchedNotes.length > 0) {
                    await supabase_sync_bullhorn_notes(user.clientId as string, profile.id, user.id, fetchedNotes, profile.candidate.id);
                }

                // Fetch Files
                const fetchedFiles = await fetchBullhornCandidateFiles(bhAuth, profile.ai_parsed_data.id);
                if (fetchedFiles && fetchedFiles.length > 0) {
                    const downloadableFiles = fetchedFiles.filter((f: any) => f.name && !f.name.endsWith('.url') && f.contentSubType !== 'text/html');
                    for (const f of downloadableFiles) {
                        try {
                            const fileBlob = await downloadBullhornCandidateFile(bhAuth, profile.ai_parsed_data.id, f.id);
                            if (fileBlob) {
                                const fileExt = f.name.split('.').pop() || 'pdf';
                                const fileFile = new File([fileBlob], f.name, { type: fileBlob.type || 'application/pdf' });
                                const storagePath = `bullhorn_sync/${user.clientId}/${profile.candidate.id}/${f.id}_${f.name}`;
                                
                                const { error: uploadError } = await supabase.storage.from('documents').upload(storagePath, fileFile, { upsert: true });
                                if (!uploadError) {
                                    const { data: publicUrlData } = supabase.storage.from('documents').getPublicUrl(storagePath);
                                    await supabase_save_document({
                                        id: crypto.randomUUID(),
                                        client_id: user.clientId as string,
                                        title: `[BH] ${f.name}`,
                                        content: '',
                                        is_template: false,
                                        candidate_id: profile.candidate.id,
                                        file_url: publicUrlData.publicUrl,
                                        storage_path: storagePath
                                    });
                                }
                            }
                        } catch (err) {
                            console.error("Error downloading specific file", err);
                        }
                    }
                }
                alert("Notities en bestanden succesvol gesynchroniseerd vanuit Bullhorn!");
            } catch (err) {
                console.error("Deep sync failed", err);
                alert("Fout bij ophalen van Bullhorn data.");
            } finally {
                window.dispatchEvent(new CustomEvent('hideGlobalLoader'));
            }
        };

        window.document.addEventListener('deepSyncCandidate', handleDeepSync);
        return () => window.document.removeEventListener('deepSyncCandidate', handleDeepSync);
    }, [user]);

    const renderStatsView = () => {
        return (
            <div className="h-full">
                <TalentPoolStatistics candidates={validCandidates} precomputedStats={precomputedStats} />
            </div>
        );
    };

    // Tracking the latest fetch to prevent race conditions during rapid typing
    const latestFetchId = useRef(0);

    // Load the portal-scoped candidate-id allowlist once when in client portal mode.
    // Only candidates that have an application against a vacancy of the logged-in
    // organisatie (matched via opdrachtgever_id, client_id of eindklant_id op de vacature).
    useEffect(() => {
        if (!isClientPortal) {
            // In the internal app there is no allowlist — leave it as null and
            // fall through to the standard client_id-based filter.
            if (portalCandidateIds !== null) setPortalCandidateIds(null);
            return;
        }
        const opdrachtgeverId = (user as any)?.clientId || (user as any)?.client_id || null;
        if (!opdrachtgeverId) {
            setPortalCandidateIds([]);
            return;
        }
        let cancelled = false;
        (async () => {
            const ids = await supabase_get_portal_scoped_candidate_ids(opdrachtgeverId, portalPrivacyFromUser(user));
            if (!cancelled) setPortalCandidateIds(ids);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClientPortal, (user as any)?.clientId, (user as any)?.client_id, user?.portalFullPipelineVisibility]);

    // Fetch Server-Side Paginated Data
    const fetchTalentPoolPage = async () => {
        const fetchId = ++latestFetchId.current;
        // In the client portal we don't filter by candidates.client_id — instead we
        // hard-scope via portalCandidateIds (derived from applications/vacancies).
        const filterId = isClientPortal
            ? undefined
            : (user?.role === UserRole.CLIENT || user?.role === UserRole.CLIENT_RECRUITER ? user.clientId : undefined);

        // Intersect AI filter with portal scope (if both are active).
        let scopedCandidateIds: string[] | null = null;
        if (isClientPortal) {
            scopedCandidateIds = portalCandidateIds || [];
        }

        const statusVariants = filterStatus ? talentPoolStatusFilterVariants(filterStatus, clientStatuses) : null;
        const filterStr = JSON.stringify({
            activeAiFilter,
            filterStatus,
            statusVariants,
            filterDateRange,
            filterDateFrom,
            filterDateTo,
            scoped: scopedCandidateIds?.length ?? 'none',
        });
        const cacheKey = `page-${currentPage}-query-${nlQuery}-col-${sortConfig.column}-asc-${sortConfig.ascending}-filt-${filterId}-filters-${filterStr}`;
        
        if (globalPageCache[cacheKey]) {
            setLocalCandidates(globalPageCache[cacheKey].data);
            setTotalCount(globalPageCache[cacheKey].count);
            return;
        }

        setIsFetchingPage(true);
        console.log("DEBUG: fetchTalentPoolPage called for query:", nlQuery);
        try {
            const res = await supabase_get_paginated_talent_pool(
                filterId, 
                currentPage, 
                renderPageSize, 
                nlQuery, 
                sortConfig.column, 
                sortConfig.ascending,
                isClientPortal,
                {
                    aiFilterIds: activeAiFilter,
                    status: filterStatus,
                    statusVariants,
                    scopedCandidateIds,
                    dateRange: filterDateRange,
                    dateFrom: filterDateFrom,
                    dateTo: filterDateTo,
                }
            );
            console.log("DEBUG: res length from backend:", res ? res.data?.length : 'null');
            if (res && fetchId === latestFetchId.current) {
                console.log("DEBUG: Setting localCandidates");
                setLocalCandidates(res.data);
                setTotalCount(res.count);
                globalPageCache[cacheKey] = res;
            }
        } catch (e) {
            console.error("Pagination fetch failed:", e);
        } finally {
            if (fetchId === latestFetchId.current) {
                setIsFetchingPage(false);
            }
        }
    };

    // Trigger fetch on query, page, or sort change.
    // In client portal mode we wait for the candidate-id allowlist to be resolved
    // before kicking off the first fetch (avoids a flash of "all candidates").
    useEffect(() => {
        if (isClientPortal && portalCandidateIds === null) return;
        const timer = setTimeout(() => {
            fetchTalentPoolPage();
        }, 300); // 300ms debounce
        return () => clearTimeout(timer);
    }, [currentPage, sortConfig, nlQuery, user?.clientId, filterStatus, filterDateRange, filterDateFrom, filterDateTo, activeAiFilter, refreshTrigger, isClientPortal, portalCandidateIds, clientStatuses]);

    const allCandidates = useMemo(() => {
        return localCandidates; // Already sorted by DB
    }, [localCandidates]);

    const validCandidates = useMemo(() => {
        return allCandidates.filter(p => !deletedIds.includes(p.id));
    }, [allCandidates, deletedIds]);

    const filteredProfiles = useMemo(() => {
        let result = validCandidates;

        const isClientPortal = window.location.hostname.toLowerCase().includes('clientportal') || new URLSearchParams(window.location.search).get('clientportal') === 'true';
        if (isClientPortal) {
            const blockedStatuses = ['shortlist', 'op shortlist', 'new', 'nieuw', 'screening', 'concept'];
            result = result.filter((c) => {
                const s = (c.status || c.candidate?.status || '').toLowerCase().trim();
                if (!s) return true;
                if (/\bshortlist\b/i.test(s) || s.includes('shortlist')) return false;
                return !blockedStatuses.some((blocked) => s === blocked || s.includes(blocked));
            });
        }

        if (activeAiFilter) {
            result = result.filter(c => activeAiFilter.includes(c.id));
        }

        if (filterStatus) {
            result = result.filter((c) => profileMatchesTalentPoolStatusFilter(c, filterStatus, clientStatuses));
        }
        if (filterAvailabilityDate) {
            const filterDt = new Date(filterAvailabilityDate);
            result = result.filter(c => {
                const avail = c.ai_parsed_data?.available_from || c.candidate?.available_from || c.parsed_cv?.available_from;
                if (!avail) return false;
                const dt = new Date(avail);
                return dt <= filterDt;
            });
        }

        if (filterExperience) {
            result = result.filter((c) => {
                const exp = talentPoolProfileYearsExperience(c);
                if (!Number.isFinite(exp)) return false;
                if (filterExperience === 'junior') return exp <= 2;
                if (filterExperience === 'medior') return exp > 2 && exp <= 5;
                if (filterExperience === 'senior') return exp > 5;
                return true;
            });
        }

        if (filterHasCV) {

            result = result.filter(c => {
                const cvData = c.parsed_cv || c.ai_parsed_data;
                // Consider it has a CV if it has timeline, education, or raw parsed text
                return !!(cvData && ((cvData.timeline && cvData.timeline.length > 0) || (cvData.education && cvData.education.length > 0) || cvData.raw_cv_text));
            });
        }

        if (selectedTags.length > 0) {
            result = result.filter(c => {
                const cTags = (c.parsed_cv?.tags || []).map((t: any) => t.toLowerCase());
                return selectedTags.every(tag => cTags.includes(tag.toLowerCase()));
            });
        }
        if (pipelineFilterCandidateIds !== null) {
            result = result.filter(c => pipelineFilterCandidateIds.includes(c.candidate?.id || c.candidate_id));
        }

        // Deduplicate candidates by their ID to prevent UI glitches when candidates belong to multiple overlapping clients
        const uniqueCandidateIds = new Set();
        if (filterDateRange === 'custom' && filterDateFrom && filterDateTo) {
            let start = new Date(`${filterDateFrom}T00:00:00`);
            let end = new Date(`${filterDateTo}T23:59:59.999`);
            if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
                if (start > end) {
                    const t = start;
                    start = end;
                    end = t;
                }
                result = result.filter((c) => {
                    const date = talentPoolProfileAddedDate(c);
                    if (!date) return false;
                    return date >= start && date <= end;
                });
            }
        } else if (filterDateRange && filterDateRange !== 'all') {
            const now = new Date();
            result = result.filter(c => {
                const date = talentPoolProfileAddedDate(c);
                if (!date) return false;
                if (filterDateRange === '7days') return (now.getTime() - date.getTime()) <= 7 * 24 * 60 * 60 * 1000;
                if (filterDateRange === '30days') return (now.getTime() - date.getTime()) <= 30 * 24 * 60 * 60 * 1000;
                if (filterDateRange === 'thisYear') return date.getFullYear() === now.getFullYear();
                return true;
            });
        }

        result = result.filter(c => {
            if (!c.candidate?.id) return true;
            if (uniqueCandidateIds.has(c.candidate.id)) return false;
            uniqueCandidateIds.add(c.candidate.id);
            return true;
        });

        return result;
    }, [
        localCandidates,
        activeAiFilter,
        filterStatus,
        filterDateRange,
        filterDateFrom,
        filterDateTo,
        filterExperience,
        filterHasCV,
        selectedTags,
        deletedIds,
        pipelineFilterCandidateIds,
        filterAvailabilityDate,
        clientStatuses,
    ]);


    // Pagination derived state (Server-side)
    const renderPageSize = 50;
    const paginatedProfiles = Array.from(new Map(filteredProfiles.map(p => [p.id, p])).values()); // Already paginated from DB
    const totalPages = Math.ceil(totalCount / renderPageSize) || 1;

    useEffect(() => {
        setCurrentPage(1);
    }, [nlQuery, filterStatus, filterDateRange, filterDateFrom, filterDateTo, activeAiFilter]);

    const handleAiSearch = async () => {
        if (!aiSearchInput) return;
        const queryLabel = aiSearchInput.trim();
        setIsAiFiltering(true);
        setIsAiSearching(true);
        try {
            const { generateEmbedding } = await import('../services/geminiService');
            const { supabase_match_candidates_v2 } = await import('../services/performanceService');

            const queryEmbedding = await generateEmbedding(aiSearchInput);
            if (queryEmbedding && queryEmbedding.length > 0) {
                // In the client portal we don't filter by candidates.client_id;
                // instead we intersect AI results with the portal allowlist below.
                const clientIdOpt = isClientPortal
                    ? undefined
                    : (user?.role === UserRole.CLIENT || user?.role === UserRole.CLIENT_RECRUITER ? user.clientId : undefined);

                const data = await supabase_match_candidates_v2(queryEmbedding, 0.1, 50, clientIdOpt);

                let matchedProfiles = data || [];
                if (isClientPortal && portalCandidateIds && matchedProfiles.length > 0) {
                    const allow = new Set(portalCandidateIds);
                    matchedProfiles = matchedProfiles.filter((p: any) => allow.has(p.id));
                }
                if (isClientPortal && matchedProfiles.length > 0) {
                    const blocked = ['shortlist', 'op shortlist', 'new', 'nieuw', 'screening', 'concept'];
                    matchedProfiles = matchedProfiles.filter((p: any) => {
                        const s = String(p.status || p.candidate?.status || '').toLowerCase().trim();
                        if (/\bshortlist\b/i.test(s) || s.includes('shortlist')) return false;
                        return !blocked.some((b) => s === b || s.includes(b));
                    });
                }

                if (matchedProfiles.length > 0) {
                    setAiAnalysisResult({
                        bestMatches: matchedProfiles.slice(0, 50),
                        recommendations: [],
                        summary: `Top matches gevonden voor "${queryLabel}"`
                    });

                    setActiveAiFilter(matchedProfiles.map((p: any) => p.id));
                    setLocalCandidates(matchedProfiles);
                    setTotalCount(matchedProfiles.length);
                    setCurrentPage(1);
                    setLastAiSearchQuery(queryLabel);
                    setAiSearchInput('');
                } else {
                    setAiAnalysisResult({ bestMatches: [], recommendations: [], summary: "Geen geschikte kandidaten gevonden." });
                }
            } else {
                alert("Er ging iets mis bij het genereren van de zoekopdracht.");
            }
        } catch (error) {
            console.error("AI Filtering error:", error);
            alert("Er is een fout opgetreden bij het zoeken.");
        } finally {
            setIsAiFiltering(false);
            setIsAiSearching(false);
        }
    };


    const toggleTagFilter = (tag: string) => {
        setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setUploadedFile(file);
            setIsAnalyzing(true);
            setManualEntry(false);
            setShowAddModal(false); // Make it async in background
            window.dispatchEvent(new CustomEvent('showGlobalLoader', { detail: 'CV Analyseren...' }));

            const previewUrl = URL.createObjectURL(file);
            setFilePreviewUrl(previewUrl);

            try {
                const base64 = await new Promise<string>((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve((e.target?.result as string).split(',')[1]);
                    reader.readAsDataURL(file);
                });

                setFileBase64(base64); // Store for parsing
                const analysis = await analyzeUploadedCV({ base64, mimeType: file.type });
                const globalMatch = await supabase_search_global_candidate_match(analysis);
                if (globalMatch) analysis._globalMatch = globalMatch;
                setParsedCandidate(normalizeCvAnalysisResult(analysis));
                setAddStep(2);
                setShowCvMapper(true); // Open Mapper immediately
            } catch (err) {
                const msg = String((err as any)?.message || '');
                const userMsg = String((err as any)?.userMessage || '');
                const isScanPdf =
                    msg === 'PDF_NO_TEXT' ||
                    /geen selecteerbare tekst|PDF_NO_TEXT|OCR kon|OCR gaf|OCR is niet/i.test(msg + userMsg);
                const isTimeout = msg === 'PDF_PARSE_TIMEOUT' || /timeout|408/i.test(msg);
                if (isScanPdf) {
                    console.warn('[TalentPool] PDF scan / geen tekst — paste-flow.', userMsg || msg);
                    alert(
                        userMsg ||
                            'Deze PDF bevat geen selecteerbare tekst (waarschijnlijk een scan). Upload een .docx, of plak de CV-tekst (of LinkedIn-profiel) zodat we het wel kunnen mappen.',
                    );
                    setShowAddModal(true);
                    setShowPasteText(true);
                } else if (isTimeout) {
                    console.warn('[TalentPool] PDF extract timeout.', err);
                    alert(
                        'Het uitlezen van deze PDF duurde te lang. Probeer een kleiner bestand, upload .docx, of plak de CV-tekst handmatig.',
                    );
                    setShowAddModal(true);
                    setShowPasteText(true);
                } else {
                    console.error(err);
                    alert(userMsg || 'Kon CV niet analyseren.');
                }
            } finally {
                setIsAnalyzing(false);
                window.dispatchEvent(new CustomEvent('hideGlobalLoader'));
            }
        }
    };

    const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setUploadedPhoto(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const handleManualEntry = () => {
        setParsedCandidate({ firstName: '', lastName: '', email: '', currentRole: '', yearsExperience: '', phone: '', linkedin: '', tags: [], summary: '', timeline: [], education: [] });
        setManualTagsInput('');
        setManualEntry(true);
        setAddStep(2);
    };

    const handlePastedTextSubmit = async () => {
        if (!pastedCVText.trim()) return;
        setIsAnalyzing(true);
        setManualEntry(false);
        setShowAddModal(false);
        window.dispatchEvent(new CustomEvent('showGlobalLoader', { detail: 'Tekst CV Analyseren...' }));

        try {
            setFileBase64(undefined as any); // Reset explicit base64 for pure text payload if needed, or null
            const analysis = await analyzeUploadedCV(pastedCVText.trim());
            setParsedCandidate(normalizeCvAnalysisResult(analysis));
            setAddStep(2);
            setShowCvMapper(true);
        } catch (err) {
            console.error(err);
            alert("Kon tekst niet analyseren.");
            setShowAddModal(true);
        } finally {
            setIsAnalyzing(false);
            window.dispatchEvent(new CustomEvent('hideGlobalLoader'));
            setShowPasteText(false);
            setPastedCVText('');
        }
    };

    const handleSaveCandidate = async (forceSave: boolean = false) => {
        if (!parsedCandidate) return;
        let targetClientId = user?.clientId;
        if (!targetClientId) {
            const { data: profile } = await supabase.from('profiles').select('client_id').eq('id', user?.id).single();
            if (profile?.client_id) targetClientId = profile.client_id;
        }
        if (!targetClientId) { alert("Geen Client ID"); return; }
        if (!parsedCandidate.firstName && !parsedCandidate.lastName) { alert("Naam verplicht."); return; }
        if (!isCandidateSourceFilled(parsedCandidate.source)) {
            alert('Selecteer een kandidaat bron. Dit veld is verplicht bij handmatig aanmaken of parsen.');
            return;
        }

        if (!forceSave) {
            // First check local validCandidates for immediate UI context
            let duplicate = validCandidates.find(c => {
                const cEmail = c.candidate?.email?.toLowerCase();
                const pEmail = parsedCandidate.email?.toLowerCase();
                if (cEmail && pEmail && cEmail.includes(pEmail)) return true;
                
                if (parsedCandidate.phone && c.candidate?.phone) {
                    const p1 = parsedCandidate.phone.replace(/\D/g, '');
                    const p2 = c.candidate.phone.replace(/\D/g, '');
                    if (p1.length > 8 && p2.length > 8 && (p1.endsWith(p2) || p2.endsWith(p1))) return true;
                    if (p1 && p2 && p1 === p2) return true;
                }
                
                const pName = `${parsedCandidate.firstName || ''} ${parsedCandidate.lastName || ''}`.trim().toLowerCase();
                if (pName.length > 4 && c.candidate?.name?.toLowerCase() === pName) return true;
                
                return false;
            });

            // If no local duplicate, check backend globally by email
            if (!duplicate && parsedCandidate.email) {
                try {
                    const { data: globalMatch } = await supabase.from('candidates').select('id, name, email, phone').ilike('email', `%${parsedCandidate.email}%`).maybeSingle();
                    if (globalMatch) {
                        // Construct a virtual duplicate object for the warning modal
                        duplicate = {
                            id: 'global-virtual-profile',
                            candidate: globalMatch,
                            ai_parsed_data: {}
                        };
                    }
                } catch (e) {
                    console.warn("Global dup check failed", e);
                }
            }

            if (duplicate) {
                setDuplicateCandidateMatch(duplicate);
                setShowDuplicateWarning(true);
                return;
            }
        }

        setShowDuplicateWarning(false);
        setIsSavingNew(true);
        window.dispatchEvent(new CustomEvent('showGlobalLoader', { detail: 'Kandidaat opslaan...' }));
        try {
            let cvUrl = null;
            if (uploadedFile) cvUrl = await supabase_upload_file(uploadedFile, 'cv_assets', 'cvs/');

            let photoUrl = null;
            if (uploadedPhoto) photoUrl = await supabase_upload_image(uploadedPhoto);

            let finalTags = parsedCandidate.tags || [];
            if (manualEntry && manualTagsInput) finalTags = manualTagsInput.split(',').map(t => t.trim()).filter(Boolean);

            const candidateSource = String(parsedCandidate.source || '').trim();
            const candidateData = {
                name: `${parsedCandidate.firstName || ''} ${parsedCandidate.lastName || ''}`.trim() || 'Naamloos',
                email: parsedCandidate.email || null,
                phone: parsedCandidate.phone || null,
                role: parsedCandidate.currentRole || 'Kandidaat',
                experience: parseInt(parsedCandidate.yearsExperience) || 0,
                linkedin_url: parsedCandidate.linkedin || null,
                photo_url: photoUrl,
                source: candidateSource,
            };
            const profileData = {
                ai_parsed_data: { ...parsedCandidate, source: candidateSource, cv_url: cvUrl, tags: finalTags },
                status: `Lead ${(user as any)?.name || (user as any)?.fullName || user?.email?.split('@')[0] || ''}`.trim(),
                tags: finalTags,
            };

            const savedCandidateId = await supabase_save_candidate(candidateData, profileData, targetClientId);

            setShowAddModal(false);
            setShowCvMapper(false);
            setParsedCandidate(null);
            setUploadedFile(null);
            setUploadedPhoto(null);
            setPhotoPreview(null);
            setAddStep(1);
            setFileBase64(null);
            if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
            setFilePreviewUrl(null);
            
            // Auto-open new candidate profile
            if (savedCandidateId) {
                // Wipe cache and force paginated list reload
                Object.keys(globalPageCache).forEach(k => delete globalPageCache[k]);
                setRefreshTrigger(prev => prev + 1);
                
                const refreshedCandidates = await supabase_get_talent_pool(targetClientId);
                if (refreshedCandidates.length > 0) {
                    // fullDatabaseCandidates removed
                    const newlySavedProfile = refreshedCandidates.find(c => c.candidate_id === savedCandidateId);
                    if (newlySavedProfile) {
                        setSelectedProfile(newlySavedProfile);
                    }
                }
            }
            
        } catch (err: any) {
            alert(`Opslaan mislukt: ${err.message}`);
        } finally {
            setIsSavingNew(false);
            window.dispatchEvent(new CustomEvent('hideGlobalLoader'));
        }
    };

    const handleOverwriteCandidate = async (dupArg?: any) => {
        const targetDup = (dupArg && dupArg.id) ? dupArg : duplicateCandidateMatch;
        if (!targetDup || !parsedCandidate) return;
        
        let targetClientId = user?.clientId || targetDup?.client_id;
        if (!targetClientId) {
            if (!user) {
                alert("Geen gebruiker ingelogd"); return;
            }
            const { data: profile } = await supabase.from('profiles').select('client_id').eq('id', user.id).single();
            if (profile?.client_id) targetClientId = profile.client_id;
        }
        if (!targetClientId) { alert("Geen Client ID"); return; }
        if (!isCandidateSourceFilled(parsedCandidate.source)) {
            alert('Selecteer een kandidaat bron. Dit veld is verplicht bij handmatig aanmaken of parsen.');
            return;
        }

        setShowDuplicateWarning(false);
        setIsSavingNew(true);
        setShowCvMapper(false);
        window.dispatchEvent(new CustomEvent('showGlobalLoader', { detail: 'Kandidaat overschrijven...' }));
        
        try {
            const candidateSource = String(parsedCandidate.source || '').trim();
            let cvUrl = targetDup?.ai_parsed_data?.cv_url || null;
            if (uploadedFile) cvUrl = await supabase_upload_file(uploadedFile, 'cv_assets', 'cvs/');

            let photoUrl = targetDup?.candidate?.photo_url || null;
            if (uploadedPhoto) photoUrl = await supabase_upload_image(uploadedPhoto);

            let finalTags = parsedCandidate.tags || [];
            if (manualEntry && manualTagsInput) finalTags = manualTagsInput.split(',').map(t => t.trim()).filter(Boolean);

            const newAiParsedData = {
                ...targetDup?.ai_parsed_data,
                ...parsedCandidate,
                source: candidateSource,
                cv_url: cvUrl,
                tags: finalTags,
            };

            const candidateUpdates = {
                name: `${parsedCandidate.firstName || ''} ${parsedCandidate.lastName || ''}`.trim() || targetDup?.candidate?.name,
                email: parsedCandidate.email || targetDup?.candidate?.email,
                phone: parsedCandidate.phone || targetDup?.candidate?.phone,
                role: parsedCandidate.currentRole || targetDup?.candidate?.role,
                experience: parseInt(parsedCandidate.yearsExperience) || targetDup?.candidate?.experience || 0,
                linkedin_url: parsedCandidate.linkedin || targetDup?.candidate?.linkedin_url,
                photo_url: photoUrl,
                source: candidateSource,
                ai_parsed_data: newAiParsedData,
            };
            
            const profileUpdates = { 
                ai_parsed_data: newAiParsedData, 
                tags: finalTags 
            };

            let finalProfileId = targetDup.id;
            const targetCandidateId = targetDup.candidate?.id || targetDup.id;

            if (targetDup.id === 'global-virtual-profile') {
                // Update candidate fields globally
                await supabase.from('candidates').update(candidateUpdates).eq('id', targetCandidateId);
                
                // Create or UPDATE profile mapping to this client
                const { data: newProfile, error } = await supabase.from('candidates').upsert({
                    candidate_id: targetDup.candidate.id,
                    client_id: targetClientId,
                    status: 'Lead',
                    ...profileUpdates
                }, { onConflict: 'id' }).select('id').single();
                
                if (error) throw error;
                finalProfileId = newProfile.id;
            } else {
                await supabase_update_candidate_full(targetDup.real_profile_id || targetDup.id, targetCandidateId, candidateUpdates, profileUpdates);
            }

            setShowAddModal(false);
            setShowCvMapper(false);
            setParsedCandidate(null);
            setUploadedFile(null);
            setUploadedPhoto(null);
            setPhotoPreview(null);
            setAddStep(1);
            setFileBase64(null);
            if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
            setFilePreviewUrl(null);
            setDuplicateCandidateMatch(null);
            
            // Auto-open updated candidate profile
            // Done saving duplicate
            Object.keys(globalPageCache).forEach(k => delete globalPageCache[k]);
            setRefreshTrigger(prev => prev + 1);
            
            const refreshedCandidates = await supabase_get_talent_pool(targetClientId);
            if (refreshedCandidates.length > 0) {
                // Update list view from latest server state
                setLocalCandidates(refreshedCandidates);
                setTotalCount(refreshedCandidates.length);
                const updatedProfile = refreshedCandidates.find(c => c.id === finalProfileId);
                if (updatedProfile) {
                    setSelectedProfile(updatedProfile);
                }
            }
            
        } catch (err: any) {
            console.error("Overwrite error:", err);
            if (err?.code === '23505' || err?.message?.includes('duplicate key') || err?.status === 409 || err?.message?.includes('409')) {
                alert("Overschrijven mislukt:\n\nHet e-mailadres in dit nieuwe document is al in gebruik bij een andere kandidaat in de database. Je probeert dít profiel het e-mailadres van iemand anders te geven.");
            } else {
                alert(`Overschrijven mislukt: ${err.message || 'Onbekende fout'}`);
            }
        } finally {
            setIsSavingNew(false);
            window.dispatchEvent(new CustomEvent('hideGlobalLoader'));
        }
    };

    const handleUpdateProfile = async () => {
        if (!selectedProfile || !editForm) return;
        setIsSavingEdit(true);
        try {
            let newCvUrl = selectedProfile.candidate.cv_url;
            if (replacementCvFile) {
                newCvUrl = await supabase_upload_file(replacementCvFile, 'cv_assets', 'cvs/');
            }
            
            const updatedAiParsedData = { ...(selectedProfile.ai_parsed_data || {}), ...editForm.parsed_cv, ...(editForm.ai_parsed_data || {}) };
            
            const candidateUpdates = { 
                name: editForm?.candidate?.name || null, 
                email: editForm?.candidate?.email || null, 
                phone: editForm?.candidate?.phone || null, 
                role: editForm?.candidate?.role || null, 
                linkedin_url: editForm?.candidate?.linkedin_url || null, 
                source: editForm?.candidate?.source || null, 
                cv_url: newCvUrl,
                ai_parsed_data: updatedAiParsedData
            };
            const profileUpdates = {
                ai_parsed_data: updatedAiParsedData
            };
            await supabase_update_candidate_full(selectedProfile.id, selectedProfile.candidate.id, candidateUpdates, profileUpdates);

            const updatedProfile = { ...selectedProfile, candidate: { ...selectedProfile.candidate, ...candidateUpdates }, parsed_cv: { ...(selectedProfile.parsed_cv || {}), ...editForm.parsed_cv }, ai_parsed_data: updatedAiParsedData };
            setSelectedProfile(updatedProfile);
            
            // Wipe cache and force paginated list reload so it reflects edited data
            Object.keys(globalPageCache).forEach(k => delete globalPageCache[k]);
            setRefreshTrigger(prev => prev + 1);
            
            setIsEditing(false);
            setReplacementCvFile(null);
        } catch (err: any) { 
            console.error("Update error mapping:", err);
            if (err?.code === '23505' || err?.message?.includes('duplicate key') || err?.status === 409 || err?.message?.includes('409')) {
                alert("Opslaan mislukt:\n\nHet ingevulde e-mailadres of telefoonnummer is al in gebruik bij een andere kandidaat in de database. Dubbele profielen zijn niet toegestaan.");
            } else {
                alert(`Opslaan mislukt: ${err.message || 'Onbekende fout'}`); 
            }
        } finally { 
            setIsSavingEdit(false); 
        }
    };
    
    const executeRemapWithDoc = async (doc: any) => {
        setShowRemapDocSelector(false);
        window.dispatchEvent(new CustomEvent('showGlobalLoader', { detail: 'Document Inlezen & Analyseren...' }));
        try {
            let base64 = "";
            let mimeType = "application/pdf";
            let textContent = "";
            let previewUrl = null;

            if (doc.type === 'text') {
                textContent = doc.content;
            } else if (doc.type === 'url') {
                previewUrl = doc.url;
                try {
                    const res = await fetch(doc.url);
                    const blob = await res.blob();
                    mimeType = blob.type;
                    base64 = await new Promise<string>((resolve) => {
                        const reader = new FileReader();
                        reader.onload = (e) => resolve((e.target?.result as string).split(',')[1]);
                        reader.readAsDataURL(blob);
                    });
                } catch(e) {
                    console.warn("Could not fetch remote URL for base64 compilation, using raw API fallback if available.", e);
                    const { data: dbCand } = await supabase.from('candidates').select('original_cv_text').eq('id', selectedProfile.candidate.id).single();
                    if (dbCand && dbCand.original_cv_text) textContent = dbCand.original_cv_text;
                }
            } else if (doc.type === 'storage') {
                // Use 1-hour signed URL so the preview doesn't expire mid-session (old 60s URL caused 400s on re-render)
                let signedUrlAttempts = 0;
                while (signedUrlAttempts < 3) {
                    const { data, error } = await supabase.storage.from('documents').createSignedUrl(doc.storage_path, 3600);
                    if (!error && data?.signedUrl) {
                        previewUrl = data.signedUrl;
                        const res = await fetch(data.signedUrl);
                        const blob = await res.blob();
                        mimeType = blob.type;
                        base64 = await new Promise<string>((resolve) => {
                            const reader = new FileReader();
                            reader.onload = (e) => resolve((e.target?.result as string).split(',')[1]);
                            reader.readAsDataURL(blob);
                        });
                        break;
                    }
                    signedUrlAttempts++;
                    if (signedUrlAttempts < 3) await new Promise(r => setTimeout(r, 300 * signedUrlAttempts));
                }
            }

            let analysis;
            if (base64) {
                analysis = await analyzeUploadedCV({ base64, mimeType });
                setFileBase64(base64);
            } else if (textContent) {
                analysis = await analyzeUploadedCV(textContent);
                setPastedCVText(textContent);
            } else {
                throw new Error("Geen valide bestandsinhoud of tekst gevonden.");
            }

            if (analysis) {
                setParsedCandidate({ ...analysis, _isEditMode: true });
                setDuplicateCandidateMatch(selectedProfile);
                setFilePreviewUrl(previewUrl);
                setUploadedFile(null);
                setShowAddModal('manual');
                setShowCvMapper(true);
            }
        } catch (err) {
            console.error("Fout bij remappen van geslecteerd document:", err);
            alert("Fout bij remappen. Lokaal ophalen van het bestand is mislukt.");
        } finally {
            window.dispatchEvent(new CustomEvent('hideGlobalLoader'));
        }
    };

    const handleRemapOriginalCv = async () => {
        if (!selectedProfile?.candidate?.id) return;
        
        window.dispatchEvent(new CustomEvent('showGlobalLoader', { detail: 'Beschikbare documenten zoeken...' }));
        try {
            const docs: any[] = [];
            
            // 1. Raw Text fallback
            const { data: dbCandidate, error } = await supabase.from('candidates').select('original_cv_text').eq('id', selectedProfile.candidate.id).single();
            if (dbCandidate && dbCandidate.original_cv_text) {
                docs.push({ id: 'raw_text', title: 'Originele ingelezen tekst (Fallback)', type: 'text', content: dbCandidate.original_cv_text, date: new Date().toISOString() });
            }
            
            // 2. Original Uploaded URL
            const origUrl = selectedProfile.parsed_cv?.cv_url || selectedProfile.ai_parsed_data?.cv_url || selectedProfile.candidate.cv_url;
            if (origUrl) {
                docs.push({ id: 'orig_url', title: 'Origineel CV', type: 'url', url: origUrl, date: selectedProfile.candidate.created_at });
            }
            
            // 3. Attached candidateDocs
            const attachedDocs = candidateDocs.filter(d => d.candidate_id === selectedProfile.candidate.id);
            attachedDocs.forEach(d => {
                if (d.storage_path) {
                    docs.push({ id: d.id, title: d.title || d.file_name || 'Document', type: 'storage', storage_path: d.storage_path, date: d.created_at || d.updated_at });
                }
            });

            if (docs.length === 0) {
                alert("Er zijn geen documenten of originele teksten van deze kandidaat gevonden om te remappen.");
                return;
            }

            if (docs.length === 1) {
                executeRemapWithDoc(docs[0]);
            } else {
                setRemapDocsList(docs);
                setShowRemapDocSelector(true);
            }
        } catch (error) {
            console.error("Fout bij ophalen documenten:", error);
            alert("Er trad een fout op bij het voorbereiden van de remap actie.");
        } finally {
            window.dispatchEvent(new CustomEvent('hideGlobalLoader'));
        }
    };
    
    const handleUploadReplacementCv = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !editForm) return;
        window.dispatchEvent(new CustomEvent('showGlobalLoader', { detail: 'Nieuw CV Analyseren...' }));
        try {
            const base64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve((e.target?.result as string).split(',')[1]);
                reader.readAsDataURL(file);
            });
            const analysis = await analyzeUploadedCV({ base64, mimeType: file.type });
            if (analysis) {
                setParsedCandidate({ ...normalizeCvAnalysisResult(analysis), _isEditMode: true });
                setDuplicateCandidateMatch(selectedProfile);
                const previewUrl = URL.createObjectURL(file);
                setFilePreviewUrl(previewUrl);
                setUploadedFile(file);
                setFileBase64(base64);
                
                // Also update local original text directly for future maps
                if(selectedProfile) selectedProfile.candidate.original_cv_text = analysis.resume_text || '';
                
                setShowAddModal('manual');
                setShowCvMapper(true);
            }
        } catch (error) {
            console.error("Fout bij uploaden nieuw CV:", error);
            alert("Er trad een fout op bij het uploaden van het nieuwe CV.");
        } finally {
            window.dispatchEvent(new CustomEvent('hideGlobalLoader'));
        }
    };

    const handleInterviewInvite = async (type: 'whatsapp' | 'email' | 'call') => {
        if (!selectedProfile || !user) return;

        let messageBody = `Uitgenodigd voor gesprek via ${type}.`;
        let commTypeToSave: 'note' | 'email' | 'phone' | 'whatsapp' = 'note';

        if (type === 'whatsapp') {
            const phone = selectedProfile.candidate.phone;
            if (phone) window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}`, '_blank');
            else alert("Geen telefoonnummer bij deze kandidaat.");
            messageBody = "Kandidaat uitgenodigd via WhatsApp.";
            commTypeToSave = 'whatsapp';
        }

        if (type === 'email') {
            const email = selectedProfile.candidate.email;
            if (email) window.open(`mailto:${email}?subject=Uitnodiging%20Kennismakingsgesprek`, '_blank');
            else alert("Geen e-mailadres bij deze kandidaat.");
            messageBody = "Kandidaat uitgenodigd via E-mail.";
            commTypeToSave = 'email';
        }

        if (type === 'call') {
            messageBody = "Kandidaat gebeld om uit te nodigen voor gesprek.";
            commTypeToSave = 'phone';
        }

        try {
            const saved = await supabase_save_candidate_communication(
                selectedProfile.id,
                user.id,
                messageBody,
                commTypeToSave
            );
            if (saved) {
                setCommunications(prev => [...prev, saved]);
            }
            setShowInterviewModal(false);
            setActiveTab('comm');
        } catch (e) {
            console.error(e);
        }
    };

    const handleSendWhatsAppWeb = async () => {
        if (!selectedProfile?.candidate?.phone || !whatsAppText.trim()) return;
        
        let phone = selectedProfile.candidate.phone.replace(/[^0-9+]/g, '');
        if (phone.startsWith('00')) phone = phone.substring(2);
        else if (phone.startsWith('06')) phone = '31' + phone.substring(1);
        else if (phone.startsWith('0')) phone = '31' + phone.substring(1);
        phone = phone.startsWith('+') ? phone.substring(1) : phone;

        const url = `https://wa.me/${phone}?text=${encodeURIComponent(whatsAppText)}`;
        window.open(url, '_blank');

        setPendingWhatsAppText(whatsAppText);
        setShowWhatsAppEditor(false);
        setShowWhatsAppConfirmModal(true);
    };

    const handleConfirmWhatsAppSent = async (didSend: boolean) => {
        setShowWhatsAppConfirmModal(false);
        if (didSend && user?.id && selectedProfile) {
            try {
                const saved = await supabase_save_candidate_communication(
                    selectedProfile.id,
                    user.id,
                    pendingWhatsAppText,
                    'whatsapp'
                );
                if (saved) setCommunications(prev => [...prev, saved]);
                setActiveTab('comm');
            } catch (e) { console.error("Could not save WA comm:", e); }
        }
    };

    const handleSendEmailDesktop = () => {
        if (!selectedProfile?.candidate?.email) return;
        const mailtoUrl = `mailto:${encodeURIComponent(selectedProfile.candidate.email)}?subject=${encodeURIComponent(generatedEmailSubject)}&body=${encodeURIComponent(generatedEmailBody)}`;
        window.open(mailtoUrl, '_blank');
        setShowEmailChoiceModal(false);
        setShowAiEmailModal(false);
        setShowEmailConfirmModal(true);
    };

    const handleSendEmailM365 = () => {
        if (!selectedProfile?.candidate?.email) return;
        const url = `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(selectedProfile.candidate.email)}&subject=${encodeURIComponent(generatedEmailSubject)}&body=${encodeURIComponent(generatedEmailBody)}`;
        window.open(url, '_blank');
        setShowEmailChoiceModal(false);
        setShowAiEmailModal(false);
        setShowEmailConfirmModal(true);
    };

    const handleConfirmEmailSent = async (didSend: boolean) => {
        setShowEmailConfirmModal(false);
        if (didSend && user?.id && selectedProfile) {
            try {
                const saved = await supabase_save_candidate_communication(
                    selectedProfile.id,
                    user.id,
                    `Mail opgesteld via AI Email Generator: ${generatedEmailSubject}`,
                    'email'
                );
                if (saved) setCommunications(prev => [...prev, saved]);
                setActiveTab('comm');
            } catch (e) { console.error("Could not save email comm:", e); }
        }
    };

    const handleOpenWhatsAppEditor = (templateType: 'custom' | 'vacancy' | 'tender' | 'interest' | 'availability') => {
        let defaultText = '';
        const name = selectedProfile?.candidate?.name?.split(' ')[0] || 'kandidaat';

        switch (templateType) {
            case 'custom': defaultText = ''; break;
            case 'vacancy': 
                const vac = vacancies.find(v => v.id === selectedWhatsAppVacancy);
                defaultText = `Hi ${name}, ik zag zojuist een vacature voorbij komen voor ${vac?.title || 'een mooie rol'} die perfect aansluit op jouw profiel. Mogen we hier even kort over bellen?`;
                break;
            case 'tender': defaultText = `Hoi ${name}, we overwegen je profiel aan te bieden op een mooie aanbesteding die zojuist is binnengekomen. Ben je komende week beschikbaar voor een overleg?`; break;
            case 'interest': defaultText = `Hi ${name}, ik ben benieuwd of je nog open staat voor een nieuwe uitdaging. Zo ja, wat zoek je precies in een volgende stap?`; break;
            case 'availability': defaultText = `Hoi ${name}, ben jij momenteel of op korte termijn weer beschikbaar voor nieuwe opdrachten?`; break;
        }

        setWhatsAppText(defaultText);
        setShowWhatsAppOptionsModal(false);
        setShowWhatsAppEditor(true);
    };

    const handleImproveWhatsApp = async () => {
        setIsGeneratingWhatsApp(true);
        try {
            const context = selectedWhatsAppVacancy ? vacancies.find(v => v.id === selectedWhatsAppVacancy)?.title : undefined;
            const rewritten = await improveWhatsAppMessage(whatsAppText, selectedProfile?.candidate?.name || 'Kandidaat', context);
            setWhatsAppText(rewritten);
        } catch (e) {
            alert('Fout bij Froukje AI generatie.');
        } finally {
            setIsGeneratingWhatsApp(false);
        }
    };

    const handleGenerateAiEmail = async () => {
        if (!selectedProfile || !emailContext) return;
        setIsGeneratingEmail(true);

        // Check if the selected context is a predefined template
        const selectedTemplate = savedEmailTemplates.find(t => t.id === emailContext);

        if (selectedTemplate) {
            // Use the template, just let AI customize variables like name
            try {
                const prompt = `Je bent Froukje, een professionele recruiter. Neem dit onderstaande e-mail template en vul de gegevens in voor kandidaat '${selectedProfile.candidate.name}' (Functie: ${selectedProfile.candidate.role || 'onbekend'}).

TEMPLATE ONDERWERP: ${selectedTemplate.subject}
TEMPLATE BODY: ${selectedTemplate.body}

Geef je antwoord ALTIJD in het volgende JSON formaat terug:
{"subject": "Het (ingevulde) onderwerp", "body": "De (ingevulde) tekst van de mail, inclusief aanhef en afsluiting"}
Pas alleen de aanhef etc aan, houd de kern van het template hetzelfde.`;

                const response = await ai.models.generateContent({
                    model: AI_PROXY_CHAT_MODEL,
                    contents: prompt,
                    config: { responseMimeType: "application/json" }
                });

                if (response && response.text) {
                    try {
                        const parsed = JSON.parse(response.text);
                        setGeneratedEmailSubject(parsed.subject || selectedTemplate.subject);
                        setGeneratedEmailBody(parsed.body || selectedTemplate.body);
                    } catch (e) {
                        setGeneratedEmailSubject(selectedTemplate.subject);
                        setGeneratedEmailBody(selectedTemplate.body);
                    }
                }
            } catch (e) {
                console.error("Template customization failed", e);
                setGeneratedEmailSubject(selectedTemplate.subject);
                setGeneratedEmailBody(selectedTemplate.body);
            } finally {
                setIsGeneratingEmail(false);
            }
            return;
        }

        try {
            const prompt = `Je bent een professionele recruiter (genaamd Froukje). Schrijf een korte, vlotte en persoonlijke e-mail naar kandidaat '${selectedProfile.candidate.name}' (Functie: ${selectedProfile.candidate.role || 'onbekend'}).

Context (waarom we mailen): ${emailContext}

${emailVacancyId ? `Specifieke vacature waar we voor mailen: ${vacancies.find(v => v.id === emailVacancyId)?.title || 'Onbekend'}` : ''}

Zorg voor:
1. Een pakkend onderwerp
2. Een korte, enthousiaste introductie
3. Een duidelijke call-to-action (bijv. inplannen kennismaking of reageren op de vacature)
4. Schrijf GEEN handtekening aan het einde van de e-mail. De afzender heeft zelf al een handtekening ingesteld, dus sluit gewoon af na de laatste zin en vermijd 'Met vriendelijke groet' of 'Groet, Froukje'.

Geef je antwoord ALTIJD in het volgende JSON formaat terug:
{"subject": "Onderwerp van de mail", "body": "Volledige tekst van de mail, inclusief aanhef en afsluiting"}`;

            const response = await ai.models.generateContent({
                model: AI_PROXY_CHAT_MODEL,
                contents: prompt,
                config: {
                    responseMimeType: "application/json",
                }
            });

            if (response && response.text) {
                try {
                    const match = response.text.match(/\{[\s\S]*\}/);
                    const jsonStr = match ? match[0] : response.text;
                    const parsed = JSON.parse(jsonStr);
                    setGeneratedEmailSubject(parsed.subject || 'Kennismaking');
                    setGeneratedEmailBody(parsed.body || parsed.text || response.text);
                } catch (e) {
                    console.warn("Could not parse AI email response as JSON", e);
                    setGeneratedEmailSubject('Voorstel ' + selectedProfile.candidate.name);
                    setGeneratedEmailBody(response.text);
                }
            }
        } catch (e) {
            console.error("AI Email generation failed", e);
            alert("Kon geen e-mail genereren. Probeer het later opnieuw.");
        } finally {
            setIsGeneratingEmail(false);
        }
    };

    const executeDeleteCandidate = async () => {
        if (!candidateToDelete || !selectedProfile) return;

        if (deleteConfirmationName.trim().toUpperCase() !== 'VERWIJDEREN') {
            alert("De ingevoerde tekst is niet correct. Typ VERWIJDEREN om te bevestigen.");
            return;
        }

        // Optimistic Update
        setDeletedIds(prev => [...prev, candidateToDelete]);
        handleSetSelectedProfile(null);
        setShowDeleteConfirm(false);
        setDeleteConfirmationName('');

        const success = await supabase_delete_candidate_profile(candidateToDelete);

        if (!success) {
            alert("Verwijderen mislukt. De kandidaat wordt teruggezet.");
            setDeletedIds(prev => prev.filter(id => id !== candidateToDelete)); // Rollback
        }
        setCandidateToDelete(null);
    };

    const handleMatch = async () => {
        if (!selectedProfile) return;
        setIsMatching(true);
        setMatchStatusText('Vacatures verifiëren...');
        
        const activeVacancies = vacancies.filter(v => {
            const s = (v.data?.lifecycleStatus || v.status || 'Concept').toLowerCase();
            if (['archief', 'archive', 'done', 'afwezen', 'ingetrokken', 'afgerond'].includes(s) || v.data?.isClosed === true) return false;
            return true;
        });
        
        if (activeVacancies.length === 0) {
            alert("Er zijn momenteel geen vacatures om de kandidaat aan te matchen.");
            setIsMatching(false);
            setMatchStatusText('');
            return;
        }
        
        setMatchStatusText(`Gevonden: ${activeVacancies.length} vacatures. AI analyse start...`);
        try {
            const results = await performBatchMatch({ name: selectedProfile.candidate.name, role: selectedProfile.candidate.role, summary: selectedProfile.parsed_cv?.summary, skills: selectedProfile.parsed_cv?.skills, timeline: selectedProfile.parsed_cv?.timeline }, activeVacancies);
            setMatchResult(results || []);
            setMatchStatusText('Match voltooid!');
            setTimeout(() => setMatchStatusText(''), 3000);
        } catch (e) { 
            console.error(e); 
            setMatchStatusText('Fout bij analyseren'); 
            setTimeout(() => setMatchStatusText(''), 3000); 
        } finally { 
            setIsMatching(false); 
        }
    };

    const handleProfileImageUpload = async (file: File, profile: any) => {
        try {
            // Optimistic update
            const objectUrl = URL.createObjectURL(file);
            const updatedProfile = { ...profile, candidate: { ...profile.candidate, photo_url: objectUrl } };
            setSelectedProfile(updatedProfile);

            // Upload
            const photoUrl = await supabase_upload_image(file);
            if (photoUrl) {
                await supabase_update_candidate_full(profile.id, profile.candidate.id, { photo_url: photoUrl }, {});
                // Final update with real URL
                setSelectedProfile({ ...updatedProfile, candidate: { ...updatedProfile.candidate, photo_url: photoUrl } });
            }
        } catch (error) {
            console.error("Image upload failed", error);
            alert("Foto uploaden mislukt.");
        }
    };

    const handleOpenVersion = (version: any) => {
        try {
            const cvData = JSON.parse(version.motivation);
            let restoredTemplate: CVTemplate | undefined = undefined;
            if (version.cv_snapshot && version.cv_snapshot.design) {
                restoredTemplate = {
                    id: version.id,
                    name: version.cv_summary,
                    design: version.cv_snapshot.design,
                    structure: version.cv_snapshot.structure || version.cv_snapshot.pages,
                    decorations: version.cv_snapshot.decorations
                };
            }
            cvData.candidateId = selectedProfile.candidate.id;
            onNavigate('cv_builder', cvData, restoredTemplate);
        } catch (e) {
            console.error("Error parsing CV data", e);
            alert("Kan deze versie niet openen.");
        }
    };

    const handleDeleteVersion = async (e: React.MouseEvent, versionId: string) => {
        e.stopPropagation();
        if (confirm("Weet je zeker dat je deze versie wilt verwijderen?")) {
            const success = await supabase_delete_application(versionId);
            if (success) {
                setCvVersions(prev => prev.filter(v => v.id !== versionId));
            } else {
                alert("Kon versie niet verwijderen.");
            }
        }
    };

    const handleOpenDoc = async (doc: any) => {
        if (!doc) return;
        
        if (doc.storage_path) {
             try {
                await openStorageDocument(doc.storage_path);
             } catch (e: any) {
                alert(e.message || "Kon document niet openen.");
             }
             return;
        }

        try {
            if (doc.content && typeof doc.content === 'string' && doc.content.includes('_isDocumentBuilder')) {
                if (onNavigate) {
                    onNavigate('documentbuilder', null, undefined, {
                        title: doc.title,
                        content: doc.content,
                        candidateId: selectedProfile?.candidate?.id,
                        fileId: doc.id
                    } as any);
                }
                return;
            }
        } catch (e) {
            console.error("Error checking document type", e);
        }
        
        if (onNavigate) {
            onNavigate('docs', null, undefined, {
                title: doc.title,
                content: doc.content || '',
                candidateId: selectedProfile?.candidate?.id
            } as any);
        }
    };

    const handleOpenTemplateModal = async () => {
        if (!selectedProfile) return;
        setShowTemplateModal(true);
    };

    const handleGenerateCV = async (templateId: string) => {
        if (!selectedProfile || !user?.clientId) return;
        const tmpl = proTemplates.find(t => t.id === templateId);
        if (!tmpl || !tmpl.storage_path) return;

        setIsPoolLoading(true);

        try {
            // 1. Fetch template buffer to extract custom shortcodes on-the-fly
            const { data: fileData, error: fileError } = await supabase.storage.from('documents').download(tmpl.storage_path);
            if (fileError || !fileData) throw fileError;
            const templateBuffer = await fileData.arrayBuffer();

            const extractedTags = extractTemplateFields(templateBuffer);

            const templateData: CVData = mapCandidateToCVData(selectedProfile);

            // Add any custom fields found in the document that aren't standard mapped tags
            templateData.shortcodes = extractedTags.map(tag => ({
                name: tag,
                value: ''
            }));

            setProTemplateConfig({
                isOpen: true,
                template: tmpl,
                candidateData: templateData,
                vacancyId: '',
                isTailoring: false
            });

            setShowTemplateModal(false);
        } catch (error) {
            console.error("Fout bij ophalen Pro Template voor parsing:", error);
            alert("Kon het Pro Template niet voorbereiden.");
        } finally {
            setIsPoolLoading(false);
        }
    };

    const executeProGenerate = async () => {
        if (!selectedProfile || !user?.clientId || !proTemplateConfig.template) return;
        const tmpl = proTemplateConfig.template as any;
        const dataConfig = proTemplateConfig.candidateData;

        setIsPoolLoading(true);
        try {
            if (!tmpl.storage_path) {
                alert('Template niet gevonden.');
                return;
            }

            // 1. Fetch template buffer
            const { data: fileData, error: fileError } = await supabase.storage.from('documents').download(tmpl.storage_path);
            if (fileError || !fileData) throw fileError;
            const templateBuffer = await fileData.arrayBuffer();

            // 2. Prepare Data arrays for loops 
            const workHistory = (dataConfig.workHistory || []).map((exp: any) => ({
                role: exp.role || exp.title || '',
                company: exp.company || '',
                period: exp.period || exp.year || '',
                description: exp.description || ''
            }));

            const education = (dataConfig.education || []).map((edu: any) => ({
                degree: edu.degree || edu.opleiding || '',
                school: edu.school || edu.instituut || '',
                period: edu.year || edu.periode || ''
            }));

            const templateData: any = {
                name: dataConfig.personalInfo?.name || '',
                email: dataConfig.personalInfo?.email || '',
                phone: dataConfig.personalInfo?.phone || '',
                location: dataConfig.personalInfo?.location || '',
                linkedin: dataConfig.personalInfo?.linkedin || '',
                dateOfBirth: dataConfig.personalInfo?.dateOfBirth || '',
                placeOfBirth: dataConfig.personalInfo?.placeOfBirth || '',
                role: dataConfig.title || '',
                summary: dataConfig.summary || '',
                skills: (dataConfig.skills || []).map((s: any) => typeof s === 'string' ? s : s.name).join(', '),
                workHistory,
                education
            };

            // Inject 1-indexed array tags for Word templates that don't want to use loops
            // e.g. [experience1_role], [experience1_company], [education1_degree]
            workHistory.forEach((exp: any, index: number) => {
                const i = index + 1;
                templateData[`experience${i}_role`] = exp.role;
                templateData[`experience${i}_company`] = exp.company;
                templateData[`experience${i}_period`] = exp.period;
                templateData[`experience${i}_description`] = exp.description;
            });

            education.forEach((edu: any, index: number) => {
                const i = index + 1;
                templateData[`education${i}_degree`] = edu.degree;
                templateData[`education${i}_school`] = edu.school;
                templateData[`education${i}_period`] = edu.period;
            });

            // Inject Custom Fields seamlessly
            if (dataConfig.customFields) {
                dataConfig.customFields.forEach(cf => {
                    templateData[cf.name] = cf.value || '';
                });
            }

            // Inject Shortcodes seamlessly
            if (dataConfig.shortcodes) {
                dataConfig.shortcodes.forEach(sc => {
                    templateData[sc.name] = sc.value || '';
                });
            }

            // 3. Generate Doc
            const newDocBlob = generateWordDocument(templateBuffer, templateData);

            // 4. Upload to OneDrive
            const safeName = (dataConfig.personalInfo?.name || 'Gegenereerd_CV').replace(/[^a-z0-9]/gi, '_').toLowerCase();
            const fileName = `CV_${safeName}_${Date.now()}.docx`;

            const { webUrl, error: uploadErr } = await uploadToOneDrive(newDocBlob, fileName);

            if (uploadErr) {
                console.error("OneDrive upload error", uploadErr);
                alert("Fout bij opslaan in de OneDrive: Microsoft account is mogelijk niet gekoppeld. Controleer de Agenda Instellingen in Froukje.");
            } else if (webUrl) {
                // Success! Open it
                window.open(webUrl, '_blank');
            }

            // Save to candidate docs
            try {
                // 1. Upload to storage
                const storageFileName = `resumes/${selectedProfile.candidate.id}_${fileName}`;
                const { error: storageErr } = await supabase.storage.from('public').upload(storageFileName, newDocBlob);
                if (!storageErr) {
                    const { data: publicData } = supabase.storage.from('public').getPublicUrl(storageFileName);

                    // 2. Save DB record
                    await supabase_save_document({
                        client_id: user.clientId,
                        title: tmpl.title ? `Word CV - ${tmpl.title}` : `Word CV - ${fileName}`,
                        content: '',
                        is_template: false,
                        candidate_id: selectedProfile.candidate.id,
                        file_url: publicData.publicUrl,
                        storage_path: `public/${storageFileName}`
                    });
                }
            } catch (saveErr) {
                console.error("Kan gegenereerd CV niet als document opslaan in de DB:", saveErr);
            }

            setProTemplateConfig(prev => ({ ...prev, isOpen: false }));

            // Optionally log to communication
            if (user?.id) {
                await supabase_save_candidate_communication(selectedProfile.id, user.id, `Pro Word CV Gegenereerd o.b.v. template: ${tmpl.title}`, 'note');
                fetchCandidateDocs(selectedProfile.candidate.id);
            }

        } catch (e) {
            console.error(e);
            alert('Er is een fout opgetreden bij het downloaden van de template.');
        } finally {
            setIsPoolLoading(false);
        }
    };

    const generateTailoredCV = async () => {
        if (!proTemplateConfig.vacancyId) {
            alert("Selecteer eerst een vacature om op af te stemmen.");
            return;
        }

        const vac = vacancies.find(v => v.id === proTemplateConfig.vacancyId) as any;
        if (!vac) return;

        setProTemplateConfig(prev => ({ ...prev, isTailoring: true }));
        try {
            const promptStr = `Je bent een expert IT Recruiter Froukje.
Jouw taak is om het CV profiel en de huidige rol van een kandidaat te herschrijven en optimaliseren, zodat deze perfect aansluit bij een specifieke vacature.
Maak de tekst overtuigend, professioneel en vlot. Gebruik de originele input en zuig geen onwaarheden uit je duim, maar leg de juiste accenten.

Vacature:
Titel: ${vac.title}
Functie eisen: ${vac.requirements || vac.description}
Omschrijving: ${vac.description}

Originele CV Data:
Huidige Rol: ${proTemplateConfig.candidateData.title}
Profielschets / Summary: ${proTemplateConfig.candidateData.summary}

Geef uitsluitend JSON terug in dit exacte formaat (zonder markdown blokken of extra tekst):
{
  "tailoredRole": "De geoptimaliseerde functietitel of headline (kort)",
  "tailoredSummary": "De wervende en op de vacature afgestemde profielschets in een vlotte, professionele paragraaf. Ongeveer 4 zinnen."
}`;

            const aiResponse = await ai.models.generateContent({ model: AI_PROXY_CHAT_MODEL, contents: promptStr });
            const resultStr = aiResponse.text || "{}";
            const cleanedStr = resultStr.replace(/```json/g, '').replace(/```/g, '').trim();
            const result = JSON.parse(cleanedStr);

            setProTemplateConfig(prev => ({
                ...prev,
                tailorResult: {
                    matchAnalysis: "Kandidaat profielschets en functietitel zijn geoptimaliseerd voor de gekozen vacature.",
                    tailoredRole: result.tailoredRole,
                    tailoredSummary: result.tailoredSummary
                },
                candidateData: {
                    ...prev.candidateData,
                    title: result.tailoredRole || prev.candidateData.title,
                    summary: result.tailoredSummary || prev.candidateData.summary
                }
            }));

        } catch (error) {
            console.error("AI Tailoring Error:", error);
            alert("Kon de AI optimalisatie niet voltooien.");
        } finally {
            setProTemplateConfig(prev => ({ ...prev, isTailoring: false }));
        }
    };

    const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0] || !selectedProfile || !user?.clientId) return;

        const file = e.target.files[0];
        const fileExt = file.name.split('.').pop()?.toLowerCase();
        
        const allowedExts = ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg', 'txt'];
        if (!allowedExts.includes(fileExt || '')) {
             alert('Ongeldig bestandstype. Gebruik PDF, Word (doc/docx), of veelgebruikte afbeeldingen.');
             return;
        }

        setIsPoolLoading(true);
        try {
            const fileId = crypto.randomUUID();
            const storagePath = `${user.id || user.clientId}/${fileId}.${fileExt}`;

            const { error: uploadError } = await supabase.storage
                .from('documents')
                .upload(storagePath, file);

            if (uploadError) throw uploadError;

            const { data: publicData } = supabase.storage.from('documents').getPublicUrl(storagePath);

            await supabase_save_document({
                id: fileId,
                client_id: user.clientId,
                title: file.name,
                content: '',
                is_template: false,
                candidate_id: selectedProfile.candidate.id,
                file_url: publicData.publicUrl,
                storage_path: storagePath
            });

            alert('Bestand succesvol geüpload!');
            fetchCandidateDocs(selectedProfile.candidate.id);
        } catch (e) {
            console.error('Upload failed:', e);
            alert('Upload mislukt.');
        } finally {
            setIsPoolLoading(false);
            e.target.value = '';
        }
    };



    const mapCandidateToCVData = (profile: any): CVData => {
        const c = profile.candidate || {};
        // Fix: Use ai_parsed_data if parsed_cv is missing (common in Supabase data)
        const p = profile.parsed_cv || profile.ai_parsed_data || {};

        const dobFromProfile = normalizeDateOfBirthToInputValue(extractDateOfBirthFromProfile(profile)) || '';
        return {
            candidateId: profile.candidate_id,
            title: c.role || p.currentRole || 'Kandidaat',
            summary: p.summary || c.cvSummary || '',
            personalInfo: {
                name: c.name || '',
                email: c.email || '',
                phone: c.phone || '',
                location: '',
                dateOfBirth:
                    dobFromProfile ||
                    normalizeDateOfBirthToInputValue(
                        p.dateOfBirth ||
                            p.geboortedatum ||
                            p.personalInfo?.dateOfBirth ||
                            p.personalInfo?.geboortedatum ||
                            ''
                    ) ||
                    p.dateOfBirth ||
                    p.geboortedatum ||
                    p.personalInfo?.dateOfBirth ||
                    '',
                placeOfBirth: p.placeOfBirth || p.personalInfo?.placeOfBirth || '',
                linkedin: c.linkedin_url || '',
                photoUrl: c.photo_url || c.avatar_url || ''
            } as any,
            workHistory: (p.timeline || []).map((t: any) => ({
                company: [t.company, t.location].filter(Boolean).join(' · '),
                role: t.role || t.title,
                period: t.year || t.period,
                description: t.description
            })),
            education: (p.education || []).map((e: any) => ({
                school: e.school,
                degree: e.degree,
                year: e.year
            })),
            skills: p.tags || [],
            motivation: p.motivation || '',
            references: (p.references || []).map((r: any) => ({
                name: r.name,
                role: r.role,
                company: r.company,
                email: r.email,
                phone: r.phone
            })),
            languages: (p.languages || []).map((l: any) => ({
                language: l.language,
                level: l.level
            })),
            courses: (p.courses || []).map((c: any) => ({
                name: c.name,
                institute: c.institute,
                year: c.year
            })),
            hobbies: p.hobbies || [],
            otherInfo: p.otherText || ''
        };
    };

    const handleSelectCVTemplate = (template: CVTemplate) => {
        if (!selectedProfile) return;
        const cvData = mapCandidateToCVData(selectedProfile);
        setShowTemplateModal(false);
        onNavigate('cv_builder', cvData, template);
    };

    const handleSelectDocTemplateAndNavigate = (doc?: any) => {
        if (!selectedProfile) return;

        let processedContent = '<p>Start hier met typen...</p>';
        let docTitle = 'Nieuw Document';

        if (doc) {
            docTitle = `${selectedProfile.candidate.name} - ${doc.title}`;
            const formatList = (items: any[], type: 'timeline' | 'education') => {
                if (!Array.isArray(items) || items.length === 0) return '';
                return items.map(item => {
                    if (type === 'timeline') {
                        return `<div style="margin-bottom: 12px;"><strong>${item.role || 'Functie'}</strong> bij ${item.company || 'Bedrijf'} (${item.year || 'Jaar'})<br /><span style="color: #666; font-size: 0.9em;">${item.description || ''}</span></div>`;
                    } else {
                        return `<div style="margin-bottom: 8px;"><strong>${item.degree || 'Opleiding'}</strong> - ${item.school || 'School'} (${item.year || 'Jaar'})</div>`;
                    }
                }).join('');
            };

            // Use ai_parsed_data fallback
            const dataToUse = selectedProfile.parsed_cv || selectedProfile.ai_parsed_data || {};

            let text = doc.content || '';
            text = text.replace(/{{ name }}/g, selectedProfile.candidate.name || 'Naam');
            text = text.replace(/{{ email }}/g, selectedProfile.candidate.email || 'email@example.com');
            text = text.replace(/{{ phone }}/g, selectedProfile.candidate.phone || '06-12345678');
            text = text.replace(/{{ role }}/g, selectedProfile.candidate.role || 'Functie');
            text = text.replace(/{{ linkedin }}/g, selectedProfile.candidate.linkedin_url || '#');
            text = text.replace(/{{ summary }}/g, dataToUse.summary || selectedProfile.candidate.cvSummary || 'Profielschets...');
            text = text.replace(/{{ experience }}/g, formatList(dataToUse.timeline, 'timeline'));
            text = text.replace(/{{ education }}/g, formatList(dataToUse.education, 'education'));

            processedContent = text;
        } else {
            docTitle = `${selectedProfile.candidate.name} - Blanco`;
        }

        setShowTemplateModal(false);
        onNavigate('docs', null, undefined, {
            title: docTitle,
            content: processedContent,
            candidateId: selectedProfile.candidate.id
        });
    };

    const handleDeleteDoc = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (!confirm("Weet je zeker dat je dit document wilt verwijderen?")) return;

        try {
            await supabase_delete_document(id);
            if (selectedProfile?.candidate?.id) {
                fetchCandidateDocs(selectedProfile.candidate.id);
            }
        } catch (error) {
            console.error("Error deleting document:", error);
            alert("Kon document niet verwijderen.");
        }
    };


    return (
        <div className="flex flex-col h-full bg-white">
            {/* Header Area with Sub-Tabs */}
            <div className="px-4 lg:px-6 pt-2 pb-0">
                <div className="flex justify-between items-center mb-0">
                    <div className="flex items-center gap-3">
                        <h1 className="text-[15px] font-bold text-slate-900 tracking-tight leading-none">Talent Pool</h1>
                        <span className="text-[11px] text-slate-400 font-medium px-2 py-0.5 bg-slate-50 rounded-full">{totalCount} kandidaten</span>
                    </div>
                </div>

                {/* Sub Navigation */}
                <div className="flex items-center gap-0 border-b border-slate-100 mt-2">
                    <button
                        onClick={() => setSubTab('list')}
                        className={`py-2 px-4 text-[12px] font-semibold border-b-2 transition-all flex items-center gap-2 ${subTab === 'list' ? 'border-[#E21E26] text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                    >
                        Kandidaten <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${subTab === 'list' ? 'bg-slate-100 text-slate-600' : 'bg-slate-100 text-slate-400'}`}>{totalCount}</span>
                    </button>
                    <button
                        onClick={() => setSubTab('stats')}
                        className={`py-2 px-4 text-[12px] font-semibold border-b-2 transition-all flex items-center gap-2 ${subTab === 'stats' ? 'border-[#e51b23] text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                    >
                        Statistieken
                    </button>


                </div>
            </div>

            <div className="flex-1 flex flex-col overflow-hidden relative w-full min-h-0">
                {subTab === 'stats' && renderStatsView()}



                <div className={`flex flex-1 gap-6 overflow-hidden h-full ${subTab !== 'list' ? 'hidden' : ''}`}>

                    <div className="flex flex-1 gap-6 overflow-hidden">
                        {/* LEFT COLUMN: LIST */}
                        {!selectedProfile && (
                            <div className="flex-1 flex flex-col h-full min-h-0 gap-2 w-full">
                                {/* Search & Filter bar (inside header) — matched to PlacementsList */}
                                <div className="bg-white border-b border-slate-200 px-6 py-3 shrink-0 flex flex-wrap items-center gap-2">
                                    <div className="relative flex-1 min-w-[200px]">
                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                                    <input
                                        type="text"
                                        value={nlQuery}
                                        onChange={(e) => setNlQuery(e.target.value)}
                                        placeholder="Zoek op naam, functie, e-mail of telefoon..."
                                        className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all text-slate-700 placeholder:text-slate-400"
                                    />
                                    </div>
                                    {nlQuery && (
                                        <button onClick={() => setNlQuery('')} className="p-1 hover:bg-slate-100 rounded-full text-slate-300 transition-colors">
                                            <X size={13} />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleAiAnalysis(nlQuery)}
                                        disabled={!nlQuery.trim() || isAiSearching}
                                        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white rounded-lg text-[11px] font-bold transition-all disabled:opacity-50 shrink-0"
                                    >
                                        {isAiSearching ? <Loader2 size={12} className="animate-spin" /> : <BrainCircuit size={12} />} AI
                                    </button>
                                    <button
                                        onClick={() => setShowStats(!showStats)}
                                        className={`flex items-center gap-1.5 px-3 py-2 border rounded-full text-[12px] font-semibold transition-all shrink-0 ${
                                            showStats ? 'bg-slate-900 text-white border-slate-900 shadow-sm' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300'
                                        }`}
                                    >
                                        <Filter size={13} /> Filters
                                    </button>
                                    {!isClientPortal && (
                                        <button
                                            onClick={() => { setAddMode('new'); setShowAddModal(true); }}
                                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#e51b23] hover:bg-[#c1151d] text-white rounded-lg text-[11px] font-bold transition-all shrink-0"
                                        >
                                            <UserPlus size={12} /> Nieuw
                                        </button>
                                    )}
                                    {isSearchingBackend && <Loader2 size={14} className="animate-spin text-slate-400 shrink-0" />}
                                </div>

                                {showAiMatchColumn && lastAiSearchQuery && (
                                    <div className="px-6 py-2 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2 shrink-0">
                                        <p className="text-[11px] text-slate-600 flex items-center gap-2 min-w-0">
                                            <BrainCircuit size={14} className="text-violet-500 shrink-0" aria-hidden />
                                            <span className="truncate" title={lastAiSearchQuery}>
                                                AI-zoekactief: &quot;{lastAiSearchQuery}&quot; · {Object.keys(aiMatchById).length} treffer(s)
                                            </span>
                                        </p>
                                        <button
                                            type="button"
                                            onClick={clearAiSearch}
                                            className="text-[11px] font-semibold text-slate-600 hover:text-slate-900 underline underline-offset-2 shrink-0"
                                        >
                                            Filter wissen
                                        </button>
                                    </div>
                                )}

                                {/* Collapsible Stats & Tools Panel */}
                                {(showStats ||
                                    activeAiFilter ||
                                    filterStatus ||
                                    filterDateRange !== 'all' ||
                                    filterDateFrom ||
                                    filterDateTo ||
                                    filterExperience ||
                                    filterHasCV ||
                                    selectedTags.length > 0 ||
                                    pipelineStageFilter) && (
                                    <div className="px-4 md:px-6 pb-3 pt-1 animate-slideDown origin-top">
                                        <div className="rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden">
                                            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/40">
                                                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Filters</span>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFilterStatus(null);
                                                            setFilterDateRange('all');
                                                            setFilterDateFrom('');
                                                            setFilterDateTo('');
                                                            setFilterExperience(null);
                                                            setFilterHasCV(false);
                                                            setSelectedTags([]);
                                                            setPipelineStageFilter(null);
                                                        }}
                                                        className="text-[11px] font-medium text-slate-500 hover:text-slate-800 underline underline-offset-2"
                                                    >
                                                        Alles wissen
                                                    </button>
                                                    <button type="button" onClick={() => setShowStats(false)} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Filterpaneel sluiten">
                                                        <X size={15} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="px-4 py-4 md:px-5 md:py-5 space-y-6 bg-white">
                                                {/* Status */}
                                                <div className="space-y-2.5">
                                                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Status</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {clientStatuses.map((stat) => (
                                                            <button
                                                                key={stat.id}
                                                                type="button"
                                                                onClick={() => setFilterStatus(filterStatus === stat.label ? null : stat.label)}
                                                                className={`rounded-full px-3.5 py-2 text-[12px] font-medium border transition-all ${
                                                                    filterStatus === stat.label
                                                                        ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                                                                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                                                }`}
                                                            >
                                                                {stat.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                                {/* Tijdrange */}
                                                <div className="space-y-2.5">
                                                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Tijdrange</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {[
                                                            { id: 'all', label: 'Alle tijden' },
                                                            { id: '7days', label: 'Afgelopen 7 dagen' },
                                                            { id: '30days', label: 'Afgelopen 30 dagen' },
                                                            { id: 'thisYear', label: 'Dit jaar' },
                                                            { id: 'custom', label: 'Eigen periode' },
                                                        ].map((dr) => (
                                                            <button
                                                                key={dr.id}
                                                                type="button"
                                                                onClick={() => {
                                                                    setFilterDateRange(dr.id);
                                                                    if (dr.id !== 'custom') {
                                                                        setFilterDateFrom('');
                                                                        setFilterDateTo('');
                                                                    }
                                                                }}
                                                                className={`rounded-full px-3.5 py-2 text-[12px] font-medium border transition-all ${
                                                                    filterDateRange === dr.id
                                                                        ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                                                                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                                                }`}
                                                            >
                                                                {dr.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    {filterDateRange === 'custom' && (
                                                        <div className="flex flex-wrap items-end gap-3 pt-1 pl-0.5">
                                                            <div className="flex flex-col gap-1">
                                                                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Van</label>
                                                                <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-100">
                                                                    <Calendar size={14} className="text-slate-400 shrink-0" aria-hidden />
                                                                    <input
                                                                        type="date"
                                                                        value={filterDateFrom}
                                                                        onChange={(e) => setFilterDateFrom(e.target.value)}
                                                                        className="text-[12px] font-medium text-slate-800 bg-transparent border-0 outline-none min-w-0"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-col gap-1">
                                                                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Tot</label>
                                                                <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-100">
                                                                    <Calendar size={14} className="text-slate-400 shrink-0" aria-hidden />
                                                                    <input
                                                                        type="date"
                                                                        value={filterDateTo}
                                                                        onChange={(e) => setFilterDateTo(e.target.value)}
                                                                        className="text-[12px] font-medium text-slate-800 bg-transparent border-0 outline-none min-w-0"
                                                                    />
                                                                </div>
                                                            </div>
                                                            {(!filterDateFrom || !filterDateTo) && (
                                                                <p className="text-[11px] text-slate-400 pb-1">Kies begin- en einddatum om te filteren op aanmaakdatum.</p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Ervaring */}
                                                <div className="space-y-2.5">
                                                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Ervaring</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {(['junior', 'medior', 'senior'] as const).map((lvl, i) => (
                                                            <button
                                                                key={lvl}
                                                                type="button"
                                                                onClick={() => setFilterExperience(filterExperience === lvl ? null : lvl)}
                                                                className={`rounded-full px-3.5 py-2 text-[12px] font-medium border transition-all ${
                                                                    filterExperience === lvl
                                                                        ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                                                                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                                                }`}
                                                            >
                                                                {['Junior (0-2)', 'Medior (3-5)', 'Senior (5+)'][i]}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                                {/* Profiel */}
                                                <div className="space-y-2.5">
                                                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Profiel</p>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFilterHasCV(!filterHasCV)}
                                                        className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[12px] font-medium border transition-all ${
                                                            filterHasCV
                                                                ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                                                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                                        }`}
                                                    >
                                                        <FileText size={14} /> Met volledig CV
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                                    <div className="flex-1 overflow-x-auto overflow-y-auto min-h-0">

                                        {(() => {
                                            const effectiveLoading = isLoading;
                                            const displayLoading = effectiveLoading;
                                            return ((typeof effectiveLoading === 'number' || effectiveLoading || isFetchingPage || isAiSearching) && filteredProfiles.length === 0) ? (
                                                typeof effectiveLoading === 'number' ? (
                                                    <div className="flex-1 flex flex-col items-center justify-center py-20">
                                                        <div className="w-full max-w-sm">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <p className="text-xs font-black text-slate-700 uppercase tracking-widest">Talentpool Synchroniseren</p>
                                                                <p className="text-xs font-bold text-[#e51b23]">{displayLoading}%</p>
                                                            </div>
                                                            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                                                <div className="h-full bg-[#e51b23] transition-all duration-300 ease-out" style={{ width: `${displayLoading}%` }} />
                                                            </div>
                                                            <p className="text-[10px] text-slate-400 font-medium text-center mt-3 animate-pulse">Bijwerken van kandidaten en match-profielen...</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center py-20">
                                                        <Loader2 className={`animate-spin mb-2 ${isAiSearching ? 'text-violet-400' : 'text-slate-200'}`} size={28} />
                                                        <p className={`text-[11px] font-medium mt-2 ${isAiSearching ? 'text-violet-500' : 'text-slate-400'}`}>{isAiSearching ? 'AI matcht het beste talent...' : 'Database laden...'}</p>
                                                    </div>
                                                )
                                            ) : filteredProfiles.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center py-20 text-center">
                                                    <p className="text-slate-400 text-[12px] font-medium mb-4">Geen kandidaten gevonden.</p>
                                                    {nlQuery.trim().length > 0 && !isAiSearching && (
                                                        <button 
                                                            onClick={() => handleAiAnalysis(nlQuery)}
                                                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                                                        >
                                                            <BrainCircuit size={16} /> Meer vinden? Gebruik AI search
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                            <>
                                                {/* Desktop Table View */}
                                                <table className="w-full text-left border-collapse min-w-[1000px] hidden md:table">
                                                    <thead className="sticky top-0 z-10">
                                                        <tr className="bg-slate-50 border-b border-slate-200 text-slate-500">
                                                            <th className="px-3 py-2.5 w-10"></th>
                                                            <th 
                                                                className="px-3 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-slate-600 transition-colors select-none"
                                                                onClick={() => setSortConfig(s => ({ column: 'candidate_number', ascending: s.column === 'candidate_number' ? !s.ascending : false }))}
                                                            >
                                                                <div className="flex items-center gap-1"># {sortConfig.column === 'candidate_number' && (sortConfig.ascending ? '↑' : '↓')}</div>
                                                            </th>
                                                            <th 
                                                                className="px-3 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-slate-600 transition-colors select-none"
                                                                onClick={() => setSortConfig(s => ({ column: 'candidate', ascending: s.column === 'candidate' ? !s.ascending : true }))}
                                                            >
                                                                <div className="flex items-center gap-1">Kandidaat {sortConfig.column === 'candidate' && (sortConfig.ascending ? '↑' : '↓')}</div>
                                                            </th>
                                                            {!selectedProfile && <th className="px-3 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden md:table-cell">Contact</th>}
                                                            <th 
                                                                className="px-3 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-slate-600 transition-colors select-none"
                                                                onClick={() => setSortConfig(s => ({ column: 'status', ascending: s.column === 'status' ? !s.ascending : true }))}
                                                            >
                                                                <div className="flex items-center gap-1">Status {sortConfig.column === 'status' && (sortConfig.ascending ? '↑' : '↓')}</div>
                                                            </th>
                                                            <th 
                                                                className="px-3 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest cursor-pointer hover:text-slate-600 transition-colors select-none"
                                                                onClick={() => setSortConfig(s => ({ column: 'available_from', ascending: s.column === 'available_from' ? !s.ascending : true }))}
                                                            >
                                                                <div className="flex items-center gap-1">Beschikbaarheid {sortConfig.column === 'available_from' && (sortConfig.ascending ? '↑' : '↓')}</div>
                                                            </th>
                                                            {!selectedProfile && (
                                                                <th className="px-3 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden lg:table-cell">Laatste contact</th>
                                                            )}
                                                            {!selectedProfile && (
                                                                <th className="px-3 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest hidden xl:table-cell">Pipeline</th>
                                                            )}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginatedProfiles.map(profile => {
                                                            const parsed = profile.ai_parsed_data || profile.parsed_cv || {};
                                                            return (
                                                            <tr key={profile.id} onClick={() => handleSetSelectedProfile(profile)} className={`border-b border-slate-100 cursor-pointer transition-colors hover:bg-blue-50/40 group ${selectedProfile?.id === profile.id ? 'bg-blue-50/60' : ''}`}>
                                                                <td className="px-3 py-2.5 w-10">
                                                                    <button onClick={(e) => { e.stopPropagation(); if (profile.candidate?.id) setQuickViewCandidateId(profile.candidate.id); }} className="p-1.5 text-slate-300 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" title="Snel bekijken">
                                                                        <Search size={14} />
                                                                    </button>
                                                                </td>
                                                                <td className="px-3 py-2.5 align-middle">
                                                                    <span className="text-[10px] font-mono font-semibold text-slate-400">
                                                                        {profile.candidate?.candidate_number ? `#${profile.candidate.candidate_number}` : '—'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 py-2.5">
                                                                    {(() => {
                                                                        const lastJob = parsed.timeline?.[0];
                                                                        const cid = String(profile.candidate?.id || profile.id || '');
                                                                        const aiMeta = showAiMatchColumn && cid ? aiMatchById[cid] : undefined;
                                                                        return (
                                                                            <div className="flex flex-col gap-1.5 min-w-0 max-w-md lg:max-w-lg xl:max-w-xl">
                                                                                <div className="flex items-start gap-3">
                                                                                    <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center font-semibold text-xs border border-slate-200 overflow-hidden shrink-0 text-slate-500 mt-0.5">
                                                                                        {profile.candidate?.photo_url || profile.candidate?.avatar_url ? (
                                                                                            <img src={profile.candidate.photo_url || profile.candidate.avatar_url} className="w-full h-full object-cover" />
                                                                                        ) : profile.candidate?.name?.charAt(0) || '?'}
                                                                                    </div>
                                                                                    <div className="min-w-0 flex-1">
                                                                                        <p className="text-[12px] font-semibold text-slate-700 leading-tight truncate group-hover:text-[#E21E26] transition-colors">{profile.candidate?.name}</p>
                                                                                        {lastJob ? (
                                                                                            <p className="text-[10px] text-slate-400 font-medium truncate mt-[1px]">
                                                                                                {lastJob.title || lastJob.role || parsed.currentRole || profile.candidate?.role || ''}{lastJob.company ? <span className="text-slate-300"> · {lastJob.company}</span> : null}
                                                                                            </p>
                                                                                        ) : (
                                                                                            <p className="text-[10px] text-slate-400 font-medium truncate mt-[1px]">
                                                                                                {profile.candidate?.role || parsed.currentRole || parsed.role || ''}
                                                                                            </p>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                                {aiMeta && (
                                                                                    <p
                                                                                        className="text-[10px] text-slate-600 leading-relaxed pl-10 border-l-2 border-violet-200/90 ml-0 py-0.5 pr-1 line-clamp-3"
                                                                                        title={`${aiMeta.reason}${aiMeta.score != null && Number.isFinite(aiMeta.score) ? ` (${aiMeta.score}%)` : ''}`}
                                                                                    >
                                                                                        <span className="text-violet-700 font-semibold">Zoekmatch: </span>
                                                                                        {aiMeta.reason}
                                                                                        {aiMeta.score != null && Number.isFinite(aiMeta.score) && (
                                                                                            <span className="text-violet-600 font-medium tabular-nums"> · {aiMeta.score}%</span>
                                                                                        )}
                                                                                    </p>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })()}
                                                                </td>
                                                                {!selectedProfile && (
                                                                    <td className="px-3 py-2.5 hidden md:table-cell">
                                                                        <div className="flex flex-col gap-0.5">
                                                                            {profile.candidate?.email && <span className="text-[10px] font-medium text-slate-500 flex items-center gap-1.5"><Mail size={11} className="text-slate-300 shrink-0" /> {profile.candidate.email}</span>}
                                                                            {(profile.candidate?.phone || parsed.contact_info?.phone || parsed.contactInfo?.phone || parsed.phone) && <span className="text-[10px] font-medium text-slate-500 flex items-center gap-1.5"><Phone size={11} className="text-slate-300 shrink-0" /> {profile.candidate?.phone || parsed.contact_info?.phone || parsed.contactInfo?.phone || parsed.phone}</span>}
                                                                            {(() => {
                                                                                const city = talentPoolProfileCity(profile);
                                                                                return city ? (
                                                                                    <span className="text-[10px] font-medium text-slate-500 flex items-center gap-1.5" title="Woonplaats">
                                                                                        <MapPin size={11} className="text-slate-300 shrink-0" />
                                                                                        <span className="truncate max-w-[180px]">{city}</span>
                                                                                    </span>
                                                                                ) : null;
                                                                            })()}
                                                                        </div>
                                                                    </td>
                                                                )}
                                                                <td className="px-3 py-2.5">
                                                                    <div className="relative" onClick={e => e.stopPropagation()}>
                                                                        <button
                                                                            onClick={() => setStatusDropdownOpen(statusDropdownOpen === profile.id ? null : profile.id)}
                                                                            className="group flex items-center gap-1"
                                                                        >
                                                                            <StatusBadge status={localStatusOverrides[profile.id] ?? profile.status} customStatuses={clientStatuses} />
                                                                            <ChevronDown size={10} className="text-slate-300 group-hover:text-slate-500 transition-colors shrink-0" />
                                                                        </button>
                                                                        {(() => {
                                                                            const availableFrom = profile.candidate?.available_from || profile.ai_parsed_data?.available_from || profile.candidate?.ai_parsed_data?.available_from || profile.parsed_cv?.available_from;
                                                                            if (availableFrom) {
                                                                                return (
                                                                                    <div className="mt-1.5 text-[9px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100 inline-block">
                                                                                        Beschikbaar: {new Date(availableFrom).toLocaleDateString('nl-NL')}
                                                                                    </div>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        })()}
                                                                        {['rejected', 'afgewezen', 'archived', 'gearchiveerd', 'blacklisted'].includes((profile.status || '').toLowerCase()) && profile.ai_parsed_data?.rejectionReason && (
                                                                            <div className="absolute top-full left-0 mt-2 z-10 w-48 text-[9px] font-black uppercase text-[#e51b23] tracking-widest whitespace-normal bg-red-50 px-2 py-1.5 rounded-lg border border-red-200 shadow-sm leading-tight flex items-start gap-1">
                                                                                <span className="mt-0.5">-</span> {profile.ai_parsed_data.rejectionReason}
                                                                            </div>
                                                                        )}
                                                                        {statusDropdownOpen === profile.id && (
                                                                            <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl min-w-[180px] py-1 overflow-hidden">
                                                                                {clientStatuses.map(stat => (
                                                                                    <button
                                                                                        key={stat.id}
                                                                                        onClick={() => handleInlineStatusChange(profile.id, stat.label)}
                                                                                        className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors flex items-center gap-2"
                                                                                    >
                                                                                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${stat.color}`}>{stat.label}</span>
                                                                                    </button>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                
                                                                {/* Beschikbaarheid Column */}
                                                                <td className="px-3 py-2.5">
                                                                    {(() => {
                                                                        const avail = profile.ai_parsed_data?.available_from || profile.candidate?.available_from || profile.parsed_cv?.available_from;
                                                                        if (!avail) return <span className="text-slate-300 text-[10px]">-</span>;
                                                                        const dt = new Date(avail);
                                                                        if (isNaN(dt.getTime())) return <span className="text-slate-300 text-[10px]">-</span>;
                                                                        const isPast = dt <= new Date();
                                                                        return (
                                                                            <span className={`text-[10px] font-bold ${isPast ? 'text-emerald-600' : 'text-slate-500'}`}>
                                                                                {dt.toLocaleDateString('nl-NL')}
                                                                            </span>
                                                                        );
                                                                    })()}
                                                                </td>
                                                                {!selectedProfile && (() => {
                                                                    // Use real last_contact_at explicitly. If not available, it should just be empty.
                                                                    const realContact = profile.last_contact_at;
                                                                    const fallbackDates = realContact ? [realContact] : [];
                                                                    const dates = fallbackDates.filter(Boolean).map((d: string) => new Date(d).getTime());
                                                                    const lastContact = dates.length > 0 ? new Date(Math.max(...dates)) : null;

                                                                    const now = new Date();
                                                                    const diffDays = lastContact ? Math.floor((now.getTime() - lastContact.getTime()) / 86400000) : null;
                                                                    const isToday = diffDays === 0;
                                                                    const isYesterday = diffDays === 1;
                                                                    const isOld = diffDays !== null && diffDays > 60;
                                                                    const label = isToday ? 'Vandaag'
                                                                        : isYesterday ? 'Gisteren'
                                                                        : lastContact ? lastContact.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                                                        : null;
                                                                    return (
                                                                        <td className="px-3 py-2.5 hidden lg:table-cell">
                                                                            {label ? (
                                                                                <span className={`text-[11px] font-semibold ${
                                                                                    isToday ? 'text-emerald-600' : isYesterday ? 'text-amber-500' : isOld ? 'text-slate-300' : 'text-slate-500'
                                                                                }`}>
                                                                                    {label}
                                                                                </span>
                                                                            ) : <span className="text-slate-200 text-[11px]">—</span>}
                                                                        </td>
                                                                    );
                                                                })()}
                                                                {!selectedProfile && (() => {
                                                                    const cId = profile.candidate?.id || profile.candidate_id;
                                                                    const counts = cId ? (candidateAppCounts[cId] || {}) : {};
                                                                    const STAGES = [
                                                                        { id: 'New',         label: 'Nieuw',       color: 'bg-slate-50  text-slate-700  border-slate-200' },
                                                                        { id: 'Shortlist',   label: 'Shortlist',   color: 'bg-amber-50  text-amber-700  border-amber-200' },
                                                                        { id: 'Voorgesteld', label: 'Voorgesteld', color: 'bg-violet-50 text-violet-700 border-violet-200' },
                                                                        { id: 'Gesprek',     label: 'Gesprek',     color: 'bg-blue-50   text-blue-700   border-blue-200' },
                                                                        { id: 'Aanbod',      label: 'Aanbod',      color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
                                                                        { id: 'Plaatsing',   label: 'Plaatsing',   color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                                                                        { id: 'Rejected',    label: 'Afgewezen',   color: 'bg-red-50    text-red-700    border-red-200' },
                                                                    ];
                                                                    const total = STAGES.reduce((s, st) => s + (counts[st.id] || 0), 0);
                                                                    return (
                                                                        <td className="px-3 py-2.5 hidden xl:table-cell cursor-pointer group-hover:bg-slate-50/50 transition-colors" onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleSetSelectedProfile(profile);
                                                                            setActiveTab('flows');
                                                                        }}>
                                                                            {isCountsLoading ? (
                                                                                <div className="flex items-center gap-1">
                                                                                    <Loader2 size={11} className="animate-spin text-slate-300" />
                                                                                    <span className="text-[9px] text-slate-300 font-medium">laden...</span>
                                                                                </div>
                                                                            ) : (
                                                                            <>
                                                                            <div className="flex items-center gap-1.5">
                                                                                {STAGES.map(st => {
                                                                                    const c = counts[st.id] || 0;
                                                                                    const isOpen = activePopover?.cId === cId && activePopover?.stageId === st.id;
                                                                                    return (
                                                                                        <div key={st.id} className="relative inline-block">
                                                                                            {c > 0 ? (
                                                                                                <span
                                                                                                    title={st.label}
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        setActivePopover(isOpen ? null : { cId, stageId: st.id });
                                                                                                    }}
                                                                                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border font-black text-[9px] cursor-pointer transition-all ${st.color} ${isOpen ? 'ring-2 ring-slate-300 ring-offset-1' : 'hover:brightness-95'}`}
                                                                                                >
                                                                                                    <span className="text-[11px] font-black leading-none">{c}</span>
                                                                                                    <span className="uppercase tracking-widest text-[8px]">{st.label}</span>
                                                                                                </span>
                                                                                            ) : (
                                                                                                <span
                                                                                                    title={st.label}
                                                                                                    className="inline-flex items-center px-1.5 py-0.5 rounded-lg border bg-white border-slate-100 text-slate-300 font-black text-[10px]"
                                                                                                >
                                                                                                    0
                                                                                                </span>
                                                                                            )}
                                                                                            {isOpen && c > 0 && (
                                                                                                <div 
                                                                                                    className="absolute z-50 left-1/2 -translate-x-1/2 top-[calc(100%+6px)] bg-white border border-slate-200 shadow-xl rounded-xl p-1.5 min-w-[220px]" 
                                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                                >
                                                                                                    <div className="text-[9px] font-bold text-slate-400 mb-1 px-1.5 uppercase tracking-wider">{st.label} Vacatures</div>
                                                                                                    <div className="flex flex-col gap-0.5">
                                                                                                        {talentPoolListStageVacanciesFor(candidateStageVacancies, cId, st.id as TalentPoolListStageId).map((vac) => (
                                                                                                            <button
                                                                                                                key={`${vac.applicationId}-${vac.fromHistory ? 'h' : 'c'}`}
                                                                                                                onClick={(e) => {
                                                                                                                    e.stopPropagation();
                                                                                                                    setActivePopover(null);
                                                                                                                    const fullVac = vacancies.find((v) => v.id === vac.vacancyId);
                                                                                                                    if (fullVac) {
                                                                                                                        onNavigate('vacancies', { ...fullVac, initialStep: 'Pipeline' } as any);
                                                                                                                    } else {
                                                                                                                        onNavigate('vacancies', vac.vacancyId);
                                                                                                                    }
                                                                                                                }}
                                                                                                                className="w-full text-left px-2 py-1.5 text-[11px] font-bold text-slate-600 hover:bg-slate-50 hover:text-[#E21E26] rounded-lg transition-colors flex items-center justify-between gap-2"
                                                                                                            >
                                                                                                                <span className="flex items-center gap-1.5 truncate min-w-0">
                                                                                                                    <span className="w-1 h-1 rounded-full bg-current opacity-50 shrink-0"></span>
                                                                                                                    <span className="truncate" title={vac.title}>{vac.title}</span>
                                                                                                                    {vac.fromHistory && (
                                                                                                                        <span className="text-[8px] font-bold uppercase tracking-wider text-slate-400 shrink-0">hist.</span>
                                                                                                                    )}
                                                                                                                </span>
                                                                                                                {vac.vacancy_number && (
                                                                                                                    <span className="text-[9px] font-black tracking-widest text-[#E21E26]/70 uppercase shrink-0">
                                                                                                                        #{vac.vacancy_number}
                                                                                                                    </span>
                                                                                                                )}
                                                                                                            </button>
                                                                                                        ))}
                                                                                                        {!talentPoolListStageVacanciesFor(candidateStageVacancies, cId, st.id as TalentPoolListStageId).length && (
                                                                                                            <div className="text-[10px] text-slate-400 px-2 py-1.5 italic">Geen vacatures gevonden.</div>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                                {total > 0 && (
                                                                                    <span className="ml-0.5 inline-flex items-center px-1.5 py-0.5 rounded-lg border bg-slate-900 border-slate-800 text-white text-[10px] font-black leading-none">{total}</span>
                                                                                )}
                                                                            </div>

                                                                            </>
                                                                            )}
                                                                        </td>
                                                                    );
                                                                })()}

                                                            </tr>
                                                        )})}
                                                    </tbody>
                                                </table>

                                                {/* Mobile Card View */}
                                                <div className="flex flex-col md:hidden p-4 space-y-3 pb-24">
                                                    {paginatedProfiles.map(profile => (
                                                        <div 
                                                            key={profile.id} 
                                                            onClick={() => handleSetSelectedProfile(profile)} 
                                                            className={`bg-white rounded-xl shadow-sm border p-4 flex flex-col gap-3 active:scale-[0.98] transition-all
                                                                ${selectedProfile?.id === profile.id ? 'border-red-500 shadow-md ring-1 ring-red-500' : 'border-slate-200 hover:border-slate-300'}
                                                            `}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-lg text-slate-400 overflow-hidden shrink-0 border border-slate-200">
                                                                    {profile.candidate?.photo_url || profile.candidate?.avatar_url ? (
                                                                        <img src={profile.candidate.photo_url || profile.candidate.avatar_url} className="w-full h-full object-cover" />
                                                                    ) : (profile.candidate?.name?.charAt(0) || '?')}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <h3 className="font-black text-slate-900 truncate text-sm">{profile.candidate?.name || 'Onbekend'}</h3>
                                                                        {profile.candidate?.candidate_number && (
                                                                            <span className="text-[9px] font-black text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-200 shrink-0">#{profile.candidate.candidate_number}</span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-xs text-slate-500 font-medium truncate mt-0.5">{profile.candidate?.role || profile.ai_parsed_data?.currentRole || profile.ai_parsed_data?.role || profile.parsed_cv?.currentRole || profile.parsed_cv?.role || profile.parsed_cv?.timeline?.[0]?.role || profile.parsed_cv?.timeline?.[0]?.title || profile.parsed_cv?.timeline?.[0]?.functie || profile.ai_parsed_data?.timeline?.[0]?.role || profile.ai_parsed_data?.timeline?.[0]?.title || profile.ai_parsed_data?.timeline?.[0]?.functie || ''}</p>
                                                                    {(() => {
                                                                        const city = talentPoolProfileCity(profile);
                                                                        return city ? (
                                                                            <p className="text-[10px] text-slate-400 font-medium flex items-center gap-1 mt-1 truncate" title={city}>
                                                                                <MapPin size={11} className="text-slate-300 shrink-0" />
                                                                                <span className="truncate">{city}</span>
                                                                            </p>
                                                                        ) : null;
                                                                    })()}
                                                                </div>
                                                                <button onClick={(e) => { e.stopPropagation(); if (profile.candidate?.id) setQuickViewCandidateId(profile.candidate.id); }} className="p-2.5 bg-slate-50 text-slate-600 hover:bg-red-50 hover:text-[#e51b23] rounded-lg transition-colors border border-slate-200 active:scale-95">
                                                                     <Search size={16} />
                                                                </button>
                                                            </div>
                                                            {showAiMatchColumn && (() => {
                                                                const cid = String(profile.candidate?.id || profile.id || '');
                                                                const meta = cid ? aiMatchById[cid] : undefined;
                                                                if (!meta) return null;
                                                                return (
                                                                    <p
                                                                        className="text-[10px] text-slate-600 leading-relaxed mt-2 pt-2 border-t border-violet-100/80 line-clamp-4"
                                                                        title={`${meta.reason}${meta.score != null && Number.isFinite(meta.score) ? ` (${meta.score}%)` : ''}`}
                                                                    >
                                                                        <span className="text-violet-700 font-semibold">Zoekmatch: </span>
                                                                        {meta.reason}
                                                                        {meta.score != null && Number.isFinite(meta.score) && (
                                                                            <span className="text-violet-600 font-medium tabular-nums"> · {meta.score}%</span>
                                                                        )}
                                                                    </p>
                                                                );
                                                            })()}

                                                            <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-1">
                                                                <div className="scale-90 origin-left relative flex flex-col gap-1 items-start">
                                                                    <StatusBadge status={profile.status} customStatuses={clientStatuses} />
                                                                    {(profile.ai_parsed_data?.available_from || profile.candidate?.available_from) && (
                                                                        <div className="text-[8px] font-black uppercase text-emerald-600 tracking-widest bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100 inline-block mt-1">
                                                                            Beschikbaar: {new Date(profile.ai_parsed_data?.available_from || profile.candidate?.available_from).toLocaleDateString('nl-NL')}
                                                                        </div>
                                                                    )}
                                                                    {['rejected', 'afgewezen', 'archived', 'gearchiveerd', 'blacklisted'].includes((profile.status || '').toLowerCase()) && profile.ai_parsed_data?.rejectionReason && (
                                                                        <div className="text-[8px] font-black uppercase text-[#e51b23] tracking-widest bg-red-50 px-1.5 py-0.5 rounded border border-red-100 inline-block truncate max-w-[150px] mt-1" title={profile.ai_parsed_data.rejectionReason}>
                                                                            {profile.ai_parsed_data.rejectionReason}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    {profile.candidate?.email && <div className="p-1.5 bg-slate-50 rounded-md border border-slate-100"><Mail size={12} className="text-slate-500" /></div>}
                                                                    {profile.candidate?.phone && <div className="p-1.5 bg-slate-50 rounded-md border border-slate-100"><Phone size={12} className="text-slate-500" /></div>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )})()}
                                    </div>
                                    {nlQuery.trim().length > 0 && !isAiSearching && filteredProfiles.length > 0 && (
                                        <div className="px-4 py-3 bg-indigo-50/50 border-t border-indigo-100 flex items-center justify-between shrink-0">
                                            <div className="flex items-center gap-2">
                                                <BrainCircuit className="text-indigo-500" size={16} />
                                                <p className="text-xs font-bold text-indigo-900">Niet de juiste kandidaat gevonden?</p>
                                            </div>
                                            <button 
                                                onClick={() => handleAiAnalysis(nlQuery)}
                                                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-2"
                                            >
                                                Verbreed zoekopdracht met AI
                                            </button>
                                        </div>
                                    )}
                                    <div className="border-t border-slate-200 px-6 py-2.5 flex items-center justify-between bg-white shrink-0">
                                        <span className="text-[11px] font-medium text-slate-400">
                                            {totalCount} kandidaten · pagina {currentPage} van {totalPages || 1}
                                        </span>
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                                disabled={currentPage === 1 || isFetchingPage}
                                                className="px-3 py-1.5 text-[11px] font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 transition-colors"
                                            >
                                                Vorige
                                            </button>
                                            <div className="flex items-center gap-1">
                                                {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => {
                                                    let pageNum = currentPage - 2 + i;
                                                    if (currentPage <= 3) pageNum = i + 1;
                                                    else if (currentPage >= (totalPages || 1) - 2) pageNum = (totalPages || 1) - 4 + i;
                                                    if (pageNum > 0 && pageNum <= (totalPages || 1)) {
                                                        return (
                                                            <button
                                                                key={pageNum}
                                                                onClick={() => setCurrentPage(pageNum)}
                                                                className={`w-7 h-7 flex items-center justify-center text-[11px] font-semibold rounded-lg transition-colors ${currentPage === pageNum ? 'bg-[#E21E26] text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                                                            >
                                                                {pageNum}
                                                            </button>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </div>
                                            <button
                                                onClick={() => setCurrentPage(p => Math.min((totalPages || 1), p + 1))}
                                                disabled={currentPage === (totalPages || 1) || isFetchingPage}
                                                className="px-3 py-1.5 text-[11px] font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 transition-colors"
                                            >
                                                Volgende
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* RIGHT COLUMN: DETAIL */}
                        {selectedProfile && (
                            <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col overflow-hidden animate-slideRight">
                                {/* COMPACT HEADER */}
                                <div className="p-3 md:p-4 border-b border-gray-100 bg-white flex flex-col gap-2 shrink-0">
                                    {/* Prominent Back Button */}
                                    <div className="flex items-center justify-between">
                                        <button
                                            onClick={() => handleSetSelectedProfile(null)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider text-[#e51b23] bg-red-50 border border-red-100 hover:bg-red-100 transition-all shadow-sm active:scale-95"
                                        >
                                            <ChevronLeft size={14} /> Terug
                                        </button>
                                        
                                        {/* Actions Right */}
                                        <div className="flex items-center gap-2 shrink-0">
                                            {!isClientPortal && (
                                                <>
                                                    {isEditing ? (
                                                        <button onClick={handleUpdateProfile} disabled={isSavingEdit} className="p-1.5 px-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-all shadow-sm font-bold text-xs flex items-center gap-1.5" title="Wijzigingen Opslaan">
                                                            {isSavingEdit ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Opslaan
                                                        </button>
                                                    ) : (
                                                        <button onClick={() => setIsEditing(true)} className="p-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-400 transition-colors" title="Bewerk Profiel">
                                                            <Edit3 size={12} />
                                                        </button>
                                                    )}
                                                    
                                                    {!isEditing && (
                                                        <>
                                                            <button onClick={handleRemapOriginalCv} className="p-1.5 bg-slate-50 hover:bg-blue-50 text-slate-400 hover:text-blue-500 rounded-lg transition-colors" title="Herstart CV Mapper">
                                                                <RefreshCw size={12} />
                                                            </button>
                                                            <button onClick={() => setShowDeleteConfirm(true)} className="p-1.5 bg-slate-50 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-lg transition-colors" title="Verwijder Kandidaat">
                                                                <Trash2 size={12} />
                                                            </button>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                            <select
                                                value={selectedProfile.ai_parsed_data?.preferredLanguage || selectedProfile.parsed_cv?.preferredLanguage || 'nl'}
                                                onChange={(e) => {
                                                    const newLang = e.target.value;
                                                    const currentData = selectedProfile.ai_parsed_data || {};
                                                    setSelectedProfile({
                                                        ...selectedProfile,
                                                        ai_parsed_data: { ...currentData, preferredLanguage: newLang }
                                                    });
                                                }}
                                                className="text-[9px] font-bold uppercase tracking-wider bg-slate-50 border border-slate-100 rounded px-2 py-1 text-slate-600 outline-none cursor-pointer"
                                            >
                                                <option value="nl">NL 🇳🇱</option>
                                                <option value="en">EN 🇬🇧</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex items-start justify-between gap-3 md:gap-4">
                                        {/* Avatar & Info */}
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-2xl font-black text-slate-300 shadow-sm shrink-0 overflow-hidden relative group">
                                                {selectedProfile.candidate.photo_url || selectedProfile.candidate.avatar_url ? (
                                                    <img src={selectedProfile.candidate.photo_url || selectedProfile.candidate.avatar_url} className="w-full h-full object-cover" />
                                                ) : selectedProfile.candidate.name?.charAt(0)}
                                                
                                                {isEditing && (
                                                    <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity" title="Upload nieuwe profielfoto">
                                                        <Camera size={16} className="text-white" />
                                                        <input 
                                                            type="file" 
                                                            accept="image/*" 
                                                            className="hidden" 
                                                            onChange={async (e) => {
                                                                const file = e.target.files?.[0];
                                                                if (file && selectedProfile?.candidate) {
                                                                    try {
                                                                        setIsSavingEdit(true);
                                                                        const photoUrl = await supabase_upload_image(file);
                                                                        await supabase_update_candidate_full(selectedProfile.id, selectedProfile.candidate.id, { photo_url: photoUrl }, {});
                                                                        setSelectedProfile({...selectedProfile, candidate: {...selectedProfile.candidate, photo_url: photoUrl}});
                                                                    } catch (err) {
                                                                        console.error(err);
                                                                        alert("Fout bij uploaden foto.");
                                                                    } finally {
                                                                        setIsSavingEdit(false);
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </label>
                                                )}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-3 mb-1 w-full relative">
                                                    {isEditing ? (
                                                        <input 
                                                            value={editForm?.candidate?.name || ''} 
                                                            onChange={(e) => setEditForm((prev: any) => ({ ...prev, candidate: { ...(prev?.candidate || {}), name: e.target.value } }))}
                                                            className="text-lg md:text-xl font-black text-slate-900 tracking-tight truncate border-b border-dashed border-red-400 outline-none w-full max-w-[200px] bg-slate-50" 
                                                            placeholder="Naam"
                                                        />
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <h2 className="text-lg md:text-xl font-black text-slate-900 tracking-tight truncate">{selectedProfile?.candidate?.name}</h2>
                                                            {selectedProfile?.candidate?.candidate_number && (
                                                                <span className="text-[10px] font-black text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200 uppercase tracking-widest">
                                                                    #{selectedProfile.candidate.candidate_number}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    <select
                                                        value={selectedProfile.status || 'Lead'}
                                                        onChange={async (e) => {
                                                            const newStatus = e.target.value;
                                                            const previousStatus = selectedProfile.status || 'Lead';
                                                            console.log('[TalentPool] Status changed (detail)', {
                                                                profileId: selectedProfile.id,
                                                                candidateId: selectedProfile.candidate?.id,
                                                                previousStatus,
                                                                newStatus,
                                                            });
                                                            setSelectedProfile({ ...selectedProfile, status: newStatus });
                                                            try {
                                                                await supabase_update_candidate_full(selectedProfile.id, selectedProfile.candidate.id, {}, { status: newStatus });
                                                                void triggerCandidateStatusChanged({
                                                                    candidateId: selectedProfile.candidate.id,
                                                                    profileId: selectedProfile.id,
                                                                    clientId: user?.clientId,
                                                                    previousStatus,
                                                                    newStatus,
                                                                    source: 'talentpool_detail',
                                                                    user_id: user?.id,
                                                                    candidateName: selectedProfile.candidate?.name,
                                                                });
                                                            } catch (error) {
                                                                console.error("Failed to update status", error);
                                                            }
                                                        }}
                                                        className={`text-[9px] md:text-[10px] font-black uppercase tracking-widest pl-2 pr-6 py-1 rounded-full border outline-none cursor-pointer appearance-none ${DEFAULT_STATUSES.find(s => s.label === selectedProfile.status || s.id === selectedProfile.status?.toLowerCase())?.color || 'bg-gray-50 text-gray-500 border-gray-100'}`}
                                                        style={{
                                                            backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22currentColor%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")`,
                                                            backgroundRepeat: 'no-repeat',
                                                            backgroundPosition: 'right .5rem top 50%',
                                                            backgroundSize: '.5rem auto',
                                                        }}
                                                    >
                                                        {DEFAULT_STATUSES.map(stat => <option key={stat.id} value={stat.label}>{stat.label}</option>)}
                                                    </select>

                                                    <span className="text-[9px] md:text-[10px] font-black tracking-widest px-3 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-500 uppercase flex items-center gap-1">
                                                        🗓️ Contact: {selectedProfile.updated_at ? new Date(selectedProfile.updated_at).toLocaleDateString() : (selectedProfile.candidate?.updated_at ? new Date(selectedProfile.candidate.updated_at).toLocaleDateString() : 'Onbekend')}
                                                    </span>
                                                    
                                                    {isEditing ? (
                                                        <select
                                                            value={editForm.candidate?.source || ''}
                                                            onChange={(e) => setEditForm((prev: any) => ({ ...prev, candidate: { ...prev.candidate, source: e.target.value } }))}
                                                            className="text-[9px] md:text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border outline-none cursor-pointer bg-slate-100 text-slate-600 border-slate-200"
                                                        >
                                                            <option value="" disabled>Bron</option>
                                                            {candidateSources.map(src => <option key={src} value={src}>{src}</option>)}
                                                        </select>
                                                    ) : selectedProfile.candidate?.source && (
                                                        <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border bg-slate-50 text-slate-500 border-slate-200 truncate max-w-[120px]">
                                                            {selectedProfile.candidate.source}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-500 font-bold truncate mb-2">{selectedProfile.candidate.role}</p>

                                                {/* Contact Actions */}
                                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                                    {selectedProfile.candidate.phone && (
                                                        <>
                                                            <button onClick={() => {
                                                                if (onStartCall) onStartCall(selectedProfile);
                                                                else setShowCallModal(true);
                                                            }} className="flex items-center gap-2 px-4 py-2 bg-[#F1FDF7] text-[#007B55] hover:bg-[#E2F8EE] border border-[#BDE8D3] rounded-full text-[10px] font-black uppercase tracking-widest transition-all shadow-sm active:scale-95">
                                                                <Phone size={14} /> BELLEN
                                                            </button>
                                                            <button onClick={() => setShowWhatsAppOptionsModal(true)} className="flex items-center gap-2 px-4 py-2 bg-[#F1FDF7] text-[#007B55] hover:bg-[#E2F8EE] border border-[#BDE8D3] rounded-full text-[10px] font-black uppercase tracking-widest transition-all shadow-sm active:scale-95" title="Start WhatsApp">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg> WHATSAPP
                                                            </button>
                                                        </>
                                                    )}
                                                    {selectedProfile.candidate.email && (
                                                        <button onClick={() => setShowAiEmailModal(true)} className="flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] text-[#334155] hover:bg-[#F1F5F9] border border-[#E2E8F0] rounded-full text-[10px] font-black uppercase tracking-widest transition-all shadow-sm active:scale-95">
                                                            <Mail size={14} /> MAILEN
                                                        </button>
                                                    )}
                                                </div>
                                                
                                                {/* Edit CV Actions */}
                                                {isEditing && (
                                                    <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-dashed border-red-200">
                                                        <button onClick={handleRemapOriginalCv} className="flex items-center justify-center gap-1.5 px-4 py-2 bg-gradient-to-br from-purple-50 to-fuchsia-50 text-purple-600 hover:from-purple-100 hover:to-fuchsia-100 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border border-purple-100 shadow-sm active:scale-95 group">
                                                            <RefreshCw size={14} className="group-hover:rotate-180 transition-transform duration-500" /> HER-LEES ACTIEF DOCUMENT
                                                        </button>
                                                        <label className="flex items-center justify-center gap-1.5 px-4 py-2 bg-gradient-to-br from-slate-50 to-white text-slate-600 hover:from-slate-100 hover:to-slate-50 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border border-slate-200 shadow-sm active:scale-95 cursor-pointer">
                                                            <Upload size={14} /> NIEUW DOCUMENT KOPPELEN
                                                            <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleUploadReplacementCv} />
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Shortlist journey — volle breedte paneel; klik opent Shortlist-tab */}
                                <div className="w-full shrink-0">
                                    <CandidatePipelineStrip
                                        applications={candidateApplications}
                                        interviews={candidateInterviews}
                                        placements={candidatePlacements}
                                        stageHistory={candidateStageHistory}
                                        scope={shortlistScope}
                                        onOpenShortlistTab={(step: PipelineStepId) => {
                                            setActiveTab('shortlist');
                                            setShortlistFocusPhase(pipelineStepToShortlistPhase(step));
                                            setShortlistFocusKey((k) => k + 1);
                                        }}
                                    />
                                </div>

                                {/* Modern Tabs */}
                                <div className="flex flex-wrap border-b border-slate-100 bg-white px-2 md:px-4 gap-x-1 gap-y-1">
                                    {['profile', 'cv', 'match', 'notes', 'comm', 'shortlist', 'flows', 'automations'].map(tab => (
                                        <button
                                            key={tab}
                                            onClick={() => setActiveTab(tab as any)}
                                            className={`px-3 py-3 text-[9px] md:text-[10px] font-black uppercase tracking-widest border-b-2 transition-all relative top-[1px] whitespace-nowrap ${activeTab === tab ? 'border-[#e51b23] text-[#e51b23]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                                        >
                                            {tab === 'notes' ? `Notities (${communications.filter(c => c.type === 'note').length})` : tab === 'comm' ? `Communicatie (${communications.filter(c => c.type !== 'note').length})` : tab === 'match' ? 'Matching' : tab === 'cv' ? `Documenten (${candidateDocs.filter(d => d.candidate_id === selectedProfile?.candidate?.id).length + cvVersions.filter(v => v.candidate_id === selectedProfile?.candidate?.id).length + (selectedProfile?.parsed_cv?.cv_url || selectedProfile?.ai_parsed_data?.cv_url ? 1 : 0)})` : tab === 'shortlist' ? `Shortlist (${candidateApplications.filter((a: any) => a.status !== 'Draft_CV').length})` : tab === 'flows' ? 'Vacature Flows' : tab === 'automations' ? 'Automations' : 'Profiel'}
                                        </button>
                                    ))}
                                </div>

                                {/* Tab Content */}
                                <div className="flex-1 overflow-y-auto p-2 md:p-4 bg-[#F8FAFC] custom-scrollbar flex flex-col relative min-h-0">
                                    {activeTab === 'cv' && (
                                        <div className="space-y-6 animate-fadeIn">
                                            <div className="flex justify-between items-center">
                                                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">Opgeslagen Documenten & CV's</h3>
                                                <div className="flex gap-2">
                                                    <label className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-md text-[10px] font-black uppercase tracking-widest hover:border-slate-400 hover:text-slate-800 transition-all flex items-center gap-2 cursor-pointer">
                                                        <Upload size={12} /> Upload
                                                        <input
                                                            type="file"
                                                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt"
                                                            className="hidden"
                                                            onChange={handleDocumentUpload}
                                                        />
                                                    </label>

                                                    {oneDriveUrl && (
                                                        <a
                                                            href={oneDriveUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="px-3 py-1.5 bg-red- text-red- border border-red- rounded-md text-[10px] font-black uppercase tracking-widest hover:bg-red- transition-all flex items-center gap-2 shadow-sm"
                                                        >
                                                            <Cloud size={12} className="text-red-" /> M365 Opslag
                                                        </a>
                                                    )}

                                                    <button onClick={() => {
                                        const prefillData = mapCandidateToCVData(selectedProfile);
                                        onNavigate('documentbuilder', undefined, undefined, {
                                            title: `CV - ${selectedProfile.candidate.name}`,
                                            content: JSON.stringify({
                                                _isDocumentBuilder: true,
                                                resumeData: prefillData
                                            }),
                                            candidateId: selectedProfile.candidate.id
                                        });
                                    }} className="px-3 py-1.5 bg-[#185ABD] text-white rounded-md text-[10px] font-black uppercase tracking-widest hover:bg-[#144799] transition-all flex items-center gap-2"><Plus size={12} /> Nieuw CV Template</button>
                                                </div>
                                            </div>

                                            {candidateDocs.filter(doc => doc.candidate_id === selectedProfile?.candidate?.id).length === 0 && cvVersions.filter(version => version.candidate_id === selectedProfile?.candidate?.id).length === 0 && !(selectedProfile.parsed_cv?.cv_url || selectedProfile.ai_parsed_data?.cv_url) && (
                                                <div className="text-center py-10 bg-white rounded-xl border border-dashed border-slate-200">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Nog geen documenten.</p>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-1 gap-2">
                                                {/* Virtual Item for Original CV */}
                                                {(selectedProfile.parsed_cv?.cv_url || selectedProfile.ai_parsed_data?.cv_url) && (
                                                    <a
                                                        href={selectedProfile.parsed_cv?.cv_url || selectedProfile.ai_parsed_data?.cv_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm hover:border-red- cursor-pointer flex items-center justify-between group"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="p-2 bg-slate-900 text-white rounded">
                                                                <FileText size={16} />
                                                            </div>
                                                            <div>
                                                                <p className="text-xs font-bold text-slate-900">Origineel CV</p>
                                                                <p className="text-[10px] text-slate-500">Geüpload bij sollicitatie</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 px-2 py-0.5 rounded">PDF</span>
                                                            <ChevronRight size={14} className="text-slate-300 group-hover:text-red-0" />
                                                        </div>
                                                    </a>
                                                )}

                                                {/* CV Versions */}
                                                {cvVersions.filter(version => version.candidate_id === selectedProfile?.candidate?.id).map(version => (
                                                    <div key={version.id} onClick={() => handleOpenVersion(version)} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm hover:border-purple-400 cursor-pointer flex items-center justify-between group">
                                                        <div className="flex items-center gap-3">
                                                            <div className="p-2 bg-purple-50 text-purple-600 rounded">
                                                                <LayoutTemplate size={16} />
                                                            </div>
                                                            <div>
                                                                <p className="text-xs font-bold text-slate-900">{version.cv_summary || 'Naamloze Versie'}</p>
                                                                <p className="text-[10px] text-slate-500">CV Versie • {new Date(version.created_at).toLocaleDateString()}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={(e) => handleDeleteVersion(e, version.id)}
                                                                className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="Verwijder versie"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                            <ChevronRight size={14} className="text-slate-300 group-hover:text-red-0" />
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Text Documents */}
                                                {candidateDocs.filter(doc => doc.candidate_id === selectedProfile?.candidate?.id).map(doc => (
                                                    <div key={doc.id} onClick={() => handleOpenDoc(doc)} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm hover:border-red- cursor-pointer flex items-center justify-between group">
                                                        <div className="flex items-center gap-3">
                                                            <div className="p-2 bg-red- text-red- rounded">
                                                                <FileText size={16} />
                                                            </div>
                                                            <div>
                                                                <p className="text-xs font-bold text-slate-900">{doc.title}</p>
                                                                <p className="text-[10px] text-slate-500">Document • {new Date(doc.updated_at).toLocaleDateString()}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={(e) => handleDeleteDoc(e, doc.id)}
                                                                className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="Verwijder document"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                            <ChevronRight size={14} className="text-slate-300 group-hover:text-red-0" />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {activeTab === 'profile' && (
                                        <div className="space-y-8 animate-fadeIn">
                                            {/* Profile Details */}
                                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                                                {/* Profiel / Motivatie */}
                                                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm col-span-1 xl:col-span-2 space-y-3">
                                                    <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                                        <Quote size={16} /> Profiel/Motivatie
                                                    </h4>
                                                    {isEditing ? (
                                                        <textarea
                                                            value={editForm.parsed_cv?.motivation || ''}
                                                            onChange={(e) =>
                                                                setEditForm({
                                                                    ...editForm,
                                                                    parsed_cv: { ...(editForm.parsed_cv || {}), motivation: e.target.value },
                                                                })
                                                            }
                                                            className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm leading-relaxed outline-none focus:border-red-0 transition-colors min-h-[120px]"
                                                            placeholder="Korte introductie / motivatie..."
                                                        />
                                                    ) : (
                                                        <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                                                            {talentPoolProfileSummary(selectedProfile) || '—'}
                                                        </div>
                                                    )}
                                                </div>
                                                {/* New Extended Contact Info Block */}
                                                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm col-span-1 xl:col-span-2 space-y-4">
                                                    <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2"><User size={16} /> Contact & Adresgegevens</h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                        <div className="col-span-1 md:col-span-2">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Volledige Naam</label>
                                                            {isEditing ? <input value={editForm.candidate?.name || ''} onChange={(e) => setEditForm({ ...editForm, candidate: { ...editForm.candidate, name: e.target.value } })} className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-red-0 outline-none pb-0.5" /> : <div className="text-xs font-medium text-slate-800">{selectedProfile.candidate?.name || '-'}</div>}
                                                        </div>
                                                        <div className="col-span-1 md:col-span-2">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Adres</label>
                                                            {isEditing ? <input value={editForm.parsed_cv?.address || ''} onChange={(e) => setEditForm({ ...editForm, parsed_cv: { ...editForm.parsed_cv, address: e.target.value } })} className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-red-0 outline-none pb-0.5" /> : <div className="text-xs font-medium text-slate-800">{selectedProfile.parsed_cv?.address || selectedProfile.ai_parsed_data?.address || '-'}</div>}
                                                        </div>
                                                        <div>
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Postcode</label>
                                                            {isEditing ? <input value={editForm.parsed_cv?.zipcode || ''} onChange={(e) => setEditForm({ ...editForm, parsed_cv: { ...editForm.parsed_cv, zipcode: e.target.value } })} className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-red-0 outline-none pb-0.5" /> : <div className="text-xs font-medium text-slate-800">{selectedProfile.parsed_cv?.zipcode || selectedProfile.ai_parsed_data?.zipcode || '-'}</div>}
                                                        </div>
                                                        <div>
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Plaats</label>
                                                            {isEditing ? <input value={editForm.parsed_cv?.city || ''} onChange={(e) => setEditForm({ ...editForm, parsed_cv: { ...editForm.parsed_cv, city: e.target.value } })} className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-red-0 outline-none pb-0.5" /> : <div className="text-xs font-medium text-slate-800">{selectedProfile.parsed_cv?.city || selectedProfile.ai_parsed_data?.city || '-'}</div>}
                                                        </div>
                                                        <div className="col-span-1 md:col-span-2">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Land</label>
                                                            {isEditing ? <input value={editForm.parsed_cv?.country || ''} onChange={(e) => setEditForm({ ...editForm, parsed_cv: { ...editForm.parsed_cv, country: e.target.value } })} className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-red-0 outline-none pb-0.5" /> : <div className="text-xs font-medium text-slate-800">{selectedProfile.parsed_cv?.country || selectedProfile.ai_parsed_data?.country || '-'}</div>}
                                                        </div>
                                                        <div className="col-span-1 md:col-span-2">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                                                <Calendar size={12} className="text-slate-300 shrink-0" />
                                                                Geboortedatum
                                                            </label>
                                                            <p className="text-[9px] text-slate-400 font-medium mt-0.5 mb-1">
                                                                Voor verjaardag (o.a. engagement) — automatisch uit CV-parse, of handmatig invullen.
                                                            </p>
                                                            {isEditing ? (
                                                                <input
                                                                    type="date"
                                                                    value={normalizeDateOfBirthToInputValue(
                                                                        editForm.ai_parsed_data?.dateOfBirth ||
                                                                            editForm.ai_parsed_data?.geboortedatum
                                                                    )}
                                                                    onChange={(e) => {
                                                                        const v = e.target.value;
                                                                        setEditForm({
                                                                            ...editForm,
                                                                            ai_parsed_data: {
                                                                                ...(editForm.ai_parsed_data || {}),
                                                                                dateOfBirth: v,
                                                                                geboortedatum: v,
                                                                            },
                                                                        });
                                                                    }}
                                                                    className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-red-0 outline-none pb-0.5"
                                                                />
                                                            ) : (
                                                                <div className="space-y-0.5">
                                                                    <div className="text-xs font-medium text-slate-800">
                                                                        {(() => {
                                                                            const iso = normalizeDateOfBirthToInputValue(
                                                                                extractDateOfBirthFromProfile(selectedProfile)
                                                                            );
                                                                            if (!iso) return '—';
                                                                            const [Y, M, D] = iso.split('-').map(Number);
                                                                            return new Date(Y, M - 1, D).toLocaleDateString('nl-NL', {
                                                                                day: 'numeric',
                                                                                month: 'long',
                                                                                year: 'numeric',
                                                                            });
                                                                        })()}
                                                                    </div>
                                                                    {(() => {
                                                                        const iso = normalizeDateOfBirthToInputValue(
                                                                            extractDateOfBirthFromProfile(selectedProfile)
                                                                        );
                                                                        const hint = iso ? birthdayHintFromIsoYmd(iso) : null;
                                                                        return hint ? (
                                                                            <p className="text-[10px] font-bold text-amber-800/90">{hint}</p>
                                                                        ) : null;
                                                                    })()}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="col-span-1 md:col-span-2">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">LinkedIn Profiel</label>
                                                            {isEditing ? <input value={editForm.candidate?.linkedin_url || ''} onChange={(e) => setEditForm({ ...editForm, candidate: { ...editForm.candidate, linkedin_url: e.target.value } })} className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-red-0 outline-none pb-0.5" /> : <div className="text-xs font-medium text-slate-800 truncate">{selectedProfile.candidate?.linkedin_url ? <a href={selectedProfile.candidate.linkedin_url.startsWith('http') ? selectedProfile.candidate.linkedin_url : `https://${selectedProfile.candidate.linkedin_url}`} target="_blank" className="text-red-0 hover:underline">{selectedProfile.candidate.linkedin_url}</a> : '-'}</div>}
                                                        </div>
                                                        <div className="col-span-1 md:col-span-2">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Telefoon 1</label>
                                                            {isEditing ? <input value={editForm.candidate?.phone || ''} onChange={(e) => setEditForm({ ...editForm, candidate: { ...editForm.candidate, phone: e.target.value } })} className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-red-0 outline-none pb-0.5" /> : <div className="text-xs font-medium text-slate-800">{selectedProfile.candidate?.phone || '-'}</div>}
                                                        </div>
                                                        <div className="col-span-1 md:col-span-2">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Beschikbaarheid (Datum)</label>
                                                            {isEditing ? <input type="date" value={editForm.ai_parsed_data?.available_from ? editForm.ai_parsed_data.available_from.split('T')[0] : ''} onChange={(e) => setEditForm({ ...editForm, ai_parsed_data: { ...editForm.ai_parsed_data, available_from: e.target.value } })} className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-red-0 outline-none pb-0.5" /> : <div className="text-xs font-medium text-slate-800">{selectedProfile.ai_parsed_data?.available_from ? new Date(selectedProfile.ai_parsed_data.available_from).toLocaleDateString('nl-NL') : '-'}</div>}
                                                        </div>
                                                        <div className="col-span-1 md:col-span-2">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">E-mailadres 1</label>
                                                            {isEditing ? <input value={editForm.candidate?.email || ''} onChange={(e) => setEditForm({ ...editForm, candidate: { ...editForm.candidate, email: e.target.value } })} className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-red-0 outline-none pb-0.5" /> : <div className="text-xs font-medium text-slate-800">{selectedProfile.candidate?.email || '-'}</div>}
                                                        </div>
                                                        <div className="col-span-1 md:col-span-2">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Voorkeur Dienstverband</label>
                                                            {isEditing ? (
                                                                <div>
                                                                    <select value={['Vast', 'ZZP', 'Contract', 'Vast of ZZP', 'Vast of Contract'].includes(editForm.ai_parsed_data?.employment_preference || '') ? (editForm.ai_parsed_data?.employment_preference || '') : (editForm.ai_parsed_data?.employment_preference ? 'Overig' : '')} onChange={e => {
                                                                        const val = e.target.value;
                                                                        if (val !== 'Overig') {
                                                                            setEditForm({ ...editForm, ai_parsed_data: { ...editForm.ai_parsed_data, employment_preference: val } });
                                                                        } else {
                                                                            setEditForm({ ...editForm, ai_parsed_data: { ...editForm.ai_parsed_data, employment_preference: '' } });
                                                                        }
                                                                    }} className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-slate-300 outline-none pb-0.5 mb-1 bg-transparent">
                                                                        <option value="">-- Kies of typ zelf --</option>
                                                                        <option value="Vast">Vast</option>
                                                                        <option value="ZZP">ZZP</option>
                                                                        <option value="Contract">Contract</option>
                                                                        <option value="Vast of ZZP">Vast of ZZP</option>
                                                                        <option value="Vast of Contract">Vast of Contract</option>
                                                                        <option value="Overig">Anders (zelf invullen)...</option>
                                                                    </select>
                                                                    {(!['Vast', 'ZZP', 'Contract', 'Vast of ZZP', 'Vast of Contract'].includes(editForm.ai_parsed_data?.employment_preference || '') && editForm.ai_parsed_data?.employment_preference !== '') && (
                                                                        <input placeholder="Typ specifiek dienstverband..." value={editForm.ai_parsed_data?.employment_preference || ''} onChange={(e) => setEditForm({ ...editForm, ai_parsed_data: { ...editForm.ai_parsed_data, employment_preference: e.target.value } })} className="w-full text-xs font-bold text-slate-800 border-b border-dashed border-red-0 outline-none pb-0.5" />
                                                                    )}
                                                                </div>
                                                            ) : <div className="text-xs font-medium text-slate-800">{Array.isArray(selectedProfile.ai_parsed_data?.employment_preference) ? selectedProfile.ai_parsed_data.employment_preference.join(', ') : (selectedProfile.ai_parsed_data?.employment_preference || '-')}</div>}
                                                        </div>
                                                        <div className="col-span-1 md:col-span-4 lg:col-span-4 mt-2">
                                                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Extra Informatie</label>
                                                            {isEditing ? <textarea value={editForm.parsed_cv?.extra_information || ''} onChange={(e) => setEditForm({ ...editForm, parsed_cv: { ...editForm.parsed_cv, extra_information: e.target.value } })} className="w-full text-xs font-bold text-slate-800 border rounded border-dashed border-red-0 outline-none p-2 min-h-[60px]" /> : <div className="text-xs font-medium text-slate-800 whitespace-pre-wrap">{selectedProfile.parsed_cv?.extra_information || selectedProfile.ai_parsed_data?.extra_information || '-'}</div>}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Use DB parsed data if available */}
                                                {renderTagSection("Skills & Tools", isEditing ? editForm.parsed_cv?.tags : (selectedProfile.parsed_cv?.tags && selectedProfile.parsed_cv.tags.length > 0 ? selectedProfile.parsed_cv.tags : (selectedProfile.ai_parsed_data?.tags && selectedProfile.ai_parsed_data.tags.length > 0 ? selectedProfile.ai_parsed_data.tags : (Array.isArray(selectedProfile.candidate?.skill_set) ? selectedProfile.candidate.skill_set : (typeof selectedProfile.candidate?.skill_set === 'string' ? selectedProfile.candidate.skill_set.split(',') : [])).map((s: any) => typeof s === 'string' ? s.trim() : String(s)).filter(Boolean))), 'tags', <Wrench size={16} className="text-red-0" />)}
                                                {renderEditableList("Werkervaring", isEditing ? editForm.parsed_cv?.timeline : (selectedProfile.parsed_cv?.timeline || selectedProfile.ai_parsed_data?.timeline || []), 'timeline', <Briefcase size={16} className="text-slate-800" />)}
                                                {renderEditableList("Opleidingen", isEditing ? editForm.parsed_cv?.education : (selectedProfile.parsed_cv?.education || selectedProfile.ai_parsed_data?.education), 'education', <GraduationCap size={16} className="text-slate-800" />)}
                                                {renderEditableList("Cursussen", isEditing ? editForm.parsed_cv?.courses : (selectedProfile.parsed_cv?.courses || selectedProfile.ai_parsed_data?.courses), 'courses', <FileCheck size={16} className="text-slate-800" />)}
                                                {renderEditableList("Referenties", isEditing ? editForm.parsed_cv?.references : (selectedProfile.parsed_cv?.references || selectedProfile.ai_parsed_data?.references), 'references', <Users size={16} className="text-slate-800" />)}
                                                {renderEditableList("Talen", isEditing ? editForm.parsed_cv?.languages : (selectedProfile.parsed_cv?.languages || selectedProfile.ai_parsed_data?.languages), 'languages', <MessageSquare size={16} className="text-slate-800" />)}
                                                {/* Hobbies / Others */}
                                                {renderTagSection("Hobby's", isEditing ? editForm.parsed_cv?.hobbies : (selectedProfile.parsed_cv?.hobbies || selectedProfile.ai_parsed_data?.hobbies), 'hobbies', <Activity size={16} className="text-purple-500" />)}

                                                {/* Motivation / Other Text removed to Ruwe CV Data tab */}
                                            </div>
                                        </div>
                                    )}
                                    {activeTab === 'match' && (
                                        <div className="space-y-6 animate-fadeIn">
                                            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
                                                <div>
                                                    <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-1">Matching</h3>
                                                    <p className="text-xs text-slate-500 font-medium">Vergelijk automatisch met openstaande vacatures.</p>
                                                </div>
                                                <button
                                                    onClick={handleMatch}
                                                    disabled={isMatching}
                                                    className="px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black disabled:opacity-50 flex items-center gap-2 shadow-lg active:scale-95 transition-all w-[320px] justify-center"
                                                >
                                                    {isMatching ? (
                                                        <><Loader2 size={14} className="animate-spin" /> {matchStatusText || 'Bezig...'}</>
                                                    ) : (
                                                        <><BrainCircuit size={14} /> {matchStatusText || 'Start Matching'}</>
                                                    )}
                                                </button>
                                            </div>

                                            {Array.isArray(matchResult) && matchResult.filter((r: any) => r.score >= 50).sort((a,b) => b.score - a.score).slice(0, visibleMatchCount).map((result: any, idx: number) => {
                                                const matchedVac = vacancies.find(v => v.id === result.vacancyId);
                                                const scoreColor = result.score >= 75 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700';
                                                return (
                                                    <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm animate-scaleIn mb-4">
                                                        <div className="flex items-start gap-4 mb-4 relative">
                                                            <div className={`w-20 h-20 rounded-2xl flex flex-col items-center justify-center shrink-0 border-2 ${scoreColor}`}>
                                                                <span className="text-2xl font-black leading-none">{result.score}</span>
                                                                <span className="text-[9px] font-black uppercase tracking-widest opacity-70">Score</span>
                                                            </div>
                                                            <div className="flex-1 pr-10">
                                                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Match Analyse</p>
                                                                <h4 className="text-base font-black text-slate-900 leading-tight">{matchedVac ? matchedVac.title : 'Vacature'}</h4>
                                                                <p className="text-xs text-slate-500 font-medium leading-relaxed mt-1">{result.reason}</p>
                                                            </div>
                                                            {matchedVac && (
                                                                <button onClick={() => window.open(`/?vacancy=${matchedVac.id}`, '_blank')} className="absolute top-0 right-0 p-2 bg-slate-50 rounded-xl text-slate-400 hover:bg-[#e51b23] hover:text-white transition-colors" title="Bekijk Vacature">
                                                                    <Search size={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            {result.pros && result.pros.length > 0 && (
                                                                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                                                                    <h5 className="text-[10px] font-black uppercase tracking-widest text-emerald-700 mb-2 flex items-center gap-2"><CheckCircle2 size={12} /> Pluspunten</h5>
                                                                    <ul className="space-y-1">
                                                                        {result.pros.map((p: string, i: number) => (
                                                                            <li key={i} className="text-xs text-emerald-800 font-medium">• {p}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                            {result.cons && result.cons.length > 0 && (
                                                                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                                                                    <h5 className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-2 flex items-center gap-2"><AlertCircle size={12} /> Aandachtspunten</h5>
                                                                    <ul className="space-y-1">
                                                                        {result.cons.map((c: string, i: number) => (
                                                                            <li key={i} className="text-xs text-amber-800 font-medium">• {c}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {Array.isArray(matchResult) && matchResult.filter((r: any) => r.score >= 50).length > visibleMatchCount && (
                                                <div className="flex justify-center mt-4 mb-4">
                                                    <button onClick={() => setVisibleMatchCount(prev => prev + 10)} className="px-6 py-2.5 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-indigo-600 rounded-xl text-xs font-black uppercase tracking-widest transition-colors flex items-center gap-2 shadow-sm">
                                                        <Plus size={14} /> Toon meer vacatures
                                                    </button>
                                                </div>
                                            )}
                                            {Array.isArray(matchResult) && matchResult.filter((r: any) => r.score >= 50).length === 0 && (
                                                <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-slate-200">
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Geen relevante matches gevonden (score &lt; 50)</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {activeTab === 'notes' && (
                                        <div className="h-full flex flex-col animate-fadeIn">
                                            <CandidateNotes candidateId={selectedProfile.candidate.id} currentUser={user} />
                                        </div>
                                    )}
                                    {activeTab === 'comm' && (
                                        <div className="flex-1 flex flex-col bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden animate-fadeIn relative min-h-0 h-full">
                                            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50 custom-scrollbar relative" ref={commScrollRef}>
                                                {communications.map((comm) => {
                                                    const displayContent = comm.content.split('---RAW_TRANSCRIPT---')[0];
                                                    const isCallType = comm.type === 'call';
                                                    return (
                                                        <div key={comm.id} className={`flex ${comm.user_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                                                            <div
                                                                onClick={() => {
                                                                    if (isCallType && onEditCallNote) {
                                                                        onEditCallNote(selectedProfile, comm.id, comm.content);
                                                                    }
                                                                }}
                                                                className={`max-w-[80%] p-4 rounded-2xl text-sm font-medium ${isCallType ? 'cursor-pointer hover:ring-2 hover:ring-red- transition-all' : ''} ${comm.user_id === user?.id
                                                                    ? 'bg-slate-900 text-white rounded-br-none'
                                                                    : 'bg-white border border-slate-200 text-slate-700 rounded-bl-none shadow-sm'
                                                                    }`}>
                                                                <div className="flex items-center gap-2 mb-1 opacity-80 justify-between">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-[10px] font-black uppercase tracking-wider">
                                                                            {comm.type} {comm.profiles?.name ? `• ${comm.profiles.name}` : ''}
                                                                        </span>
                                                                        <span className="text-[10px]">• {new Date(comm.created_at).toLocaleString()}</span>
                                                                    </div>
                                                                    {isCallType && <Edit3 size={12} className="opacity-50" />}
                                                                </div>
                                                                <div className="whitespace-pre-wrap">{displayContent}</div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {communications.length === 0 && (
                                                    <div className="text-center text-slate-400 text-xs font-bold uppercase tracking-widest py-10">Nog geen notities of berichten.</div>
                                                )}
                                            </div>
                                            <div className="p-4 bg-white border-t border-slate-100">
                                                <div className="flex justify-between items-center mb-3">
                                                    <div className="flex gap-2">
                                                        <div className="px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border transition-all bg-slate-900 text-white border-slate-900">
                                                            NOTE
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2 items-center">
                                                        <button onClick={() => {
                                                            if (onStartCall) onStartCall(selectedProfile);
                                                        }} className="flex items-center gap-2 bg-green-50 text-green-700 hover:bg-green-100 transition-colors px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-green-200">
                                                            <Phone size={12} /> Bellen (Bird)
                                                        </button>
                                                        {selectedProfile?.candidate?.email && (
                                                            <button onClick={() => setShowAiEmailModal(true)} className="flex items-center gap-2 bg-red- text-red- hover:bg-red- transition-colors px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-red-">
                                                                <Mail size={12} /> Mailen
                                                            </button>
                                                        )}
                                                        {selectedProfile?.candidate?.phone && (
                                                            <button onClick={() => setShowWhatsAppOptionsModal(true)} className="flex items-center gap-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-emerald-200 shadow-sm active:scale-95">
                                                                <MessageSquare size={12} className="text-emerald-500" /> Appen
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="relative">
                                                    <textarea
                                                        value={newCommText}
                                                        onChange={(e) => setNewCommText(e.target.value)}
                                                        placeholder="Typ een notitie of bericht..."
                                                        className="w-full p-4 bg-slate-50 border-0 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-slate-200 resize-none h-24"
                                                    />
                                                    <button
                                                        onClick={handleSendComm}
                                                        disabled={isSendingComm || !newCommText.trim()}
                                                        className="absolute bottom-3 right-3 p-2 bg-slate-900 text-white rounded-lg hover:bg-black disabled:opacity-50 transition-all shadow-md active:scale-95"
                                                    >
                                                        {isSendingComm ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {activeTab === 'shortlist' && (
                                        <CandidateShortlistPhasesPanel
                                            applications={candidateApplications}
                                            interviews={candidateInterviews}
                                            placements={candidatePlacements}
                                            stageHistory={candidateStageHistory}
                                            scope={shortlistScope}
                                            onScopeChange={setShortlistScope}
                                            onOpenVacancy={(vacancyId) => onNavigate('vacancies', vacancyId)}
                                            isClientPortal={isClientPortal}
                                            onApplicationStatusClick={openShortlistMoveForApplication}
                                            onApplicationDeleteClick={requestShortlistApplicationDelete}
                                            onPlacementDeleteClick={requestShortlistPlacementDelete}
                                            focusPhase={shortlistFocusPhase}
                                            focusPhaseKey={shortlistFocusKey}
                                        />
                                    )}
                                    {activeTab === 'flows' && (
                                        <div className="space-y-4 animate-fadeIn">
                                            <div className="flex justify-between items-center bg-slate-50 p-4 rounded-xl border border-slate-100 mb-6">
                                                <div>
                                                    <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">Actieve Vacature Flows</h3>
                                                    <p className="text-[10px] text-slate-500 font-medium tracking-wide mt-1">Bekijk en beheer de gekoppelde vacatures voor deze kandidaat.</p>
                                                </div>
                                                <div className="relative">
                                                    <button 
                                                        onClick={() => setShowVacancySelector(!showVacancySelector)}
                                                        className="px-4 py-2 bg-[#e51b23] text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-red-700 transition-all flex items-center gap-2 shadow-sm active:scale-95"
                                                    >
                                                        <Briefcase size={14} /> {showVacancySelector ? 'Annuleren' : '+ Vacature koppelen'}
                                                    </button>
                                                    
                                                    {/* Vacancy Selection Modal (Right-Slide-Out) */}
                                                    <div className={`fixed inset-y-0 right-0 w-[400px] bg-white shadow-2xl z-[999] transform transition-transform duration-300 flex flex-col ${showVacancySelector ? 'translate-x-0' : 'translate-x-full'}`}>
                                                        <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                                                            <h3 className="font-black text-slate-800 uppercase flex items-center gap-2"><Briefcase size={16} className="text-red-" /> Selecteer Vacature</h3>
                                                            <button onClick={() => setShowVacancySelector(false)} className="text-slate-400 hover:text-slate-700 bg-white shadow-sm p-1 rounded-md border border-slate-200 transition-colors"><X size={16} /></button>
                                                        </div>
                                                        <div className="p-4 border-b border-slate-200 bg-white">
                                                            <div className="relative">
                                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                                                <input 
                                                                    type="text" 
                                                                    placeholder="Zoek vacature op titel, klant..." 
                                                                    className="w-full pl-9 pr-3 py-2 bg-slate-100 border-none rounded-lg text-sm font-medium focus:ring-2 focus:ring-red- outline-none transition-shadow"
                                                                    value={searchVacancy}
                                                                    onChange={e => setSearchVacancy(e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar bg-slate-50">
                                                            {vacancies.filter((v: any) => {
                                                                const cl = Array.isArray(v.clients) ? v.clients[0] : (v.clients || (Array.isArray(v.client) ? v.client[0] : v.client));
                                                                const cName = cl?.name || v.data?.organizationText || '';
                                                                return !searchVacancy || (v.title || v.data?.title || '').toLowerCase().includes(searchVacancy.toLowerCase()) || cName.toLowerCase().includes(searchVacancy.toLowerCase());
                                                            }).map((v: any) => {
                                                                const cl = Array.isArray(v.clients) ? v.clients[0] : (v.clients || (Array.isArray(v.client) ? v.client[0] : v.client));
                                                                const cName = cl?.name || v.data?.organizationText || 'Onbekende Klant';
                                                                return (
                                                                <button
                                                                    key={v.id}
                                                                    onClick={() => handleLinkVacancy(v.id)}
                                                                    disabled={isLinkingVacancy}
                                                                    className={`w-full text-left p-3 rounded-lg border transition-all flex flex-col gap-1 bg-white border-slate-200 hover:border-red- hover:shadow-sm disabled:opacity-50`}
                                                                >
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="font-bold text-slate-800 text-sm truncate pr-2">{v.title || v.data?.title || 'Naamloze Vacature'}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2 mt-0.5 min-w-0">
                                                                        {v.vacancy_number && (
                                                                            <span className="text-[10px] font-black tracking-widest bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 shrink-0">
                                                                                #{v.vacancy_number}
                                                                            </span>
                                                                        )}
                                                                        <span className="text-xs text-slate-500 truncate">
                                                                            {cName}
                                                                        </span>
                                                                    </div>
                                                                </button>
                                                            )})}
                                                            {vacancies.length === 0 && <div className="text-center p-8 text-xs text-slate-400 font-medium">Geen vacatures gevonden.</div>}
                                                            {vacancies.length > 0 && vacancies.filter((v: any) => {
                                                                const cl = Array.isArray(v.clients) ? v.clients[0] : (v.clients || (Array.isArray(v.client) ? v.client[0] : v.client));
                                                                const cName = cl?.name || v.data?.organizationText || '';
                                                                return !searchVacancy || (v.title || v.data?.title || '').toLowerCase().includes(searchVacancy.toLowerCase()) || cName.toLowerCase().includes(searchVacancy.toLowerCase());
                                                            }).length === 0 && <div className="text-center p-8 text-xs text-slate-400 font-medium">Geen vacatures voldoen aan de zoekterm.</div>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Pipeline Stage Blocks */}
                                            {(() => {
                                                const PIPELINE_STAGES = [
                                                    { id: 'Shortlist', label: 'Shortlist', color: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' },
                                                    { id: 'Voorgesteld', label: 'Voorgesteld', color: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100' },
                                                    { id: 'Gesprek', label: 'Gesprek', color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' },
                                                    { id: 'Aanbieding', label: 'Aanbod', color: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' },
                                                    { id: 'Plaatsing', label: 'Plaatsing', color: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' },
                                                ];
                                                const filteredApps = candidateApplications?.filter((a: any) => a.status !== 'Draft_CV') || [];
                                                const total = filteredApps.length;
                                                return (
                                                    <div className="flex items-stretch gap-2 mb-4">
                                                        {PIPELINE_STAGES.map(stage => {
                                                            const stageApps = filteredApps.filter((a: any) => a.status === stage.id || a.status?.toLowerCase() === stage.id.toLowerCase());
                                                            const count = stageApps.length;
                                                            return (
                                                                <button
                                                                    key={stage.id}
                                                                    onClick={() => { if (count > 0 && stageApps[0]?.vacancy_id) onNavigate('vacancies', stageApps[0].vacancy_id); }}
                                                                    className={`flex-1 text-center py-3 rounded-xl border font-black transition-all ${stage.color} ${count > 0 ? 'cursor-pointer' : 'opacity-40 cursor-default'}`}
                                                                    disabled={count === 0}
                                                                    title={count > 0 ? `Open vacature (${stage.label})` : ''}
                                                                >
                                                                    <div className="text-xl font-black">{count}</div>
                                                                    <div className="text-[8px] uppercase tracking-widest font-black mt-0.5">{stage.label}</div>
                                                                </button>
                                                            );
                                                        })}
                                                        <div className="flex-none w-px bg-slate-200 mx-1 self-stretch" />
                                                        <div className="flex-none text-center py-3 px-4 rounded-xl border bg-slate-900 text-white border-slate-800 font-black">
                                                            <div className="text-xl font-black">{total}</div>
                                                            <div className="text-[8px] uppercase tracking-widest font-black mt-0.5">Totaal</div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}

                                            {candidateApplications?.filter((a: any) => a.status !== 'Draft_CV').length === 0 ? (
                                                <div className="text-center py-10 bg-white rounded-xl border border-slate-200">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Geen actieve sollicitaties.</p>
                                                </div>
                                            ) : (
                                                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                                    <div className="overflow-x-auto custom-scrollbar">
                                                        <table className="w-full text-left text-xs min-w-[820px]">
                                                            <thead className="bg-slate-50 border-b border-slate-100">
                                                                <tr>
                                                                    <th className="px-4 py-3 font-black text-slate-400 uppercase tracking-widest text-[9px]">Vacature</th>
                                                                    <th className="px-4 py-3 font-black text-slate-400 uppercase tracking-widest text-[9px] whitespace-nowrap">Opdrachtgever</th>
                                                                    <th className="px-4 py-3 font-black text-slate-400 uppercase tracking-widest text-[9px] whitespace-nowrap">Eindklant</th>
                                                                    <th className="px-4 py-3 font-black text-slate-400 uppercase tracking-widest text-[9px] whitespace-nowrap">Vacature nr.</th>
                                                                    <th className="px-4 py-3 font-black text-slate-400 uppercase tracking-widest text-[9px]">Status / Fase</th>
                                                                    <th className="px-4 py-3 font-black text-slate-400 uppercase tracking-widest text-[9px]">Datum</th>
                                                                    <th className="px-4 py-3 w-8"></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-slate-50">
                                                                {candidateApplications.filter((a: any) => a.status !== 'Draft_CV').map((app: any) => {
                                                                    const vac = app.vacancies;
                                                                    const isHired = ['Plaatsing', 'Hired', 'Offer', 'Aanbieding'].includes(app.status);
                                                                    const isRejected = app.status === 'Rejected';
                                                                    const badgeColor = isHired ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : isRejected ? 'bg-red-50 text-red-600 border-red-100' : 'bg-blue-50 text-blue-700 border-blue-100';
                                                                    const vacNr = vac?.vacancy_number;
                                                                    const vacNrStr = vacNr != null && String(vacNr).trim() !== '' ? String(vacNr).trim() : null;
                                                                    return (
                                                                        <tr key={app.id} className="hover:bg-slate-50 transition-colors cursor-pointer group" onClick={() => onNavigate('vacancies', app.vacancy_id)}>
                                                                            <td className="px-4 py-3 font-bold text-slate-900 group-hover:text-[#e51b23] transition-colors max-w-[220px]">
                                                                                <span className="line-clamp-2">{vac?.title || 'Onbekende Vacature'}</span>
                                                                            </td>
                                                                            <td className="px-4 py-3 align-middle">
                                                                                <FlowLinkedClientCell resolved={vac?.opdrachtgever} hasLink={Boolean(vac?.opdrachtgever_id)} />
                                                                            </td>
                                                                            <td className="px-4 py-3 align-middle">
                                                                                <FlowLinkedClientCell resolved={vac?.eindklant} hasLink={Boolean(vac?.eindklant_id)} />
                                                                            </td>
                                                                            <td className="px-4 py-3 align-middle">
                                                                                {vacNrStr ? (
                                                                                    <span className="text-[11px] font-black text-slate-700 tabular-nums">{vacNrStr}</span>
                                                                                ) : (
                                                                                    <span className="text-slate-400">—</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-4 py-3">
                                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${badgeColor}`}>
                                                                                    {applicationStatusDisplay(app.status)}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-4 py-3 font-medium text-slate-400 text-[11px] whitespace-nowrap">{new Date(app.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: '2-digit' })}</td>
                                                                            <td className="px-4 py-3 text-right">
                                                                                <ChevronRight size={14} className="text-slate-200 group-hover:text-[#e51b23] transition-colors inline" />
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {activeTab === 'automations' && (
                                        <div className="space-y-4 animate-fadeIn">
                                            <div className="flex justify-between items-center mb-6">
                                                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                                                    <Wand2 size={16} className="text-purple-600" />
                                                    Candidate Automations
                                                </h3>
                                                <button onClick={() => onNavigate('automation')} className="px-3 py-1.5 bg-slate-900 text-white rounded-md text-[10px] font-black uppercase tracking-widest hover:bg-purple-600 transition-all flex items-center gap-2">
                                                    <Plus size={12} /> Nieuwe Automation
                                                </button>
                                            </div>

                                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden p-8 flex flex-col items-center justify-center text-center">
                                                <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mb-4 border border-purple-100">
                                                    <CalendarClock size={28} className="text-purple-500" />
                                                </div>
                                                <h4 className="text-sm font-bold text-slate-900 mb-2">Automatiseringen voor {selectedProfile.candidate?.name}</h4>
                                                <p className="text-xs text-slate-500 max-w-md mb-6 leading-relaxed">
                                                    Klik op een actieve automatisering om deze flow direct te starten voor deze kandidaat. Configureer nieuwe in de Automation Manager.
                                                </p>

                                                <div className="w-full max-w-sm grid gap-3 text-left">
                                                    {automationFlows.length === 0 ? (
                                                        <div className="text-center p-4 border border-dashed border-slate-300 rounded-lg text-slate-500 text-xs">
                                                            Geen actieve automations gevonden in database.
                                                        </div>
                                                    ) : automationFlows.map(flow => (
                                                        <button key={flow.id} onClick={() => alert(`Start Flow: ${flow.name}`)} className="flex flex-col p-3 rounded-lg border border-slate-100 hover:border-purple-200 bg-slate-50 transition-colors w-full group outline-none text-left">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-xs font-bold text-slate-700 flex items-center gap-2"><Zap size={14} className="text-purple-500" /> {flow.name}</span>
                                                                <div className="px-2 py-0.5 rounded text-[8px] font-black text-purple-600 bg-purple-100 uppercase tracking-widest">Starten</div>
                                                            </div>
                                                            {flow.description && <p className="text-[10px] text-slate-500 mt-2 pl-6 truncate">{flow.description}</p>}
                                                            <div className="text-[9px] text-slate-400 mt-2 pl-6 font-medium">Trigger: {flow.trigger} • {flow.steps?.length || 0} stappen</div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* CV MAPPER MODAL */}
                    {showCvMapper && parsedCandidate && (
                        <div className="fixed inset-0 z-[250] bg-slate-900/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
                            <div className="bg-white w-full h-full max-w-[1800px] rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row animate-scaleIn min-h-0">
                                {/* LEFT: PDF VIEW */}
                                <div className="w-full md:w-1/2 bg-slate-800 border-r border-slate-200 flex flex-col relative min-h-0">
                                    <div className="p-4 bg-slate-900 border-b border-slate-700 flex justify-between items-center text-white">
                                        <h3 className="font-bold flex items-center gap-2"><FileText size={18} className="text-[#e51b23]" /> Bron Document</h3>
                                        <div className="flex items-center gap-3">
                                            {uploadedFile && <span className="text-xs text-slate-400 truncate max-w-[200px]">{uploadedFile.name}</span>}
                                            <label className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-600 rounded-lg cursor-pointer transition-colors shadow-sm active:scale-95 flex items-center gap-2">
                                                <Upload size={12} /> {uploadedFile || filePreviewUrl || pastedCVText ? 'Vervang Document' : 'Upload Document'}
                                                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={async (e) => {
                                                    const f = e.target.files?.[0];
                                                    if (!f) return;
                                                    window.dispatchEvent(new CustomEvent('showGlobalLoader', { detail: 'Nieuw Document Analyseren...' }));
                                                    try {
                                                        const pUrl = URL.createObjectURL(f);
                                                        setFilePreviewUrl(pUrl);
                                                        setUploadedFile(f);
                                                        const b64 = await new Promise<string>((res) => {
                                                            const r = new FileReader();
                                                            r.onload = ev => res((ev.target?.result as string).split(',')[1]);
                                                            r.readAsDataURL(f);
                                                        });
                                                        setFileBase64(b64);
                                                        const analysis = await analyzeUploadedCV({ base64: b64, mimeType: f.type });
                                                        if (analysis) {
                                                            const globalMatch = await supabase_search_global_candidate_match(analysis);
                                                            if (globalMatch) analysis._globalMatch = globalMatch;
                                                            setParsedCandidate((prev: any) => ({
                                                                ...prev,
                                                                ...normalizeCvAnalysisResult(analysis),
                                                                _isEditMode: prev?._isEditMode,
                                                            }));
                                                            if (selectedProfile && parsedCandidate?._isEditMode) {
                                                                selectedProfile.candidate.original_cv_text = analysis.resume_text || '';
                                                            }
                                                        }
                                                    } catch(err) {
                                                        alert("Er trad een fout op bij het laden van het document.");
                                                    } finally {
                                                        window.dispatchEvent(new CustomEvent('hideGlobalLoader'));
                                                    }
                                                }} />
                                            </label>
                                        </div>
                                    </div>
                                    <div className="flex-1 bg-slate-800 relative p-2 min-h-0">
                                        {filePreviewUrl && (uploadedFile?.type === 'application/pdf' || filePreviewUrl.toLowerCase().includes('.pdf')) ? (
                                            <iframe
                                                src={filePreviewUrl}
                                                className="w-full h-full absolute inset-0 border-none bg-white rounded-lg"
                                                title="CV Preview"
                                            />
                                        ) : filePreviewUrl && (uploadedFile?.type.startsWith('image/') || filePreviewUrl.match(/\.(jpeg|jpg|gif|png)$/i)) ? (
                                            <div className="w-full h-full flex items-center justify-center bg-slate-900 rounded-lg overflow-hidden p-4">
                                                <img src={filePreviewUrl} className="max-w-full max-h-full object-contain" alt="CV Preview" />
                                            </div>
                                        ) : uploadedFile && (uploadedFile.name.toLowerCase().endsWith('.docx') || uploadedFile?.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') ? (
                                            <div className="w-full h-full bg-slate-900 rounded-lg flex flex-col overflow-hidden relative">
                                                <div className="px-4 py-2 bg-slate-800 border-b border-slate-700 flex items-center gap-2 shrink-0 z-10">
                                                    <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/><polyline points="14 2 14 8 20 8"/></svg>
                                                    <span className="text-xs text-slate-300 font-bold truncate">{uploadedFile.name}</span>
                                                    <span className="ml-auto text-[10px] bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded font-bold uppercase tracking-widest">Word Document</span>
                                                </div>
                                                <div className="flex-1 overflow-y-auto bg-white custom-scrollbar relative">
                                                    {docxLoading && (
                                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm z-10 gap-3">
                                                            <Loader2 size={32} className="animate-spin text-blue-500" />
                                                            <p className="text-xs font-bold text-slate-600 uppercase tracking-widest">Preview genereren...</p>
                                                        </div>
                                                    )}
                                                    <div ref={docxRef} className="w-full h-auto min-h-full bg-white text-black text-left px-8 py-4 [&_.docx-viewer]:!bg-transparent [&_.docx-viewer]:!p-0 [&_.docx-viewer_section]:!shadow-none [&_.docx-viewer_section]:!m-0 [&_.docx-viewer_section]:!w-full [&_.docx-viewer_section]:!min-h-full"></div>
                                                </div>
                                            </div>
                                        ) : pastedCVText ? (
                                            <div className="w-full h-full bg-slate-900 rounded-lg p-6 overflow-y-auto custom-scrollbar">
                                                <pre className="text-[11px] leading-relaxed text-slate-300 font-mono whitespace-pre-wrap">{pastedCVText}</pre>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center h-full text-slate-500 flex-col gap-4 bg-slate-900 rounded-lg p-8 text-center border-2 border-dashed border-slate-700">
                                                <FileText size={48} className="text-slate-600 mb-2" />
                                                <p className="text-sm font-semibold text-white">Geen visuele preview beschikbaar</p>
                                                {uploadedFile?.name.toLowerCase().endsWith('.doc') ? (
                                                    <div className="bg-orange-500/10 text-orange-400 p-4 rounded-xl border border-orange-500/20 max-w-sm mt-2 flex flex-col items-center gap-3">
                                                        <p className="text-xs font-medium">Het oudere <b>.doc</b> formaat kan niet in de browser worden weergegeven.</p>
                                                        <p className="text-[10px] text-orange-400/80">De tekst is wél op de achtergrond succesvol geanalyseerd door de AI. Controleer de velden in het rechterpaneel handmatig aan de hand van uw originele bestand.</p>
                                                    </div>
                                                ) : (
                                                    uploadedFile && <p className="text-xs text-slate-600">Bestand: {uploadedFile.name}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* RIGHT: MAPPER FORM */}
                                <div className="w-full md:w-1/2 flex flex-col bg-slate-50 h-full min-h-0">
                                    <div className="p-6 bg-white border-b border-slate-200 flex justify-between items-center sticky top-0 z-10 shadow-sm">
                                        <div>
                                            <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                                                <BrainCircuit className="text-purple-600" /> CV Mapper
                                            </h2>
                                            <p className="text-slate-500 text-xs font-bold">Controleer en vul de gegevens aan voordat je opslaat.</p>
                                        </div>
                                        <div className="flex gap-3">
                                            <button onClick={() => setShowCvMapper(false)} className="px-4 py-2 text-slate-400 hover:text-slate-600 font-bold text-xs uppercase tracking-wide">Annuleren</button>
                                            {parsedCandidate?._isEditMode ? (
                                                <button
                                                    onClick={() => handleOverwriteCandidate(selectedProfile)}
                                                    disabled={isSavingNew || !isCandidateSourceFilled(parsedCandidate?.source)}
                                                    className="px-6 py-2 bg-[#e51b23] hover:bg-red-700 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-red-200 flex items-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isSavingNew ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Update Actieve Kandidaat
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleSaveCandidate(false)}
                                                    disabled={isSavingNew || !isCandidateSourceFilled(parsedCandidate?.source)}
                                                    className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-emerald-200 flex items-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isSavingNew ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Opslaan in Talentpool
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex-1 overflow-y-auto p-8 custom-scrollbar space-y-8">
                                        
                                        {(() => {
                                            const match = parsedCandidate._globalMatch || validCandidates.find(c => {
                                                const cEmail = c.candidate?.email?.toLowerCase();
                                                const pEmail = parsedCandidate.email?.toLowerCase();
                                                if (cEmail && pEmail && cEmail.includes(pEmail)) return true;
                                                
                                                if (parsedCandidate.phone && c.candidate?.phone) {
                                                    const p1 = parsedCandidate.phone.replace(/\D/g, '');
                                                    const p2 = c.candidate.phone.replace(/\D/g, '');
                                                    if (p1.length > 8 && p2.length > 8 && (p1.endsWith(p2) || p2.endsWith(p1))) return true;
                                                    if (p1 && p2 && p1 === p2) return true;
                                                }
                                                
                                                const pName = `${parsedCandidate.firstName || ''} ${parsedCandidate.lastName || ''}`.trim().toLowerCase();
                                                if (pName.length > 4 && c.candidate?.name?.toLowerCase() === pName) return true;
                                                
                                                return false;
                                            });
                                            if (!match || parsedCandidate?._isEditMode) return null;
                                            return (
                                                <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 shadow-sm flex flex-col gap-4 relative overflow-hidden animate-fadeIn">
                                                    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 transform translate-x-1/2 -translate-y-1/2"></div>
                                                    <div className="relative z-10 flex flex-col gap-2">
                                                        <h4 className="text-sm font-black text-amber-900 flex items-center gap-2">
                                                            <AlertTriangle size={18} className="text-amber-500" /> Let op! Start met {(match as any)?.candidate?.name || 'een eerdere versie'} in Talentpool.
                                                        </h4>
                                                        <p className="text-xs text-amber-700 font-medium leading-relaxed">
                                                            We zien al een kandidaat op <strong>{parsedCandidate.email}</strong>. 
                                                            Kies of je de bestaande kandidaat in het systeem wilt updaten met CV details (overschrijven) of deze dubbel wilt opslaan.
                                                        </p>
                                                        <div className="flex flex-wrap gap-2 mt-2">
                                                            <button onClick={() => handleOverwriteCandidate(match)} className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-amber-200 transition-all flex items-center gap-2 active:scale-95">
                                                                <Check size={16} /> Update & Overschrijf
                                                            </button>
                                                            <button onClick={() => handleSaveCandidate(true)} disabled={isSavingNew} className="px-5 py-2.5 bg-white text-slate-700 border-2 border-amber-200 hover:border-amber-300 hover:bg-amber-50 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-sm active:scale-95 disabled:opacity-50">
                                                                {isSavingNew ? <Loader2 size={16} className="animate-spin" /> : "Als Nieuwe CV (Dubbel)"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Persoonsgegevens */}
                                        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                            <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2"><User size={16} /> Persoonsgegevens</h4>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-[10px] font-bold uppercase text-slate-400 mb-1 block">Voornaam</label>
                                                    <input value={parsedCandidate.firstName || ''} onChange={e => setParsedCandidate({ ...parsedCandidate, firstName: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 outline-none focus:border-red-0 transition-colors" />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-bold uppercase text-slate-400 mb-1 block">Achternaam</label>
                                                    <input value={parsedCandidate.lastName || ''} onChange={e => setParsedCandidate({ ...parsedCandidate, lastName: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 outline-none focus:border-red-0 transition-colors" />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="text-[10px] font-bold uppercase text-slate-400 mb-1 block">Huidige Rol</label>
                                                    <input value={parsedCandidate.currentRole || ''} onChange={e => setParsedCandidate({ ...parsedCandidate, currentRole: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 outline-none focus:border-red-0 transition-colors" />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-bold uppercase text-slate-400 mb-1 block">Email</label>
                                                    <input value={parsedCandidate.email || ''} onChange={e => setParsedCandidate({ ...parsedCandidate, email: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 outline-none focus:border-red-0 transition-colors" />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-bold uppercase text-slate-400 mb-1 block">Telefoon</label>
                                                    <input value={parsedCandidate.phone || ''} onChange={e => setParsedCandidate({ ...parsedCandidate, phone: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 outline-none focus:border-red-0 transition-colors" />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="text-[10px] font-bold uppercase text-slate-400 mb-1 block">LinkedIn URL</label>
                                                    <div className="flex items-center gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
                                                        <Linkedin size={16} className="text-red-" />
                                                        <input value={parsedCandidate.linkedin || ''} onChange={e => setParsedCandidate({ ...parsedCandidate, linkedin: e.target.value })} className="w-full bg-transparent text-sm font-bold text-slate-900 outline-none" placeholder="https://linkedin.com/in/..." />
                                                    </div>
                                                </div>

                                                {/* Nieuwe velden */}
                                                <div className="col-span-2 mt-2 pt-4 border-t border-slate-100">
                                                    <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Extra Locatie & Taal Gegevens</h5>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="col-span-2">
                                                            <label className="text-[10px] font-bold uppercase text-slate-400 mb-1 block">Adres</label>
                                                            <input value={parsedCandidate.address || ''} onChange={e => setParsedCandidate({ ...parsedCandidate, address: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 outline-none focus:border-red-0 transition-colors" placeholder="Straatnaam 123" />
                                                        </div>
                                                        <div>
                                                            <label className="text-[10px] font-bold uppercase text-slate-400 mb-1 block">Postcode</label>
                                                            <input value={parsedCandidate.zipCode || ''} onChange={e => setParsedCandidate({ ...parsedCandidate, zipCode: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 outline-none focus:border-red-0 transition-colors" placeholder="1234 AB" />
                                                        </div>
                                                        <div>
                                                            <label className="text-[10px] font-bold uppercase text-slate-400 mb-1 block">Woonplaats</label>
                                                            <input value={parsedCandidate.city || ''} onChange={e => setParsedCandidate({ ...parsedCandidate, city: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 outline-none focus:border-red-0 transition-colors" placeholder="Amsterdam" />
                                                        </div>
                                                        <div>
                                                            <label className="text-[10px] font-bold uppercase text-slate-400 mb-1 block">Land</label>
                                                            <input value={parsedCandidate.country || ''} onChange={e => setParsedCandidate({ ...parsedCandidate, country: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 outline-none focus:border-red-0 transition-colors" placeholder="Nederland" />
                                                        </div>
                                                        <div>
                                                            <label className="text-[10px] font-bold uppercase text-slate-400 mb-1 block">Voorkeurstaal</label>
                                                            <select value={parsedCandidate.preferredLanguage || 'nl'} onChange={e => setParsedCandidate({ ...parsedCandidate, preferredLanguage: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold text-slate-900 outline-none focus:border-red-0 transition-colors">
                                                                <option value="nl">Nederlands</option>
                                                                <option value="en">Engels</option>
                                                            </select>
                                                        </div>
                                                        <div className="col-span-2">
                                                            <label className="text-[10px] font-bold uppercase text-slate-400 mb-1 block">
                                                                Kandidaat bron <span className="text-[#e51b23]">*</span>
                                                            </label>
                                                            <select
                                                                required
                                                                value={parsedCandidate.source || ''}
                                                                onChange={(e) => setParsedCandidate({ ...parsedCandidate, source: e.target.value })}
                                                                className={`w-full p-2.5 bg-slate-50 border rounded-lg text-sm font-bold text-slate-900 outline-none focus:border-[#e51b23] transition-colors ${
                                                                    isCandidateSourceFilled(parsedCandidate.source)
                                                                        ? 'border-slate-200'
                                                                        : 'border-amber-300 ring-1 ring-amber-100'
                                                                }`}
                                                            >
                                                                <option value="" disabled>
                                                                    Selecteer bron (verplicht)…
                                                                </option>
                                                                {candidateSources.map((s) => (
                                                                    <option key={s} value={s}>
                                                                        {s}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {!isCandidateSourceFilled(parsedCandidate.source) && (
                                                                <p className="text-[10px] text-amber-700 font-medium mt-1">
                                                                    Kies de herkomst uit Instellingen → Kandidaat bronnen.
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Motivatie */}
                                        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                            <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2"><Quote size={16} /> Korte Motivatie / Profiel</h4>
                                            <textarea
                                                value={parsedCandidate.motivation || parsedCandidate.summary || ''}
                                                onChange={e => setParsedCandidate({ ...parsedCandidate, motivation: e.target.value })}
                                                className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm leading-relaxed outline-none focus:border-red-0 transition-colors min-h-[120px]"
                                                placeholder="Korte introductie of motivatie van de kandidaat..."
                                            />
                                        </div>

                                        {/* Generic Helper for Lists */}
                                        {/* Note: In a real refactor, we would lift 'renderEditableList' to be reusable, but here we can reimplement a simplified version for 'parsedCandidate' state editing since 'editForm' is separate */}
                                        {['timeline', 'education', 'courses', 'references', 'languages'].map(section => {
                                            const titles = { timeline: 'Werkervaring', education: 'Opleidingen', courses: 'Cursussen', references: 'Referenties', languages: 'Talen' };
                                            const icons = { timeline: <Briefcase size={16} />, education: <GraduationCap size={16} />, courses: <FileCheck size={16} />, references: <Users size={16} />, languages: <MessageSquare size={16} /> };
                                            const list = coerceCvList((parsedCandidate as any)?.[section]);
                                            return (
                                                <div key={section} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                                    <div className="flex justify-between items-start mb-4">
                                                        <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                                            {(icons as any)[section]} {(titles as any)[section]}
                                                        </h4>
                                                        <button
                                                            onClick={() => {
                                                                const newItem = section === 'timeline' ? { role: '', company: '', year: '', description: '' }
                                                                    : section === 'education' ? { degree: '', school: '', year: '' }
                                                                        : section === 'courses' ? { name: '', institute: '', year: '' }
                                                                            : section === 'references' ? { name: '', company: '', role: '', email: '' }
                                                                                : { language: '', level: '' };
                                                                setParsedCandidate({ ...parsedCandidate, [section]: [...list, newItem] });
                                                            }}
                                                            className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-emerald-500"
                                                        >
                                                            <Plus size={16} />
                                                        </button>
                                                    </div>
                                                    <div className="space-y-4">
                                                        {list.map((item: any, idx: number) => (
                                                            <div key={idx} className="relative pl-4 border-l-2 border-emerald-100 py-2 hover:border-emerald-300 transition-all group">
                                                                <button
                                                                    onClick={() => {
                                                                        const newList = list.filter((_: any, i: number) => i !== idx);
                                                                        setParsedCandidate({ ...parsedCandidate, [section]: newList });
                                                                    }}
                                                                    className="absolute right-0 top-0 opacity-0 group-hover:opacity-100 text-red-300 hover:text-red-500 transition-opacity"
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                                <div className="grid gap-2">
                                                                    {/* Dynamic Inputs based on section */}
                                                                    {section === 'timeline' && (
                                                                        <>
                                                                            <input value={item.role || item.title || ''} onChange={e => { const l = [...parsedCandidate.timeline]; l[idx].role = e.target.value; l[idx].title = e.target.value; setParsedCandidate({ ...parsedCandidate, timeline: l }); }} className="font-bold text-sm bg-transparent border-b border-transparent focus:border-emerald-300 outline-none" placeholder="Functie" />
                                                                            <input value={item.company || ''} onChange={e => { const l = [...parsedCandidate.timeline]; l[idx].company = e.target.value; setParsedCandidate({ ...parsedCandidate, timeline: l }); }} className="text-xs font-medium text-slate-500 bg-transparent border-b border-transparent focus:border-emerald-300 outline-none" placeholder="Bedrijf" />
                                                                            <input value={item.year || item.period || ''} onChange={e => { const l = [...parsedCandidate.timeline]; l[idx].year = e.target.value; l[idx].period = e.target.value; setParsedCandidate({ ...parsedCandidate, timeline: l }); }} className="text-[10px] text-slate-400 bg-transparent border-b border-transparent focus:border-emerald-300 outline-none" placeholder="Periode" />
                                                                            <textarea value={item.description || ''} onChange={e => { const l = [...parsedCandidate.timeline]; l[idx].description = e.target.value; setParsedCandidate({ ...parsedCandidate, timeline: l }); }} className="text-xs text-slate-600 bg-slate-50 p-2 rounded w-full min-h-[120px] resize-y" placeholder="Beschrijving..." />
                                                                        </>
                                                                    )}
                                                                    {/* Add other sections similarly... simplified for brevity but functional */}
                                                                    {section !== 'timeline' && Object.keys(item).map(key => (
                                                                        <input
                                                                            key={key}
                                                                            value={item[key] || ''}
                                                                            onChange={e => {
                                                                                const l = [...list];
                                                                                l[idx][key] = e.target.value;
                                                                                setParsedCandidate({ ...parsedCandidate, [section]: l });
                                                                            }}
                                                                            className="text-xs bg-transparent border-b border-transparent focus:border-emerald-300 outline-none w-full"
                                                                            placeholder={key}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {list.length === 0 && <p className="text-[10px] text-slate-300 italic">Geen items</p>}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* Skills */}
                                        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                            <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2"><Wrench size={16} /> Skills & Tags</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {coerceCvTags(parsedCandidate.tags).map((tag: string, idx: number) => (
                                                    <div key={idx} className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-lg">
                                                        <span className="text-xs font-bold text-slate-700">{tag}</span>
                                                        <button onClick={() => {
                                                            const newTags = coerceCvTags(parsedCandidate.tags).filter((_: any, i: number) => i !== idx);
                                                            setParsedCandidate({ ...parsedCandidate, tags: newTags });
                                                        }} className="text-slate-400 hover:text-red-500"><X size={12} /></button>
                                                    </div>
                                                ))}
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        placeholder="+ Tag toevoegen"
                                                        className="text-xs bg-transparent outline-none min-w-[100px]"
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                const val = (e.target as HTMLInputElement).value.trim();
                                                                if (val) setParsedCandidate({ ...parsedCandidate, tags: [...coerceCvTags(parsedCandidate.tags), val] });
                                                                (e.target as HTMLInputElement).value = '';
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Hobbies */}
                                        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                            <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2"><Gamepad2 size={16} /> Hobby's & Interesses</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {(parsedCandidate.hobbies || []).map((hobby: string, idx: number) => (
                                                    <div key={idx} className="flex items-center gap-1 bg-purple-50 px-2 py-1 rounded-lg border border-purple-100">
                                                        <span className="text-xs font-bold text-purple-700">{hobby}</span>
                                                        <button onClick={() => {
                                                            const newHobbies = parsedCandidate.hobbies.filter((_: any, i: number) => i !== idx);
                                                            setParsedCandidate({ ...parsedCandidate, hobbies: newHobbies });
                                                        }} className="text-purple-300 hover:text-red-500"><X size={12} /></button>
                                                    </div>
                                                ))}
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        placeholder="+ Hobby toevoegen"
                                                        className="text-xs bg-transparent outline-none min-w-[120px]"
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                const val = (e.target as HTMLInputElement).value.trim();
                                                                if (val) setParsedCandidate({ ...parsedCandidate, hobbies: [...(parsedCandidate.hobbies || []), val] });
                                                                (e.target as HTMLInputElement).value = '';
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* OTHERS */}
                                        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                                            <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4 flex items-center gap-2"><Info size={16} /> Overig</h4>
                                            <textarea
                                                value={parsedCandidate.otherText || ''}
                                                onChange={e => setParsedCandidate({ ...parsedCandidate, otherText: e.target.value })}
                                                className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs leading-relaxed outline-none focus:border-red-0 transition-colors min-h-[100px]"
                                                placeholder="Overige niet-gestructureerde info..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Template Selection Modal */}
                    {showTemplateModal && (
                        <div className="fixed inset-0 z-[150] flex items-center justify-center p-6 bg-slate-900/80 backdrop-blur-md animate-fadeIn">
                            <div className="bg-white w-full max-w-6xl h-[85vh] rounded-[2rem] shadow-2xl flex flex-col overflow-hidden animate-scaleIn">
                                <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-white z-10">
                                    <div>
                                        <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Kies een Template</h2>
                                        <p className="text-slate-500 text-sm font-medium">Selecteer een Pro CV ontwerp voor {selectedProfile?.candidate?.name}</p>
                                    </div>
                                    <button onClick={() => setShowTemplateModal(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors"><X size={24} className="text-slate-400 hover:text-red-500" /></button>
                                </div>

                                <div className="flex-1 overflow-y-auto p-10 bg-slate-50 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 custom-scrollbar">

                                    {proTemplates.map(tmpl => (
                                        <div key={tmpl.id} onClick={() => handleGenerateCV(tmpl.id)} className="bg-white rounded-xl overflow-hidden cursor-pointer shadow-sm hover:shadow-md transition-all duration-300 group relative flex flex-col border border-slate-200 hover:border-red-">
                                            <div className="w-full bg-slate-50 flex flex-col items-center justify-center p-8 aspect-[210/297] relative overflow-hidden">
                                                <div className="w-16 h-20 bg-white shadow-sm border border-slate-200 flex flex-col group-hover:scale-105 transition-transform duration-500">
                                                    <div className="h-3 bg-red-0 w-full rounded-t-[2px]"></div>
                                                    <div className="flex-1 flex items-center justify-center">
                                                        <FileText size={20} className="text-slate-300/50" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="p-4 bg-white relative z-20 flex justify-between items-center border-t border-slate-100">
                                                <h4 className="font-bold text-sm text-slate-800 truncate">{tmpl.title}</h4>
                                                <div className="w-3 h-3 rounded-full bg-red-0 shrink-0"></div>
                                            </div>
                                        </div>
                                    ))}
                                        <div className="col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4 shadow-sm border border-slate-200 rounded-xl bg-white p-8 mt-4">
                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center shadow-inner">
                                                    <Code size={24} />
                                                </div>
                                                <div>
                                                    <h3 className="font-black text-slate-900 text-2xl uppercase tracking-tight">Shortcodes & Tags Handleiding</h3>
                                                    <p className="text-sm text-slate-500 font-medium mt-1">Typ deze shortcodes exact over in je Word documenten (.docx) om kandidaatgegevens automatisch te koppelen.</p>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                                                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200/60 shadow-sm relative overflow-hidden group">
                                                    <h4 className="text-xs font-black uppercase tracking-widest text-[#e51b23] mb-4 pb-3 border-b border-red-200/50 flex items-center gap-2">
                                                        Kandidaat Persoonsgegevens
                                                    </h4>
                                                    <div className="grid gap-3">
                                                        {[
                                                            { tag: '{name}', desc: 'Volledige Naam' },
                                                            { tag: '{email}', desc: 'E-mailadres' },
                                                            { tag: '{phone}', desc: 'Telefoonnummer' },
                                                            { tag: '{role}', desc: 'Functietitel' },
                                                            { tag: '{location}', desc: 'Woonplaats' },
                                                            { tag: '{summary}', desc: 'Profielschets / Introductie' },
                                                            { tag: '{skills}', desc: 'Vaardigheden (komma gescheiden)' },
                                                        ].map(item => (
                                                            <div key={item.tag} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:border-red- transition-colors">
                                                                <span className="font-bold text-slate-700 text-sm">{item.desc}</span>
                                                                <code className="bg-slate-100 text-red- px-3 py-1.5 rounded-lg text-xs font-mono font-bold select-all">{item.tag}</code>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                
                                                {/* Beschikbaarheid */}
                                                <div className="space-y-1.5 ml-4">
                                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Beschikbaar per</p>
                                                    <div className="flex items-center gap-2">
                                                        <input 
                                                            type="date" 
                                                            value={filterAvailabilityDate}
                                                            onChange={e => setFilterAvailabilityDate(e.target.value)}
                                                            className="px-2.5 py-1 text-[11px] font-medium border border-slate-200 rounded-lg outline-none text-slate-600 bg-slate-50 focus:border-slate-300"
                                                        />
                                                        {filterAvailabilityDate && (
                                                            <button onClick={() => setFilterAvailabilityDate('')} className="text-slate-400 hover:text-slate-600">
                                                                <X size={12} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200/60 shadow-sm relative overflow-hidden group">
                                                    <h4 className="text-xs font-black uppercase tracking-widest text-[#e51b23] mb-4 pb-3 border-b border-red-200/50 flex items-center gap-2">
                                                        Ervaring & Opleiding (Loops)
                                                    </h4>
                                                    <p className="text-xs text-slate-500 mb-4 font-medium">Gebruik de <code className="text-[#e51b23] bg-red-50 px-1 rounded">#</code> en <code className="text-[#e51b23] bg-red-50 px-1 rounded">/</code> tags om een sectie te herhalen voor elke werkervaring of opleiding.</p>

                                                    <div className="space-y-4">
                                                        <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
                                                            <h5 className="font-bold text-slate-800 text-sm mb-2 flex items-center gap-2"><Briefcase size={14} className="text-red-0" /> Werkervaring Loop</h5>
                                                            <pre className="text-xs text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-100 font-mono">
                                                                {'{#workHistory}'}<br />
                                                                {'  Functie: {role}'}<br />
                                                                {'  Bedrijf: {company}'}<br />
                                                                {'  Periode: {period}'}<br />
                                                                {'  {description}'}<br />
                                                                {'{/workHistory}'}
                                                            </pre>
                                                        </div>
                                                        <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
                                                            <h5 className="font-bold text-slate-800 text-sm mb-2 flex items-center gap-2"><FileCheck size={14} className="text-purple-500" /> Opleidingen Loop</h5>
                                                            <pre className="text-xs text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-100 font-mono">
                                                                {'{#education}'}<br />
                                                                {'  Studie: {degree}'}<br />
                                                                {'  Instituut: {school}'}<br />
                                                                {'  Periode: {period}'}<br />
                                                                {'{/education}'}
                                                            </pre>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                </div>
                            </div>
                        </div>
                    )}

                    {templateType === 'pro' && (
                        <div className="col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4 relative shadow-sm border border-slate-200 rounded-xl bg-white overflow-hidden p-8 mt-4">
                            <button onClick={() => setTemplateType('cv')} className="absolute top-4 right-4 bg-slate-100 text-slate-600 px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-200 transition-colors flex items-center gap-2 z-10">
                                <ArrowLeft size={16} /> Terug naar Selectie
                            </button>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center shadow-inner">
                                    <Code size={24} />
                                </div>
                                <div>
                                    <h3 className="font-black text-slate-900 text-2xl uppercase tracking-tight">Shortcodes & Tags Handleiding</h3>
                                    <p className="text-sm text-slate-500 font-medium mt-1">Typ deze shortcodes exact over in je Word documenten (.docx) om kandidaatgegevens automatisch te koppelen.</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">

                                {/* Persona */}
                                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200/60 shadow-sm relative overflow-hidden group">
                                    <h4 className="text-xs font-black uppercase tracking-widest text-[#e51b23] mb-4 pb-3 border-b border-red-200/50 flex items-center gap-2">
                                        Kandidaat Persoonsgegevens
                                    </h4>
                                    <div className="grid gap-3">
                                        {[
                                            { tag: '[name]', desc: 'Volledige naam' },
                                            { tag: '[voornaam]', desc: 'Voornaam kandidaat' },
                                            { tag: '[achternaam]', desc: 'Achternaam kandidaat' },
                                            { tag: '[email]', desc: 'E-mailadres' },
                                            { tag: '[phone]', desc: 'Telefoonnummer' },
                                            { tag: '[address]', desc: 'Adres / Straat' },
                                            { tag: '[zipCode]', desc: 'Postcode' },
                                            { tag: '[city]', desc: 'Woonplaats' },
                                            { tag: '[country]', desc: 'Land' },
                                            { tag: '[linkedin]', desc: 'LinkedIn profiel URL' },
                                        ].map((t, i) => (
                                            <div key={i} className="flex justify-between items-center bg-white px-4 py-3 rounded-xl border border-slate-200/60 hover:border-red-300 transition-colors shadow-sm">
                                                <span className="text-sm text-slate-700 font-medium">{t.desc}</span>
                                                <code className="text-[13px] font-black tracking-wide text-slate-900 bg-red-50/50 px-3 py-1 rounded-lg border border-red-100 cursor-pointer hover:bg-red-100 hover:text-red-700 transition-colors" onClick={() => navigator.clipboard.writeText(t.tag)} title="Kopiëren">
                                                    {t.tag}
                                                </code>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex flex-col gap-8">
                                    {/* Profile & Match */}
                                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200/60 shadow-sm relative overflow-hidden group">
                                        <h4 className="text-xs font-black uppercase tracking-widest text-purple-600 mb-4 pb-3 border-b border-purple-200/50 flex items-center gap-2">
                                            Profiel & Match Elementen
                                        </h4>
                                        <div className="grid gap-3">
                                            {[
                                                { tag: '[role]', desc: 'Hoofdfunctie / Job Title' },
                                                { tag: '[summary]', desc: 'Persoonlijke samenvatting' },
                                                { tag: '[motivation]', desc: 'Aangepaste motivatie tekst' },
                                                { tag: '[skills]', desc: 'Vaardigheden (Aparte tags, komma gescheiden)' },
                                            ].map((t, i) => (
                                                <div key={i} className="flex justify-between items-center bg-white px-4 py-3 rounded-xl border border-slate-200/60 hover:border-purple-300 transition-colors shadow-sm">
                                                    <span className="text-sm text-slate-700 font-medium">{t.desc}</span>
                                                    <code className="text-[13px] font-black tracking-wide text-slate-900 bg-purple-50/50 px-3 py-1 rounded-lg border border-purple-100 cursor-pointer hover:bg-purple-100 hover:text-purple-700 transition-colors" onClick={() => navigator.clipboard.writeText(t.tag)} title="Kopiëren">
                                                        {t.tag}
                                                    </code>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Loops / Lists */}
                                    <div className="bg-emerald-50/30 rounded-2xl p-6 border border-emerald-100 shadow-sm relative overflow-hidden">
                                        <h4 className="text-xs font-black uppercase tracking-widest text-emerald-600 mb-4 pb-3 border-b border-emerald-200/50 flex items-center gap-2">
                                            Lijsten & Herhaling (Werkervaring & Opleiding)
                                        </h4>
                                        <p className="text-[13px] text-slate-600 mb-5 leading-relaxed font-medium">
                                            Lijsten werken met zogenaamde blocks. Typ de start tag (bijv. <code className="bg-emerald-100 text-emerald-800 px-1 py-0.5 rounded font-bold">[#experience]</code>) aan het begin van een sectie en de end tag <code className="bg-emerald-100 text-emerald-800 px-1 py-0.5 rounded font-bold">[/experience]</code> aan het einde. Word herhaalt de styling daartussen voor alles in de lijst!
                                        </p>
                                        <div className="space-y-4">

                                            {/* Experience Block */}
                                            <div className="border border-emerald-200/60 rounded-xl overflow-hidden shadow-sm">
                                                <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100/60 flex justify-between items-center cursor-pointer hover:bg-emerald-100" onClick={() => navigator.clipboard.writeText('[#experience]\n[role] bij [company] ([period])\n[description]\n[/experience]')}>
                                                    <code className="text-[13px] font-black text-emerald-800 tracking-wide">[#experience]...[/experience]</code>
                                                </div>
                                                <div className="p-4 bg-white grid grid-cols-2 gap-3">
                                                    <div className="flex flex-col gap-1">
                                                        <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[role]')}>[role]</code>
                                                        <span className="text-[11px] text-slate-500 font-medium pl-1">Functietitel</span>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[company]')}>[company]</code>
                                                        <span className="text-[11px] text-slate-500 font-medium pl-1">Werkgever</span>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[period]')}>[period]</code>
                                                        <span className="text-[11px] text-slate-500 font-medium pl-1">Periode</span>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[description]')}>[description]</code>
                                                        <span className="text-[11px] text-slate-500 font-medium pl-1">Gedetailleerde tekst</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Education Block */}
                                            <div className="border border-emerald-200/60 rounded-xl overflow-hidden shadow-sm">
                                                <div className="bg-emerald-50 px-4 py-3 border-b border-emerald-100/60 flex justify-between items-center cursor-pointer hover:bg-emerald-100" onClick={() => navigator.clipboard.writeText('[#education]\n[degree] - [school] ([year])\n[/education]')}>
                                                    <code className="text-[13px] font-black text-emerald-800 tracking-wide">[#education]...[/education]</code>
                                                </div>
                                                <div className="p-4 bg-white grid grid-cols-2 gap-3">
                                                    <div className="flex flex-col gap-1">
                                                        <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[degree]')}>[degree]</code>
                                                        <span className="text-[11px] text-slate-500 font-medium pl-1">Opleiding / Niveau</span>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[school]')}>[school]</code>
                                                        <span className="text-[11px] text-slate-500 font-medium pl-1">Onderwijsinstelling</span>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <code className="text-xs font-black bg-slate-50 px-2 py-1.5 rounded-lg border border-slate-100 cursor-pointer hover:bg-slate-100 hover:text-emerald-700 transition-colors inline-block w-fit" onClick={() => navigator.clipboard.writeText('[year]')}>[year]</code>
                                                        <span className="text-[11px] text-slate-500 font-medium pl-1">Jaartal</span>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* DUPLICATE WARNING MODAL */}
                    {showDuplicateWarning && (
                        <div className="fixed inset-0 z-[600] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
                            <div className="bg-white w-full max-w-md rounded-[2rem] shadow-2xl flex flex-col items-center mt-[-10vh] animate-scaleIn p-8 text-center border-t-8 border-orange-500">
                                <div className="w-16 h-16 bg-orange-50 text-orange-500 rounded-full flex items-center justify-center mb-4">
                                    <AlertCircle size={32} />
                                </div>
                                <h2 className="text-xl font-black text-slate-900 tracking-tight mb-2">Dubbele Kandidaat</h2>
                                <p className="text-sm text-slate-500 mb-8 font-medium">
                                    Er is al een kandidaat met het e-mailadres <strong>{parsedCandidate?.email}</strong>. 
                                    Wil je een volledig nieuwe dubbele kandidaat opslaan, of wil je de bestaande kandidaat overschrijven met deze nieuwe CV data?
                                </p>
                                <div className="flex flex-col gap-3 w-full">
                                    <button onClick={() => { setShowDuplicateWarning(false); setDuplicateCandidateMatch(null); }} className="w-full py-3 bg-slate-100 text-slate-700 font-bold uppercase tracking-widest rounded-xl hover:bg-slate-200 transition-all text-xs">
                                        Annuleren
                                    </button>
                                    <button onClick={() => handleSaveCandidate(true)} className="w-full py-3 bg-orange-100 text-orange-700 font-bold uppercase tracking-widest rounded-xl hover:bg-orange-200 transition-all text-xs border border-orange-200">
                                        Toch als Nieuw Opslaan (Dubbel)
                                    </button>
                                    <button onClick={() => handleOverwriteCandidate()} className="w-full py-3 bg-[#e51b23] text-white font-bold uppercase tracking-widest rounded-xl hover:bg-red-700 shadow-lg shadow-red-200 transition-all text-xs flex items-center justify-center gap-2">
                                        <Save size={16} /> Bestaande Overschrijven
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* REMAP DOCUMENT SELECTOR MODAL */}
            {showRemapDocSelector && (
                <div className="fixed inset-0 z-[600] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white w-full max-w-lg rounded-[2rem] shadow-2xl flex flex-col mt-[-5vh] animate-scaleIn overflow-hidden border border-slate-200">
                        <div className="p-6 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-purple-100 text-purple-600 rounded-xl shadow-sm">
                                    <FileText size={20} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-black text-slate-900 tracking-tight">Kies te scannen document</h2>
                                    <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-0.5">Meerdere bestanden gevonden</p>
                                </div>
                            </div>
                            <button onClick={() => setShowRemapDocSelector(false)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors shrink-0">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-6 space-y-3">
                            <p className="text-sm text-slate-600 font-medium mb-4">
                                Welk bestand wil je gebruiken om het profiel opnieuw door de AI in te laten lezen?
                            </p>
                            {remapDocsList.map((doc, idx) => (
                                <div key={doc.id || idx} onClick={() => executeRemapWithDoc(doc)} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:border-[#e51b23] hover:shadow-md cursor-pointer flex items-center justify-between group transition-all active:scale-[0.98]">
                                    <div className="flex items-center gap-4">
                                        <div className={`p-2.5 rounded-lg ${doc.type === 'text' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-[#e51b23]'}`}>
                                            <FileText size={18} />
                                        </div>
                                        <div>
                                            <p className="font-bold text-slate-900 text-sm group-hover:text-[#e51b23] transition-colors">{doc.title}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-[10px] uppercase font-black tracking-widest bg-slate-100 text-slate-500 px-2 py-0.5 rounded">
                                                    {doc.type === 'text' ? 'Tekst (Geen Originele PDF)' : doc.type === 'url' ? 'Extern Bestand' : 'Opgeslagen PDF'}
                                                </span>
                                                {doc.date && <span className="text-[10px] text-slate-400 font-medium">{new Date(doc.date).toLocaleDateString()}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <ChevronRight className="text-slate-300 group-hover:text-[#e51b23]" />
                                </div>
                            ))}
                        </div>
                        <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
                            <button onClick={() => setShowRemapDocSelector(false)} className="px-6 py-2.5 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 rounded-xl font-bold text-xs uppercase tracking-widest transition-all">
                                Annuleren
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* PRO TEMPLATE CONFIG MODAL */}
                    {proTemplateConfig.isOpen && proTemplateConfig.template && (
                        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md animate-fadeIn">
                            <div className="bg-white w-full max-w-5xl h-[90vh] rounded-[2rem] shadow-2xl flex flex-col md:flex-row relative animate-scaleIn overflow-hidden">
                                <CVContentSidebar
                                    className="w-full md:w-[450px] lg:w-[500px] bg-white border-r border-slate-200 h-full overflow-hidden flex flex-col shrink-0"
                                    data={proTemplateConfig.candidateData}
                                    onChange={(data) => setProTemplateConfig(p => ({ ...p, candidateData: data }))}
                                    vacancies={vacancies}
                                    selectedVacancyId={proTemplateConfig.vacancyId}
                                    onSelectVacancy={(id) => setProTemplateConfig(p => ({ ...p, vacancyId: id }))}
                                    onTailor={generateTailoredCV}
                                    isTailoring={proTemplateConfig.isTailoring}
                                    tailorResult={proTemplateConfig.tailorResult}
                                    onApplyTailoredChange={(field) => {
                                        if (field === 'summary' && proTemplateConfig.tailorResult?.tailoredSummary) {
                                            setProTemplateConfig(p => ({ ...p, candidateData: { ...p.candidateData, summary: p.tailorResult.tailoredSummary } }));
                                        }
                                    }}
                                    clientId={user?.role === 'ADMIN' ? undefined : (user?.clientId || user?.id)}
                                />

                                <div className="flex-1 bg-slate-50 flex flex-col p-8 overflow-y-auto custom-scrollbar relative">
                                    <div className="mb-6 flex gap-4 pr-10">
                                        <div className="w-14 h-14 bg-red- text-red- rounded-2xl flex items-center justify-center shrink-0">
                                            <FileCheck size={28} />
                                        </div>
                                        <div className="flex-1">
                                            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Kandidaat CV Optimaliseren</h2>
                                            <p className="text-slate-500 text-sm font-medium mt-1">Slimme Word Document Generator voor <strong>{(proTemplateConfig.template as any).title}</strong>.</p>
                                        </div>
                                    </div>

                                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-4 mb-auto">
                                        <h3 className="font-bold text-slate-800 text-sm uppercase tracking-widest flex items-center gap-2 mb-2"><Wand2 size={16} className="text-red-0" /> Hoe werkt dit?</h3>
                                        <p className="text-sm text-slate-500 leading-relaxed">
                                            Bewerk de gegevens links in de <strong className="text-slate-700">Inhoud Editor</strong>. Alle velden worden naadloos overgenomen in het Pro Template, inclusief complexe lijsten zoals werkervaring en opleidingen.
                                        </p>
                                        <p className="text-sm text-slate-500 leading-relaxed mb-4">
                                            Gebruik de knop <strong>'Optimaliseer'</strong> bovenaan de editor om Froukje de <em>Functietitel</em> en <em>Profielschets</em> automatisch te laten herschrijven op basis van een geselecteerde vacature.
                                        </p>

                                        <div className="bg-red-/50 rounded-xl p-4 border border-red-/50">
                                            <h4 className="text-xs font-bold text-red- mb-2 uppercase tracking-wide">Bekende Shortcodes in dit document:</h4>
                                            <div className="flex flex-wrap gap-2 text-[10px] font-mono font-medium">
                                                <span className="bg-white text-slate-600 px-2 py-1 rounded shadow-sm border border-slate-200">[name]</span>
                                                <span className="bg-white text-slate-600 px-2 py-1 rounded shadow-sm border border-slate-200">[role]</span>
                                                <span className="bg-white text-slate-600 px-2 py-1 rounded shadow-sm border border-slate-200">[phone]</span>
                                                <span className="bg-white text-slate-600 px-2 py-1 rounded shadow-sm border border-slate-200">[email]</span>
                                                <span className="bg-white text-slate-600 px-2 py-1 rounded shadow-sm border border-slate-200">[location]</span>
                                                <span className="bg-white text-slate-600 px-2 py-1 rounded shadow-sm border border-slate-200">[summary]</span>
                                                <span className="bg-white text-slate-600 px-2 py-1 rounded shadow-sm border border-slate-200">[#workHistory]</span>
                                                <span className="bg-white text-slate-600 px-2 py-1 rounded shadow-sm border border-slate-200">[#education]</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-6 border-t border-slate-200 flex justify-end gap-3 mt-6">
                                        <button onClick={() => setProTemplateConfig(p => ({ ...p, isOpen: false }))} className="px-6 py-3 text-slate-500 font-bold hover:bg-slate-200 rounded-xl transition-colors">Annuleren</button>
                                        <button
                                            disabled={isPoolLoading}
                                            onClick={executeProGenerate}
                                            className="px-6 py-3 bg-[#e51b23] hover:bg-red-700 disabled:bg-red-300 text-white font-black uppercase tracking-widest rounded-xl shadow-lg shadow-red-200 transition-all flex items-center gap-2"
                                        >
                                            {isPoolLoading ? <Loader2 size={18} className="animate-spin" /> : <FileCheck size={18} />}
                                            {isPoolLoading ? 'Aanmaken & Uploaden...' : 'Aanmaken & Aanpassen in Word'}
                                        </button>
                                    </div>
                                </div>
                                <button onClick={() => setProTemplateConfig(p => ({ ...p, isOpen: false }))} className="absolute top-4 right-4 p-2 hover:bg-slate-200 rounded-full transition-colors z-[210]"><X size={24} className="text-slate-400" /></button>
                            </div>
                        </div>
                    )}

                    {/* ADD CANDIDATE MODAL */}
                    {showAddModal && (
                        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md animate-fadeIn">
                            <div className="bg-white w-full max-w-2xl rounded-[2rem] shadow-2xl overflow-hidden flex flex-col animate-scaleIn h-[600px]">
                                <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                                    <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight">Kandidaat Toevoegen</h2>
                                    <button onClick={() => { setShowAddModal(false); setShowPasteText(false); setPastedCVText(''); }} className="p-2 hover:bg-slate-50 rounded-full transition-colors"><X size={20} className="text-slate-400" /></button>
                                </div>

                                <div className="flex-1 p-8 overflow-y-auto custom-scrollbar">
                                    <div className="space-y-6">
                                        {!parsedCandidate ? (
                                            <div className="space-y-4">
                                                <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 flex flex-col items-center justify-center text-center hover:border-red- hover:bg-red- transition-all cursor-pointer relative group">
                                                    <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                                                    <div className="w-16 h-16 bg-red- text-red-0 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                                        {isAnalyzing ? <Loader2 className="animate-spin" size={24} /> : <Upload size={24} />}
                                                    </div>

                                                    <>
                                                        <h3 className="font-bold text-slate-900">Upload CV</h3>
                                                        <p className="text-xs text-slate-500 mt-1">Sleep bestand of klik om te uploaden</p>
                                                    </>
                                                </div>
                                                <div className="relative flex py-2 items-center">
                                                    <div className="flex-grow border-t border-slate-100"></div>
                                                    <span className="flex-shrink-0 mx-4 text-[10px] text-slate-300 font-bold uppercase tracking-widest">Of</span>
                                                    <div className="flex-grow border-t border-slate-100"></div>
                                                </div>
                                                {!showPasteText ? (
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <button onClick={handleManualEntry} className="w-full py-4 border border-slate-200 rounded-xl text-slate-600 font-bold text-xs hover:bg-slate-50 transition-all flex items-center justify-center gap-2">
                                                            <Edit3 size={16} /> Handmatig Invoeren
                                                        </button>
                                                        <button onClick={() => setShowPasteText(true)} className="w-full py-4 border border-slate-200 rounded-xl text-slate-600 font-bold text-xs hover:bg-slate-50 transition-all flex items-center justify-center gap-2">
                                                            <FileText size={16} /> Plak Tekst / LinkedIn
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4 animate-fadeIn">
                                                        <textarea 
                                                            value={pastedCVText} 
                                                            onChange={(e) => setPastedCVText(e.target.value)} 
                                                            placeholder="Plak hier het LinkedIn profiel of CV tekst..." 
                                                            className="w-full h-40 p-4 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 outline-none focus:border-red-500 custom-scrollbar resize-none" 
                                                        />
                                                        <div className="flex gap-4">
                                                            <button onClick={() => setShowPasteText(false)} className="px-6 py-3 border border-slate-200 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-50 transition-colors">Terug</button>
                                                            <button onClick={handlePastedTextSubmit} disabled={isAnalyzing || !pastedCVText.trim()} className="flex-1 py-3 bg-[#e51b23] text-white rounded-xl text-xs font-bold hover:bg-[#B71C1C] flex items-center justify-center gap-2 transition-colors disabled:opacity-50">{isAnalyzing ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Analyseren met AI</button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 flex items-center gap-3">
                                                    <CheckCircle2 className="text-emerald-600" size={20} />
                                                    <div>
                                                        <p className="text-xs font-bold text-emerald-800">Gegevens Klaar</p>
                                                        <p className="text-[10px] text-emerald-600">{uploadedFile ? uploadedFile.name : 'Handmatige invoer'}</p>
                                                    </div>
                                                    <button onClick={() => setShowCvMapper(true)} className="ml-auto bg-emerald-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-emerald-700 transition-colors shadow-sm">Open CV Mapper</button>
                                                </div>
                                                <button onClick={() => { setParsedCandidate(null); setUploadedFile(null); }} className="w-full py-3 text-slate-400 hover:text-red-500 text-xs font-bold">Annuleren & Opnieuw</button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* INTERVIEW INVITE MODAL */}
                    {showInterviewModal && selectedProfile && (
                        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md animate-fadeIn">
                            <div className="bg-white w-full max-w-xl rounded-[2rem] shadow-2xl overflow-hidden flex flex-col animate-scaleIn">
                                <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                                    <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                                        <CalendarClock size={20} className="text-purple-600" /> Uitnodigen voor gesprek
                                    </h2>
                                    <button onClick={() => setShowInterviewModal(false)} className="p-2 hover:bg-slate-50 rounded-full transition-colors"><X size={20} className="text-slate-400" /></button>
                                </div>
                                <div className="p-8 space-y-6 bg-slate-50">
                                    <p className="text-sm text-slate-600 font-medium">Kies hoe je <strong className="text-slate-900">{selectedProfile.candidate.name}</strong> wilt uitnodigen voor een kennismaking. Dit wordt automatisch gelogd in de communicatie historie.</p>

                                    <div className="grid grid-cols-2 gap-4">
                                        <button onClick={() => { setCommType('email'); setNewCommText(interviewCommText.replace(/{{name}}/g, selectedProfile.candidate.name || 'Kandidaat')); setShowInterviewModal(false); setActiveTab('comm'); }} className="p-4 bg-white border border-slate-200 rounded-2xl hover:border-purple-300 hover:shadow-md transition-all text-left group">
                                            <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><MessageSquare size={18} /></div>
                                            <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-1">Stuur Bericht</h4>
                                            <p className="text-[10px] text-slate-500">Standaard template of vrij bericht via het systeem.</p>
                                        </button>

                                        <button onClick={() => handleInterviewInvite('whatsapp')} className="p-4 bg-white border border-slate-200 rounded-2xl hover:border-emerald-300 hover:shadow-md transition-all text-left group">
                                            <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><Phone size={18} /></div>
                                            <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-1">WhatsApp</h4>
                                            <p className="text-[10px] text-slate-500">Open WhatsApp Web ({selectedProfile.candidate.phone || 'Geen nr'}).</p>
                                        </button>

                                        <button onClick={() => handleInterviewInvite('email')} className="p-4 bg-white border border-slate-200 rounded-2xl hover:border-red- hover:shadow-md transition-all text-left group">
                                            <div className="w-10 h-10 bg-red- text-red- rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><Mail size={18} /></div>
                                            <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-1">E-mail</h4>
                                            <p className="text-[10px] text-slate-500">Open e-mailprogramma ({selectedProfile.candidate.email || 'Geen email'}).</p>
                                        </button>

                                        <button onClick={() => handleInterviewInvite('call')} className="p-4 bg-white border border-slate-200 rounded-2xl hover:border-amber-300 hover:shadow-md transition-all text-left group">
                                            <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><Activity size={18} /></div>
                                            <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-1">Telefoon</h4>
                                            <p className="text-[10px] text-slate-500">Maak direct een "Gebeld" notitie aan in de log.</p>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Delete Confirmation Modal */}
                    {showDeleteConfirm && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
                            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 border border-slate-200 animate-scaleIn">
                                <div className="flex flex-col items-center text-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-500">
                                        <Trash2 size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-black text-slate-900 mb-1">Kandidaat Verwijderen?</h3>
                                        <p className="text-sm text-slate-500 font-medium">Weet je zeker dat je {selectedProfile?.candidate.name} wilt verwijderen? Dit kan niet ongedaan worden gemaakt.</p>
                                    </div>
                                    <div className="w-full">
                                        <label className="block text-xs font-bold text-slate-500 mb-2">Typ <span className="text-slate-900">VERWIJDEREN</span> om te bevestigen:</label>
                                        <input
                                            type="text"
                                            value={deleteConfirmationName}
                                            onChange={(e) => setDeleteConfirmationName(e.target.value)}
                                            placeholder="Typ VERWIJDEREN..."
                                            className="w-full p-2 border border-slate-300 rounded-lg text-sm font-bold text-slate-900 focus:outline-none focus:border-red-500"
                                        />
                                    </div>
                                    <div className="flex gap-3 w-full mt-4">
                                        <button
                                            onClick={() => {
                                                setShowDeleteConfirm(false);
                                                setDeleteConfirmationName('');
                                            }}
                                            className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 font-bold text-xs uppercase tracking-wider hover:bg-slate-50 transition-colors"
                                        >
                                            Annuleren
                                        </button>
                                        <button
                                            onClick={() => {
                                                if (selectedProfile) {
                                                    setCandidateToDelete(selectedProfile.id);
                                                    executeDeleteCandidate();
                                                }
                                            }}
                                            disabled={deleteConfirmationName.trim().toUpperCase() !== 'VERWIJDEREN'}
                                            className="flex-1 py-2.5 rounded-lg bg-red-500 text-white font-bold text-xs uppercase tracking-wider hover:bg-red-600 shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Verwijderen
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CallSidebar has been moved globally to App.tsx */}

                    {/* Async Analysis Overlay */}
                    {isAnalyzing && (
                        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] bg-white/90 backdrop-blur-md px-6 py-4 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] border border-red- flex items-center gap-4 animate-slideUp">
                            <div className="w-10 h-10 bg-red- text-red- rounded-full flex items-center justify-center shrink-0">
                                <Loader2 className="animate-spin" size={20} />
                            </div>
                            <div>
                                <h3 className="font-black text-slate-900 text-sm mb-1">Froukje analyseert CV...</h3>
                                <RecruitmentNewsLoader />
                            </div>
                        </div>
                    )}

                    {/* AI Email Generator Sidebar */}
                    <div className={`fixed top-0 right-0 h-full w-full sm:w-[800px] bg-white shadow-2xl z-[400] transform transition-transform duration-300 ease-in-out flex flex-col ${showAiEmailModal ? 'translate-x-0' : 'translate-x-full'}`}>
                        {/* Header */}
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600">
                                    <Wand2 size={24} />
                                </div>
                                <div>
                                    <h3 className="font-black text-lg text-slate-900 leading-none mb-1">AI Mail Generator</h3>
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Froukje AI Assistent</p>
                                </div>
                            </div>
                            <button onClick={() => setShowAiEmailModal(false)} className="text-slate-400 hover:text-slate-700 p-2"><X size={20} /></button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">1. Kies de Insteek van de Mail</label>
                                    <select
                                        value={emailContext}
                                        onChange={(e) => setEmailContext(e.target.value)}
                                        className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-purple-400"
                                    >
                                        <optgroup label="Standaard Opties">
                                            <option value="Stuur kandidaat mail over vacature">Benaderen voor specifieke vacature</option>
                                            <option value="Korte algemene introductie en kennismaking">Korte introductie / kennismaking</option>
                                            <option value="Vragen naar huidige beschikbaarheid en wensen">Checken of kandidaat beschikbaar is</option>
                                            <option value="Afwijzing wegens onvoldoende match">Nette afwijzing na beoordeling</option>
                                            <option value="Uitnodiging voor een (video) gesprek">Uitnodigen voor gesprek</option>
                                        </optgroup>
                                        {savedEmailTemplates.length > 0 && (
                                            <optgroup label="Mijn Eigen Templates">
                                                {savedEmailTemplates.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                            </optgroup>
                                        )}
                                    </select>
                                </div>
                                {emailContext === 'Stuur kandidaat mail over vacature' && (
                                    <div>
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Selecteer de Vacature</label>
                                        <select
                                            value={emailVacancyId}
                                            onChange={(e) => setEmailVacancyId(e.target.value)}
                                            className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-purple-400"
                                        >
                                            <option value="">(Optioneel) Selecteer Vacature</option>
                                            {Array.from(new Map(vacancies.map(v => [v.id, v])).values()).map(v => <option key={v.id} value={v.id}>{v.title}</option>)}
                                        </select>
                                    </div>
                                )}
                                <button
                                    onClick={handleGenerateAiEmail}
                                    disabled={isGeneratingEmail}
                                    className="w-full p-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-black text-sm tracking-wider uppercase flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                                >
                                    {isGeneratingEmail ? <Loader2 size={18} className="animate-spin" /> : <BrainCircuit size={18} />}
                                    {isGeneratingEmail ? 'Froukje is aan het denken...' : 'Genereer Mail Concept'}
                                </button>
                            </div>

                            {(generatedEmailSubject || generatedEmailBody) && (
                                <div className="pt-6 border-t border-slate-100 space-y-4 animate-fadeIn">
                                    <h4 className="text-[10px] font-black text-emerald-600 uppercase tracking-widest flex items-center gap-1"><CheckCircle2 size={12} /> Concept Klaar</h4>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 block">Onderwerp</label>
                                        <input
                                            value={generatedEmailSubject}
                                            onChange={(e) => setGeneratedEmailSubject(e.target.value)}
                                            className="w-full p-3 bg-emerald-50/50 border border-emerald-100 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-emerald-300"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 block flex items-center gap-2">
                                            Bericht
                                            <span className="text-purple-500 font-normal normal-case opacity-75">(Selecteer een stuk tekst om door AI te laten herschrijven)</span>
                                        </label>
                                        <div className="relative">
                                            <textarea
                                                ref={emailBodyRef}
                                                value={generatedEmailBody}
                                                onChange={(e) => setGeneratedEmailBody(e.target.value)}
                                                onSelect={handleEmailTextSelection}
                                                onMouseUp={handleEmailTextSelection}
                                                onKeyUp={handleEmailTextSelection}
                                                className="w-full p-4 bg-emerald-50/50 border border-emerald-100 rounded-xl text-sm text-slate-800 leading-relaxed outline-none focus:border-emerald-300 min-h-[400px]"
                                            />
                                            {/* AI FLOATING MENU OVER EMAIL BODY */}
                                            {aiMenuPos && (
                                                <div
                                                    className="fixed z-[500] flex items-center gap-1 p-1 bg-white rounded-xl shadow-2xl shadow-purple-500/20 border border-purple-100 animate-in fade-in zoom-in-95 duration-200"
                                                    style={{
                                                        top: aiMenuPos.top,
                                                        left: aiMenuPos.left,
                                                        transform: 'translateX(-50%)'
                                                    }}
                                                    onMouseDown={(e) => e.preventDefault()}
                                                >
                                                    <div className="flex items-center gap-1 pr-2 border-r border-slate-100 mr-1">
                                                        <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                                                            {isAiWandLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                                        </div>
                                                        <span className="text-[10px] font-black uppercase text-purple-900 mr-1 tracking-widest">AI</span>
                                                    </div>

                                                    {!showAiTone ? (
                                                        <>
                                                            <button onClick={() => handleEmailAiAction('edit')} disabled={isAiWandLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50">
                                                                Check
                                                            </button>
                                                            <button onClick={() => handleEmailAiAction('rewrite')} disabled={isAiWandLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50">
                                                                Herschrijf
                                                            </button>
                                                            <button onClick={handleEmailCustomRewrite} disabled={isAiWandLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50 group" title="Eigen instructie...">
                                                                <Wand2 size={12} className="text-purple-500 group-hover:rotate-12 transition-transform" /> Aangepast
                                                            </button>
                                                            <button onClick={() => setShowAiTone(true)} disabled={isAiWandLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-600 rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50">
                                                                Toon <ChevronRight size={12} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <div className="flex items-center gap-1">
                                                            <button onClick={() => setShowAiTone(false)} disabled={isAiWandLoading} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg disabled:opacity-50"><ArrowLeft size={14} /></button>
                                                            <button onClick={() => handleEmailAiAction('tone', 'Formeel')} disabled={isAiWandLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-700 rounded-lg disabled:opacity-50">Formeel</button>
                                                            <button onClick={() => handleEmailAiAction('tone', 'Vriendelijk')} disabled={isAiWandLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-700 rounded-lg disabled:opacity-50">Vriendelijk</button>
                                                            <button onClick={() => handleEmailAiAction('tone', 'Wervend')} disabled={isAiWandLoading} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-purple-50 hover:text-purple-700 rounded-lg disabled:opacity-50">Wervend</button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer Actions */}
                        <div className="p-6 border-t border-slate-100 bg-white shadow-[0_-10px_30px_rgba(0,0,0,0.05)] z-10 flex flex-col gap-3">
                            <button
                                onClick={() => setShowEmailChoiceModal(true)}
                                disabled={!(generatedEmailSubject || generatedEmailBody)}
                                className={`w-full p-4 rounded-xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${(generatedEmailSubject || generatedEmailBody)
                                    ? 'bg-[#e51b23] text-white hover:bg-black shadow-lg shadow-red-200'
                                    : 'bg-slate-100 text-slate-400 pointer-events-none'
                                    }`}
                            >
                                <Mail size={16} /> Verder met E-mail
                            </button>
                            <button onClick={() => setShowAiEmailModal(false)} className="w-full p-3 text-slate-500 hover:text-slate-800 font-bold text-xs uppercase tracking-wider">Sluiten</button>
                        </div>
                    </div>
                    {/* Backdrop for Sidebar */}
                    {showAiEmailModal && (
                        <div
                            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[399] transition-opacity top-0 left-0"
                            onClick={() => setShowAiEmailModal(false)}
                        ></div>
                    )}

                    {/* EMAIL APPS CHOICE MODAL */}
                    {showEmailChoiceModal && (
                        <div className="fixed inset-0 z-[600] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
                            <div className="bg-white w-full max-w-sm rounded-[2rem] shadow-2xl overflow-hidden flex flex-col animate-scaleIn">
                                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                                    <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                                        <Mail size={20} className="text-[#e51b23]" />
                                        Open E-mail
                                    </h2>
                                    <button onClick={() => setShowEmailChoiceModal(false)} className="text-slate-400 hover:text-slate-700 p-2"><X size={20} /></button>
                                </div>
                                <div className="p-6 space-y-4">
                                    <button onClick={handleSendEmailDesktop} className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition-all text-left group">
                                        <div className="flex flex-col">
                                            <span className="font-black text-slate-900 uppercase tracking-widest text-xs mb-1 group-hover:text-black">App op Computer</span>
                                            <span className="text-[10px] font-medium text-slate-500">Opent je lokaal ingestelde e-mail app (bv. Outlook applicatie of Apple Mail).</span>
                                        </div>
                                    </button>
                                    <button onClick={handleSendEmailM365} className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-[#e51b23]/20 bg-red-50/50 hover:bg-red-50 hover:border-[#e51b23] transition-all text-left group">
                                        <div className="flex flex-col">
                                            <span className="font-black text-[#e51b23] uppercase tracking-widest text-xs mb-1">M365 Webbrowser</span>
                                            <span className="text-[10px] font-medium text-red-900/60">Opent de webversie van Office 365 Direct in je browser.</span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* UNIFIED WHATSAPP MODAL */}
                    <BulkWhatsAppComposerModal
                        isOpen={showWhatsAppOptionsModal}
                        onClose={() => {
                            setShowWhatsAppOptionsModal(false);
                            handleSetSelectedProfile(null);
                        }}
                        selectedCandidates={[...talentPoolWhatsAppBulk.candidates]}
                        vacancyData={talentPoolWhatsAppBulk.vacancy}
                    />

                    {/* EMAIL CONFIRM MODAL */}
                    {showEmailConfirmModal && selectedProfile && (
                        <div className="fixed inset-0 z-[530] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
                            <div className="bg-white w-full max-w-sm rounded-[2rem] shadow-2xl flex flex-col items-center mt-[-10vh] animate-scaleIn p-8 text-center border-t-8 border-[#e51b23]">
                                <div className="w-16 h-16 bg-red-50 text-[#e51b23] rounded-full flex items-center justify-center mb-4">
                                    <Mail size={32} />
                                </div>
                                <h2 className="text-xl font-black text-slate-900 tracking-tight mb-2">
                                    Mail succesvol verzonden?
                                </h2>
                                <p className="text-sm text-slate-500 mb-8 font-medium">
                                    Je hebt zojuist je mailprogramma geopend. Heb je de gegenereerde mail daadwerkelijk verstuurd?
                                </p>
                                <div className="flex gap-3 w-full">
                                    <button 
                                        onClick={() => handleConfirmEmailSent(false)} 
                                        className="flex-1 text-xs font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 py-4 rounded-xl uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                                    >
                                        <X size={14} /> Nee, afgebroken
                                    </button>
                                    <button 
                                        onClick={() => handleConfirmEmailSent(true)} 
                                        className="flex-1 bg-[#e51b23] text-white font-black text-xs py-4 rounded-xl uppercase tracking-widest hover:bg-red-700 shadow-md active:scale-95 transition-all flex items-center justify-center gap-2"
                                    >
                                        <CheckCircle2 size={14} /> Ja, Verzonden!
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <CandidateMoveModal
                isOpen={shortlistMoveModalOpen}
                onClose={() => {
                    setShortlistMoveModalOpen(false);
                    setShortlistMoveDetails(null);
                }}
                moveDetails={shortlistMoveDetails}
                templates={shortlistEmailTemplates}
                signature={(user as any)?.profile?.email_signature}
                currentUser={user}
                rejectionReasons={shortlistRejectionReasons}
                onConfirm={handleShortlistConfirmMove}
            />

            {shortlistDeleteTarget && (
                <div className="fixed inset-0 z-[540] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
                    <div
                        className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 p-6"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="shortlist-del-title"
                    >
                        <h2 id="shortlist-del-title" className="text-lg font-black text-slate-900 tracking-tight mb-2">
                            {shortlistDeleteTarget.kind === 'application'
                                ? 'Sollicitatie verwijderen?'
                                : 'Plaatsing verwijderen?'}
                        </h2>
                        <p className="text-sm text-slate-600 mb-4">{shortlistDeleteTarget.subtitle}</p>
                        <p className="text-xs font-bold text-red-700 mb-2">
                            Typ <span className="font-mono tracking-wide">VERWIJDEREN</span> om door te gaan.
                        </p>
                        <input
                            type="text"
                            autoComplete="off"
                            value={shortlistDeleteInput}
                            onChange={(e) => setShortlistDeleteInput(e.target.value)}
                            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium mb-4 focus:ring-2 focus:ring-red-200 outline-none"
                            placeholder="VERWIJDEREN"
                        />
                        <div className="flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setShortlistDeleteTarget(null);
                                    setShortlistDeleteInput('');
                                }}
                                className="px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest text-slate-600 bg-slate-100 hover:bg-slate-200"
                                disabled={shortlistDeleteBusy}
                            >
                                Annuleren
                            </button>
                            <button
                                type="button"
                                onClick={() => void executeShortlistDelete()}
                                disabled={shortlistDeleteBusy || shortlistDeleteInput.trim() !== 'VERWIJDEREN'}
                                className="px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {shortlistDeleteBusy ? 'Bezig…' : 'Definitief verwijderen'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {quickViewCandidateId && user && (
                <CandidateDetailModal
                    candidateId={quickViewCandidateId}
                    clientId={user.clientId || ''}
                    currentUser={user}
                    forceReadOnly={isClientPortal}
                    onClose={() => setQuickViewCandidateId(null)}
                />
            )}
        </div>
    );
};

export default TalentPool;
